/**
 * Engine: owns the RAF loop. Fixed 60Hz physics with an accumulator, render
 * interpolation for perfectly smooth motion at any display Hz. Input is
 * sampled every frame and consumed inside each fixed step.
 *
 * M1 loop: trash rains in → grab (hold LMB/E) → throw at portal A → momentum
 * carries it out of portal B into the incinerator → score × combo.
 */
import * as THREE from "three";
import type RAPIER from "@dimforge/rapier3d-compat";
import type { GameContext, QualityLevel } from "@host/types.js";
import { InputSampler, type InputFrame } from "./Input.js";
import { CharacterController } from "../player/CharacterController.js";
import {
  ARENA_DEFINITION,
  GrayBoxArena,
  type AuthoredTarget,
} from "../world/GrayBoxArena.js";
import { deriveArenaGeometry, resolveRuntimeArenaDefinition, type ChuteId } from "../world/ArenaDefinition.js";
import { buildArenaPhysics, type ArenaPhysicsHandles } from "../world/ArenaPhysics.js";
import { SceneRenderer } from "../render/SceneRenderer.js";
import { TrashSpawner, type TrashPiece } from "../gameplay/Trash.js";
import { PortalPair } from "../gameplay/PortalPair.js";
import { GrabBeam, type AimAssistTarget } from "../gameplay/GrabBeam.js";
import { bulkOf, FACILITY_CAPACITY, gradeMultiplier, OVERFLOW_SEVERE, OVERFLOW_WARNING, PORTAL_REARM_TICKS, TRASH, type ArenaAnchor, type LedgerEvent, type LoadRecord, type PerformanceSample, type PhaseSpec, type ReceiverTier, type RunBundle, type RunEndReason, type SimulationAction, type TrashKind as SimTrashKind } from "../simulation/types.js";
import { Simulation, type GradeMove } from "../simulation/Simulation.js";
import { getSeed } from "../simulation/seeds.js";
import { Tutorial, type CueSpec } from "../gameplay/Tutorial.js";
import { JAM_ENTRANCE_FLARE, JAM_ENTRANCE_TAG, jamRecoveryPrompt } from "../gameplay/JamRecoveryCopy.js";
import { FirstEncounter } from "../gameplay/FirstEncounter.js";
import { FxPool } from "../render/Fx.js";
import { Hud, type HudModel, type HudOverflowBuckets, type HudReticle } from "../render/Hud.js";
import { chuteIndicatorModel, type ChuteIndicatorSpec, type ChuteSignal } from "../render/ChuteIndicators.js";
import { PostFx } from "../render/PostFx.js";
import { Particles } from "../render/Particles.js";
import { Trails } from "../render/Trails.js";
import { Sound } from "../audio/Sound.js";
import { CAMERA_BLEND, CHAIN_TIER_COLORS, cssHex, FEEDBACK_COLORS, PALETTE, RECEIVER_TIER_COLORS, TRASH_THEME } from "../render/theme.js";

const FIXED_DT = 1 / 60;
const MAX_FRAME_DT = 0.1; // clamp tab-switch spikes

const NEUTRAL_INPUT: InputFrame = {
  moveX: 0, moveZ: 0,
  jumpHeld: false, jumpPressed: false, dashPressed: false, resetPressed: false,
  grabHeld: false, grabPressed: false, grabReleased: false,
  portalAPressed: false, portalBPressed: false,
  purgePressed: false,
  mouseX: 0, mouseY: 0,
  aimStickX: 0, aimStickY: 0, gamepadConnected: false,
  padActive: false, kbActive: false, skipPressed: false,
};

/**
 * Signed Grade delta in the HUD's sign typography. Zero renders "±0": a
 * clamped mover (varied at Grade 100, stale at 0) truthfully names why the
 * meter did not move instead of promising a delta it cannot pay (18.10).
 */
const signedGrade = (delta: number): string => (delta < 0 ? `−${Math.abs(delta)}` : delta === 0 ? "±0" : `+${delta}`);

const PLAYER_EXIT_CLEARANCE = 1.0; // capsule needs room at the exit
const OVERFLOW_LIMIT = FACILITY_CAPACITY;
/** Simple orientation "chase" beat telegraph (LOCKED-DESIGN 18.11 v2; widened to
 * 8s for the 56-wide arena per 18.12 / PLAN §D5): the full kid warning. */
const SIMPLE_ORIENTATION_TELEGRAPH_S = 8;
/**
 * Simple-only sweep-clock multiplier for the moving rail anchor (CHASE IT!,
 * LOCKED-DESIGN 18.25). 2 takes the authored panel from 1.71 m/s / 44.0 s to
 * 3.43 m/s / 22.0 s over the same authored 24 m of travel — about 40 % of the
 * player's 8.5 m/s top speed, so the ring is catchable at a walk but no longer
 * waits. Ranked play keeps the authored rate of 1 (Section 3.1 calls it the
 * "Slow" panel and `rail` is in the ranked anchor pool for phases 4-6).
 * The dogfood tuning dial: lower this first if the throw lead is too hard.
 */
const SIMPLE_RAIL_RATE = 2;
/**
 * Minimum gap between two intent-gate nudges (18.25). The gate is evaluated every
 * fixed step, so a child resting inside the ring's 1.55 m capture volume would
 * otherwise fire the popup 60x a second. 1.5 s is long enough that the nudge
 * reads as one message and short enough to answer a second honest attempt.
 */
const INTENT_NUDGE_COOLDOWN_TICKS = 90;
/** telegraph lookahead for mini-batches: ~1.5s before the batch lands */
const BATCH_TELEGRAPH_TICKS = 90;
/** spawns on one chute this close together (ticks) read as one mini-batch */
const BATCH_CLUSTER_GAP = 24;
/**
 * Radius around a chute outlet whose loose trash counts as that chute's pile
 * (PLAYTEST F3 / 18.19). Wide enough to include the apron a fresh drop tumbles
 * onto, tight enough that the three outlets (±25.95 x, z 17.95 / 0 / −6) never
 * claim each other's backlog.
 */
const CHUTE_PILE_RADIUS_M = 9;
/** locked five-second warning window: shutters, conveyor reversals, expiring panels */
const HAZARD_WARNING_TICKS = 300;
/**
 * Reject-return journey constants (LOCKED-DESIGN Section 6 / 18.10 third
 * dogfood): the visible path a returned reject rides — off the receiver lane,
 * onto the chamber collection belt (y/z below match the GrayBoxArena return
 * assembly), east to the duct, then out of the bright return hatch beside the
 * sorting island. The EXIT point/velocity are the pre-existing return values;
 * only the journey to them became visible. All presentation-side: the sim owns
 * WHEN (receiver-cleared), this owns only the followable HOW.
 */
const RETURN_BELT_Y = 0.9;
const RETURN_BELT_Z = -13.62;
const RETURN_DUCT_X = 6.7;
const RETURN_EXIT = { x: 4.8, y: 1.2, z: -5.5 } as const;
const RETURN_EXIT_VEL = { x: -2.5, y: 2.2, z: 0 } as const;
const RETURN_DROP_TICKS = 16;
const RETURN_BELT_SPEED = 8; // world units/second along the belt
const RETURN_STAGGER_TICKS = 7;
/** Return-pad spin-up (LOCKED-DESIGN 18.12): 0.5s > PORTAL_REARM_TICKS (24), so re-arm never blocks the launch. */
const RETURN_PAD_SPINUP_TICKS = 30;

/** One rejected piece riding the return belt (render-side body scripting). */
interface ReturnRide {
  piece: TrashPiece;
  ticks: number;
  delay: number;
  from: THREE.Vector3;
  beltEntry: THREE.Vector3;
  beltTicks: number;
  /** shared per-clear batch: one aggregate popup at the hatch, not N spam */
  batch: { bulk: number; announced: boolean };
}
/**
 * Overflow-warning hysteresis (18.4): once a 24/30 threshold has fired, the
 * level only re-arms after pressure falls this many bulk units below it —
 * hovering at a boundary must not machine-gun the warning SFX + music duck.
 */
const OVERFLOW_REARM_MARGIN = 3;
/**
 * Reinforced-glass bulkhead (arena.v1.json `glass-bulkhead`, front face at
 * z ≈ -12.84 after the LOCKED-DESIGN 18.13 depth re-author). The enforced
 * glass beat (LOCKED-DESIGN 18.6) advances only when a thrown piece's CENTER
 * reaches this contact plane — i.e. it has physically met the sealed
 * bulkhead, not merely been released in some direction.
 */
const GLASS_FRONT_Z = -12.84;
const GLASS_CONTACT_Z = -12.3;
/**
 * Chamber-block half-width (LOCKED-DESIGN 18.13): the chamber is the 24-wide
 * enclosure behind the glass bulkhead (side glass at x ±12) — the open arena
 * floor now wraps BOTH shoulders north of the z threshold out to the walls.
 * Under 18.12 the block spanned the full arena width, so a pure
 * z < chamberThresholdZ test was equivalent to chamber occupancy; after the
 * depth re-author every presentation-side "in the chamber?" classification
 * must also bound |x| to the block, or the walkable shoulder floor
 * misclassifies as in-chamber (suppressed scoring releases, camera hijack,
 * kid-cue leaks). Matches the |x| <= 12 glass-contact bound below.
 */
const CHAMBER_HALF_WIDTH_X = 12;

/**
 * How far along the aim ray a MISSED aim may land (PLAYTEST F18). Historically
 * the only rule: `ray.at(50)`, whether or not 50 m along that ray was inside the
 * building. `Engine.aimFallbackDistance` now clips it to the room first; this
 * stays the ceiling, so the fallback can only ever get shorter than it was.
 */
const AIM_FALLBACK_DISTANCE = 50;
/**
 * Floor on the clipped fallback (PLAYTEST F18). A clip length of ~0 would make
 * the aim direction — and with it GrabBeam's hold point and the player's facing
 * yaw — undefined. 1 m sits well inside GrabBeam's 2.2 m HOLD_DIST, so the hold
 * point never depends on this value; it only keeps the direction well-formed.
 */
const AIM_FALLBACK_MIN_DISTANCE = 1;

/**
 * True when a position is inside the 24-wide chamber block's FOOTPRINT — a plan
 * test, deliberately blind to height. Correct for the things that live on the
 * floor plan: which side of the building the player is standing on (chamber
 * camera, dwell clock, in-chamber release policy) and whether a blast is
 * occluded by the bulkhead. For a TRASH BODY, which can be anywhere in a 19 m
 * room, use `inChamberVolume` — see the note there.
 */
function inChamberBlock(p: { x: number; z: number }, geometry: ReturnType<typeof deriveArenaGeometry>): boolean {
  return p.z < geometry.chamberThresholdZ && Math.abs(p.x) <= CHAMBER_HALF_WIDTH_X;
}
/**
 * True when a body is inside the SEALED chamber volume: the footprint above, and
 * under the chamber lid (PLAYTEST F19 review).
 *
 * The chamber is a closed 24 x 5 x 7 box standing in a 19 m-tall room, so the
 * footprint alone is not occupancy — the open air above it, and the top surface
 * of its own ceiling slab, satisfy `inChamberBlock` exactly as well as the
 * interior does. That was inert while `p.chamber` was only ever written at
 * portal exits (all of which are inside the box); it stops being inert the
 * moment the flag is recomputed from every body's position every step. A piece
 * that comes to rest on the roof — an ordinary 16 m/s lob does it — is outside
 * the maw's intake volume on the shoulders (|x| > 5.8), unreachable by the
 * recovery route every jam cue points down, and invisible to the player, yet a
 * footprint-only test would let it be stamped as a jam body and BACK its lane's
 * debt: the unfeedable-phantom class 18.12 exists to prevent, rebuilt on the
 * roof. Bounded at the lid's underside (`chamberCeilingY`, derived from the
 * authored slab): a trash body inside the box tops out a half-extent BELOW it,
 * one resting on the roof sits a half-extent ABOVE it, so the two bands are
 * ~0.4 m clear of the bound in either direction.
 *
 * No floor bound: the containment shell and the arena floor already are one, and
 * a piece resting on the chamber floor sits within a body radius of y = 0, so a
 * lower bound could only ever produce false negatives.
 */
function inChamberVolume(p: { x: number; y: number; z: number }, geometry: ReturnType<typeof deriveArenaGeometry>): boolean {
  return inChamberBlock(p, geometry) && p.y <= geometry.chamberCeilingY;
}
const GRADE_BANDS = ["C", "B", "A", "S"] as const;
type GradeBand = (typeof GRADE_BANDS)[number];
type EndReason = "cash-out" | "death";

function bandOf(grade: number): GradeBand {
  return grade >= 85 ? "S" : grade >= 65 ? "A" : grade >= 40 ? "B" : "C";
}

/**
 * Sim end reasons that coarse-map to a "death" (LOCKED-DESIGN 18.12): critical
 * overflow plus the two new jam-recovery failures (chamber cook, furnace
 * overload). "complete" cashes out; "abandoned" is handled upstream.
 */
function isFailureReason(reason: RunEndReason): boolean {
  return reason === "overflow" || reason === "cooked" || reason === "jam-overload";
}

export interface RunResult {
  reason: EndReason;
  /**
   * Precise sim end reason (LOCKED-DESIGN 18.12): overflow | cooked |
   * jam-overload all coarse-map to "death", but the shell results copy /
   * diagnostics drawer want the exact cause. bundle.final.reason carries the
   * same value; this surfaces it without re-reading the bundle.
   */
  simEndReason: RunEndReason;
  score: number;
  bestCombo: number;
  incinerated: number;
  portalTraversals: number;
  durationMs: number;
  bundle: RunBundle;
}

export class Engine {
  private readonly arenaDefinition = resolveRuntimeArenaDefinition(ARENA_DEFINITION);
  private readonly arenaGeometry = deriveArenaGeometry(this.arenaDefinition);
  private api!: typeof RAPIER;
  private world!: RAPIER.World;
  private controller!: CharacterController;
  private arena!: GrayBoxArena;
  private arenaPhysics!: ArenaPhysicsHandles;
  private readonly baffleEuler = new THREE.Euler();
  private readonly baffleQuat = new THREE.Quaternion();
  private renderer!: SceneRenderer;
  private input!: InputSampler;
  private trash!: TrashSpawner;
  private portals!: PortalPair;
  private beam!: GrabBeam;
  private fx!: FxPool;
  private postfx: PostFx | null = null;
  private animClock = 0;
  private hud: Hud | null = null;
  private particles!: Particles;
  private trails!: Trails;
  private readonly sound = new Sound();
  /**
   * Render-side impact beat (LOCKED-DESIGN 18.4): while > 0, COSMETIC clocks
   * (fx, particles, animClock) run at a dip. The fixed-step accumulator never
   * pauses and driftMs stays honest — this replaces the old hitstop freeze.
   */
  private impactTimer = 0;
  private beamJammedTicks = 0;
  private prevGrounded = true;
  private prevVy = 0;
  private prevDashing = false;
  // presentation-state trackers (all render-side; never fed back into the sim)
  private overflowLevel: "warning" | "severe" | null = null;
  /** chamber occupancy mirror driving the camera view (render-side only) */
  private chamberViewOn = false;
  /** active quality tier (LOCKED-DESIGN 13.1); default high, UI lands Phase 3 */
  private qualityLevel: QualityLevel = "high";
  private alarmActive = false;
  private prevChainCombo = 1;
  private prevGradeBand: GradeBand = "C";
  private prevBaffleBlocked: boolean | null = null;
  private shutterWarnedLane: "left" | "right" | null = null;
  private conveyorWarnedDirection: "forward" | "reverse" | null = null;
  /** phase boundary (nextIndex) whose expiring panels have already sounded */
  private panelWarnedBoundary = 0;
  private readonly telegraphedBatches = new Set<number>();
  /**
   * Last frame's chute indicator specs (PLAYTEST F3, 18.19). Fed back into the
   * pure model so its show/hide hysteresis (46 px to show, 56 px to hide) has a
   * previous state without the model itself holding any.
   */
  private chuteIndicatorSpecs: ChuteIndicatorSpec[] = [];
  /** scratch for the chute-outlet projection (never aliases tmpV/tmpV2) */
  private readonly chuteProbe = new THREE.Vector3();
  private readonly fuseHalos: THREE.Mesh[] = [];
  /** integrated pulse phase per fuse halo (rate changes must never alias) */
  private readonly fuseHaloPhases = [0, 0];
  private reticleShake = 0;
  private reticleDenyFlash = 0;
  private readonly reducedMotionQuery =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;
  private tutorial: Tutorial | null = null;
  private needsTutorial = false;
  /** Piece ids thrown during the glass beat, watched for bulkhead contact. */
  private readonly glassWatch = new Set<number>();
  /**
   * First-encounter teaching flags (LOCKED-DESIGN 12.2/18.10). Fire once per
   * session in unranked play so the first red-orb fuse and the first receiver
   * jam are actually met — the dogfood never triggered either. Never during the
   * prologue; never reset on retry, so they read as a true first encounter.
   */
  private taughtRedFuse = false;
  private taughtJam = false;
  private taughtStale = false;
  /** One-shot per run (Simple, 18.11 v2): the kid "FULL UP! THROW IT!" beam toast. */
  private taughtBeamFull = false;
  /** One-shot per run (Simple, 18.11 v2): the "THE RING MOVES!" first-move pause card. */
  private taughtRingMove = false;
  /** Orientation "chase" beat state (Simple, 18.11 v2): the one scripted ring move. */
  private orientationMove: { elapsed: number; relocated: boolean } | null = null;
  /**
   * Grade-cause popup rate limiter (LOCKED-DESIGN 18.10 fourth dogfood):
   * animClock stamp of the last cause popup — a busy volley coalesces into
   * the single grade-meter chip instead of stacking popups.
   */
  private gradeCauseShownAt = -Infinity;
  /**
   * Dispatch grade move staged by the ticket-completed event (18.10 fourth
   * dogfood): the +8 lands inside the same sim resolve as its load, and the
   * ledger emits ticket-completed immediately BEFORE that load-resolved — the
   * load handler consumes this so ONE chip names both causes and their sum
   * matches the meter movement exactly.
   */
  private pendingDispatch: { loadId: string; seq: number } | null = null;
  /**
   * Active first-encounter teaching pause (LOCKED-DESIGN 12.2/18.10). While set,
   * frame() freezes the sim clock behind a dismissable card so the beat lands
   * (the Phase-3 fly-by toasts were missed). Unranked only; never ranked.
   */
  private firstEncounter: FirstEncounter | null = null;
  /**
   * Lanes that were fed again while already jammed (ledger cause
   * "jam-extended"): escalates the recovery prompt for THAT lane (LOCKED-DESIGN
   * 18.10 — the dogfood kept releasing into a jammed lane and stretched a jam
   * ~36 s). Per lane, not global: with two lanes jammed a global flag makes the
   * prompt shout "STOP FEEDING <lane>" about a lane the player never fed as soon
   * as the fed one clears and the prompt moves on. Entries are dropped as their
   * lane clears.
   */
  private readonly jamExtendingLanes = new Set<ReceiverTier>();
  /** rejected pieces currently riding the return belt (Section 6 journey) */
  private readonly returnRides: ReturnRide[] = [];
  /**
   * DEFERRED physical-arrival queue (LOCKED-DESIGN 18.14 / F8 direction C).
   * `portals.step` runs BEFORE the action assembly that pushes `release` and
   * before `run.step` stamps `piece.loadId`, so a piece captured on its own
   * release tick still has `loadId === null` when it crosses. Emitting `arrive`
   * inline at the traversal loop would therefore silently drop credit for every
   * point-blank throw — exactly the case the F5 inside-capture fix makes
   * common. Queue instead, and flush during the SAME tick's action assembly:
   * an already-stamped piece emits on the same tick, a same-tick
   * release+capture emits on tick N+1 (still ~178 ticks inside the 3s
   * resolution window), and a chamber-side release — which pushes no `release`
   * action, so its pieces never get a loadId — is dropped by the grace window.
   */
  private readonly arrivalQueue: Array<{ pieceId: number; queuedTick: number }> = [];
  /**
   * How long a queued arrival waits for its loadId stamp before being dropped.
   * Long enough for the one-tick stamping lag, short enough that a piece
   * knocked back OUT of the chamber earns nothing.
   */
  private static readonly ARRIVAL_STAMP_GRACE_TICKS = 2;
  /**
   * How long a lane's jam debt may outlive its physical pile before the Engine
   * returns the unbacked remainder (PLAYTEST F13). Long enough that a reject
   * still physically crossing the portal lands and becomes the real pile (the
   * late-arriving-reject stamp in the traversal loop), short enough that a kid
   * never searches an empty chamber for trash that is lying on the arena floor.
   */
  private static readonly JAM_BACKING_GRACE_TICKS = 90;
  /** First tick each lane's jam debt was seen exceeding its physical pile. */
  private readonly jamUnbackedSince: Record<ReceiverTier, number | null> = { safe: null, technical: null, hazard: null };
  /**
   * Bulk the F13 backing guarantee WROTE OFF this fixed step, per lane (PLAYTEST
   * F20). Set during action assembly, read by `consumeLedgerEvents` after
   * `run.step` in the same step, cleared there. It exists because the ledger
   * cannot distinguish a write-off from an ordinary "returned" feed — both emit
   * the same `jam-fed` shape — and the two need opposite copy: one says the jam
   * was never really there, the other is a pile that physically left.
   */
  private readonly jamWrittenOff: Record<ReceiverTier, number> = { safe: 0, technical: 0, hazard: 0 };
  private lastPad = false; // sticky device tracker for prompt text
  private readonly tmpV = new THREE.Vector3();
  private readonly tmpV2 = new THREE.Vector3();
  /** Return-pad spin-up counter (ticks the player has held position on the pad). */
  private returnPadHold = 0;
  /** dwell 2s-warning edge latch (one warning SFX per entry). */
  private dwellWarned = false;
  /** chamber-heat presence edge (fires the cool-down hiss on exit). */
  private chamberHeatWasOn = false;
  /** overload final-5s klaxon latch (priority bus; never over the overflow alarm). */
  private overloadKlaxonOn = false;
  run: Simulation;
  private runEnded = false;
  /**
   * Physics-fault latch (observed live: a Rapier wasm panic in World.step
   * poisons the wasm borrow guard, so EVERY subsequent step throws). Once
   * set, the loop stays down — start()/resume() refuse to restart into the
   * poisoned world, so one panic reads as one loud failure instead of a
   * frozen arena error-spamming ~60/s (see presentFatalFault).
   */
  private fatalFault = false;
  private readonly performance: PerformanceSample[] = [];
  private performanceElapsedMs = 0;
  private visualLedgerIndex = 0;

  private rafId = 0;
  private running = false;
  private lastTime = 0;
  private accumulator = 0;

  private readonly prevPlayerPos = new THREE.Vector3();
  private readonly currPlayerPos = new THREE.Vector3();

  // aim
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly aimPoint = new THREE.Vector3(0, 1, 0);
  private readonly aimNormal = new THREE.Vector3(0, 1, 0);
  private aimPlaceable = false; // cursor is on a static surface (not a portal)
  private aimObject: THREE.Object3D | null = null;
  private reticle!: THREE.Mesh;
  // gamepad aim: a virtual cursor driven by the right stick; last input wins
  private aimMode: "mouse" | "pad" = "mouse";
  private readonly padAimDir = new THREE.Vector2(0, -1); // world xz, default north
  private padAimDist = 6;
  private lastMouseX = -1;
  private lastMouseY = -1;
  private playerPortalArmed = true;
  /** Fixed ticks left on the player's portal re-arm cooldown (LOCKED-DESIGN 3.4/18.10). */
  private playerRearmTicks = 0;
  /**
   * Tick of the last intent-gate nudge, so leaning on the ring cannot spam it
   * (18.25). NEGATIVE_INFINITY, not `-COOLDOWN`: the run clock starts NEGATIVE
   * and counts up through the placement countdown (measured at −204), so seeding
   * this by cooldown width silences the very first nudge for the whole pre-run —
   * precisely when a child is milling around beside the ring with nothing to do.
   */
  private lastIntentNudgeTick = Number.NEGATIVE_INFINITY;
  /**
   * Chamber entries the intent gate has refused this run (18.25). Diagnostic
   * only — nothing reads it to make a decision, and it never reaches the sim.
   * It exists so QA can assert the gate ENGAGED rather than merely that the
   * player failed to arrive, which any broken drive also satisfies.
   */
  intentRefusals = 0;
  private readonly playerTrv = {
    prevPos: new THREE.Vector3(),
    currPos: new THREE.Vector3(),
    vel: new THREE.Vector3(),
  };

  private runEndCallback: ((result: RunResult) => void) | null = null;

  private fpsFrames = 0;
  private fpsLast = 0;
  /** last measured fps (readable from automation via window.__tpz.fps) */
  fps = 0;

  // One-shot input edges are latched here until a physics step consumes them.
  // A frame can legally run ZERO fixed steps (accumulator jitter); without the
  // latch, an edge sampled on such a frame vanished — releases dropped the
  // item instead of throwing ~half the time.
  private readonly pending = {
    jump: false, dash: false, grabP: false, grabR: false,
    portalA: false, portalB: false, purge: false,
  };
  private readonly pendingSimulationActions: SimulationAction[] = [];

  constructor(private readonly ctx: GameContext) {
    this.prevPlayerPos.set(this.arenaGeometry.spawn.x, this.arenaGeometry.spawn.y, this.arenaGeometry.spawn.z);
    this.currPlayerPos.copy(this.prevPlayerPos);
    this.run = this.createSimulation();
  }

  onRunEnd(cb: (result: RunResult) => void): void {
    this.runEndCallback = cb;
  }

  /** Adapter decides from the save slot whether the warm-up should run. */
  setNeedsTutorial(v: boolean): void {
    this.needsTutorial = v;
  }

  /**
   * Seed the first-encounter "seen" flags from the save layer (LOCKED-DESIGN
   * 18.10): each teaching pause fires once ever, not once per session.
   */
  setFirstEncountersSeen(seen: { red: boolean; jam: boolean; stale?: boolean }): void {
    this.taughtRedFuse = seen.red;
    this.taughtJam = seen.jam;
    this.taughtStale = seen.stale === true;
  }

  /**
   * Hide the in-arena HUD whenever a shell overlay / non-playing state is up
   * (LOCKED-DESIGN 18.10 residual): the idle chrome must not bleed through the
   * title/settings overlays while the RAF loop is stopped. Render-only.
   */
  setHudVisible(visible: boolean): void {
    this.hud?.setChromeVisible(visible);
  }

  /** True when the host selected the unranked Simple/kid mode (LOCKED-DESIGN 18.11). */
  private get simpleMode(): boolean {
    return this.ctx.mode === "simple";
  }

  /**
   * Push the current presentation mode into the render surfaces (LOCKED-DESIGN
   * 18.11): the HUD density switch and the world icon-first signage. Called at
   * run start / reset so toggling mode on the title takes effect next run. The
   * Simulation never reads mode — Simple honesty rides on seed.ruleset — so this
   * carries no determinism implication.
   */
  private applyMode(): void {
    const simple = this.simpleMode;
    this.hud?.setSimpleMode(simple);
    this.arena?.setSignageMode(simple);
    // CHASE IT! (LOCKED-DESIGN 18.25): the rail panel sweeps at the authored slow
    // rate for ranked play and at SIMPLE_RAIL_RATE for kids, so the ring is worth
    // chasing without retuning the three official seeds — `rail` is in the ranked
    // anchor pool too, which is why this is a runtime scale and not a data edit.
    this.arena?.setRailRate(simple ? SIMPLE_RAIL_RATE : 1);
  }

  configureRun(seedId: string, practicePhase: number): void {
    this.ctx.seed = seedId;
    this.ctx.practicePhase = practicePhase;
    this.needsTutorial = false;
    // A real shift never carries orientation into it — drop the overlay
    // without marking onboarding done+skipped (that only happens on an
    // intentional skip or graduation, LOCKED-DESIGN 18.6).
    if (this.tutorial) {
      this.tutorial.destroy();
      this.tutorial = null;
      this.hud?.setTutorialMode(false);
    }
    this.resetWorld();
  }

  beginPrologue(): void {
    this.needsTutorial = true;
    // Restarting the prologue while one is running: drop the stale overlay so
    // start() can arm a fresh one (LOCKED-DESIGN 18.6 — never a silent consume).
    if (this.tutorial) {
      this.tutorial.destroy();
      this.tutorial = null;
      this.hud?.setTutorialMode(false);
    }
    this.resetWorld();
  }

  /** Stress/debug hook: dump n pieces from the sky (window.__tpz.stress(200)). */
  stress(n: number): void {
    this.trash.spawnBurst(n);
  }

  runResult(): RunResult {
    return {
      reason: isFailureReason(this.run.endReason) ? "death" : "cash-out",
      simEndReason: this.run.endReason,
      score: this.run.score,
      bestCombo: this.run.bestCombo,
      incinerated: this.run.incinerated,
      portalTraversals: this.run.portalTraversals,
      durationMs: Math.round(this.run.elapsed * 1000),
      bundle: this.run.bundle(this.performance),
    };
  }

  async init(): Promise<void> {
    // compat build: embedded WASM, async init, lazy chunk
    const mod = await import("@dimforge/rapier3d-compat");
    await mod.init();
    this.api = mod as unknown as typeof RAPIER;

    this.world = new this.api.World({ x: 0, y: -9.81, z: 0 });
    this.world.timestep = FIXED_DT;

    this.controller = new CharacterController(this.api, this.world, this.arenaGeometry.spawn);
    this.arena = new GrayBoxArena(this.arenaDefinition);
    this.arenaPhysics = buildArenaPhysics(this.api, this.world, this.arenaDefinition);
    this.trash = new TrashSpawner(this.api, this.world, this.arenaGeometry.chuteOutlets);
    this.portals = new PortalPair(
      new THREE.Vector3().fromArray(this.arenaGeometry.defaultPortals.A.position),
      new THREE.Vector3().fromArray(this.arenaGeometry.defaultPortals.A.normal),
      new THREE.Vector3().fromArray(this.arenaGeometry.defaultPortals.B.position),
      new THREE.Vector3().fromArray(this.arenaGeometry.defaultPortals.B.normal),
    );
    this.beam = new GrabBeam();
    this.fx = new FxPool();
    // Screen-relative controls contract (2.2): the movement basis follows the
    // rig's input yaw — the blend DESTINATION's yaw during view transitions —
    // so W stays up-screen in the chamber view and through the blend.
    this.input = new InputSampler(this.ctx, () => this.renderer?.rig.inputYaw ?? this.arenaGeometry.camera.yaw);

    this.particles = new Particles();
    this.trails = new Trails();

  }

  mount(): void {
    const container = this.ctx.container;
    const w = container.clientWidth || 1280;
    const h = container.clientHeight || 720;
    // Camera config AND containment box both come from the RESOLVED arena
    // definition (a DEV `?arenaDraft` re-authors both), so re-authoring the
    // floor re-sizes the camera box for free instead of leaving the clamp on
    // the committed extents while the walls move (PLAYTEST F6).
    this.renderer = new SceneRenderer(
      this.ctx.canvas,
      w,
      h,
      window.devicePixelRatio || 1,
      this.arenaGeometry.camera,
      this.arenaGeometry.floorExtents,
    );
    // quality tier from the host context (default high; auto-detect UI is Phase 3)
    this.qualityLevel = this.ctx.quality;
    this.renderer.setQuality(this.qualityLevel);
    // glass reflection tiering is visual-only (static envMap + baked sheen, no
    // per-frame planar reflection); the arena drops the sheen on the low tier
    this.arena.setQuality(this.qualityLevel);
    // every shake/flash/pulse routes through the presentation knobs (12.3/18.5)
    this.renderer.setPresentation(this.ctx.presentation);
    this.renderer.setGradeBand(this.prevGradeBand);
    this.renderer.scene.add(this.arena.group);
    this.renderer.scene.add(this.trash.group);
    this.renderer.scene.add(this.portals.group);
    this.renderer.scene.add(this.beam.lines);
    this.renderer.scene.add(this.beam.preview);
    this.renderer.scene.add(this.particles.points);
    this.renderer.scene.add(this.trails.group);
    this.renderer.scene.add(this.fx.group);

    this.reticle = new THREE.Mesh(
      new THREE.RingGeometry(0.16, 0.24, 24),
      new THREE.MeshBasicMaterial({
        color: 0xdfe7f2,
        transparent: true,
        opacity: 0.85,
        depthTest: false,
        side: THREE.DoubleSide,
      }),
    );
    this.reticle.renderOrder = 10;
    this.reticle.frustumCulled = false;
    this.renderer.scene.add(this.reticle);

    // red-orb fuse telegraph halos (Section 5: at most two armed orbs exist)
    for (let i = 0; i < 2; i++) {
      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(0.62, 18, 12),
        new THREE.MeshBasicMaterial({
          color: TRASH_THEME.red.accent,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      halo.visible = false;
      this.fuseHalos.push(halo);
      this.renderer.scene.add(halo);
    }

    this.hud = new Hud(container);
    this.postfx = new PostFx(this.renderer.renderer, this.renderer.scene, this.renderer.camera, w, h, this.qualityLevel);

    // dev/testing handle (compile-time privacy only; used by automated checks)
    (window as unknown as { __tpz?: unknown }).__tpz = this;
  }

  start(): void {
    if (this.running || this.fatalFault) return;
    this.running = true;
    this.sound.start();
    this.applyMode();
    if (this.needsTutorial && !this.tutorial) this.spawnTutorial();
    this.lastTime = performance.now();
    this.fpsLast = this.lastTime;
    const loop = (now: number) => {
      if (!this.running) return;
      this.rafId = requestAnimationFrame(loop);
      this.frame(now);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  /**
   * Arm the interactive prologue (LOCKED-DESIGN 18.6). Portals start CONCEALED
   * so the enforced glass beat and the choose beats teach real placement — the
   * cue never circles an already-placed default portal.
   */
  private spawnTutorial(): void {
    this.needsTutorial = false;
    const simple = this.simpleMode;
    this.tutorial = new Tutorial(this.ctx.container, simple ? "simple" : "standard");
    this.tutorial.onStep(() => this.sound.chime());
    this.tutorial.onDone((skipped) => this.finishTutorial(skipped));
    // Orientation relief (LOCKED-DESIGN 18.15 / F9). Simple's throw beats had no
    // timeout at all, so a kid who could not land a throw was hard-blocked until
    // a parent pressed skip. Softening is a HELP (bigger ring + wider assist +
    // a concrete sub-line); the timed release is a WARM one, never a failure.
    this.tutorial.onSoften(() => {
      if (!this.flashReduced()) this.sound.chime();
    });
    this.tutorial.onReliefAdvance(() => {
      this.hud?.toast("Nice try! Let's keep going!", cssHex(RECEIVER_TIER_COLORS.safe), 2600);
    });
    this.hud?.setTutorialMode(true);
    this.portals.resetDefaults();
    // Simple pre-places the floor→safe portals LIVE before step 1 so the kid
    // learns move/grab/throw without ever touching Q/R (LOCKED-DESIGN 18.11);
    // the adult prologue keeps them concealed for the enforced glass/choose
    // beats (18.6).
    this.portals.setBothLive(simple);
    this.glassWatch.clear();
  }

  pause(): void {
    this.running = false;
    this.sound.suspend();
    cancelAnimationFrame(this.rafId);
  }

  resume(): void {
    this.sound.resume();
    this.start();
  }

  restart(): void {
    this.resetWorld();
  }

  /**
   * Terminal physics-fault handler (see `fatalFault`): logs the root error
   * once, stops the loop + audio via pause(), and raises the HUD fault
   * curtain naming the failure and the recovery (reload). No retry is
   * attempted — a panicked wasm world throws on every subsequent step.
   */
  private presentFatalFault(error: unknown): void {
    if (this.fatalFault) return;
    this.fatalFault = true;
    console.error("[trash-panic] physics core fault — run halted", error);
    this.pause();
    this.hud?.fatalFault(
      "SYSTEM FAULT",
      "PHYSICS CORE FAILURE — THIS SHIFT CANNOT CONTINUE. Reload the page to start a fresh run.",
    );
  }

  destroy(): void {
    this.pause();
    this.firstEncounter?.destroy();
    this.firstEncounter = null;
    this.tutorial?.destroy();
    this.tutorial = null;
    this.hud?.destroy();
    this.hud = null;
    this.postfx?.dispose();
    this.postfx = null;
    this.arena?.dispose();
    this.renderer?.dispose();
    this.world?.free();
  }

  /**
   * Resize the render surface. Safe in any loop state — pausing stops the RAF
   * loop but the renderer/composer resize immediately, so the paused frame
   * stays correct on resume. Inputs are normalized HERE, the one seam where
   * host sizes enter the render stack: a non-finite width/height/dpr (e.g. a
   * missing dpr from an untyped __tpz.resize call) becomes a NaN drawing
   * buffer, and NaN dimensions send the postprocessing Resolution change
   * listeners into unbounded mutual recursion (NaN !== NaN defeats their
   * change guard — RangeError: max call stack). See isSafeRenderSize.
   */
  resize(size: { width: number; height: number; dpr: number }): void {
    const width = Number.isFinite(size.width) && size.width >= 1 ? size.width : this.ctx.container.clientWidth || 1;
    const height = Number.isFinite(size.height) && size.height >= 1 ? size.height : this.ctx.container.clientHeight || 1;
    const dpr = Number.isFinite(size.dpr) && size.dpr > 0 ? size.dpr : window.devicePixelRatio || 1;
    this.renderer?.resize(width, height, dpr);
    this.postfx?.resize(width, height);
  }

  /**
   * Runtime quality tier switch (LOCKED-DESIGN 13.1 scalable visuals; 18.5
   * settings groundwork): renderer knobs apply in place, the post chain is
   * rebuilt for the tier. Render-only — the simulation never sees quality.
   */
  setQuality(level: QualityLevel): void {
    if (level === this.qualityLevel && this.postfx) return;
    this.qualityLevel = level;
    if (!this.renderer) return; // pre-mount: mount() applies ctx.quality
    this.renderer.setQuality(level);
    this.arena.setQuality(level);
    if (this.postfx) {
      this.postfx.dispose();
      const w = this.ctx.container.clientWidth || 1280;
      const h = this.ctx.container.clientHeight || 720;
      this.postfx = new PostFx(this.renderer.renderer, this.renderer.scene, this.renderer.camera, w, h, level);
    }
  }

  /**
   * Create/resume the AudioContext from a user gesture (LOCKED-DESIGN 18.6):
   * the title-card click primes the facility hum so the first run is never
   * silent. Idempotent — Sound.start() resumes an existing context.
   */
  primeAudio(): void {
    this.sound.start();
  }

  /** True while the interactive prologue is running (shell routes Escape to skip). */
  get tutorialActive(): boolean {
    return this.tutorial?.active ?? false;
  }

  /** The run's beam bulk capacity (6 everywhere since 18.15) — automation reads via window.__tpz. */
  get beamCapacity(): number {
    return this.beam.capacity;
  }

  /**
   * Test/automation hook (window.__tpz.debugAdvance): run N fixed steps of the
   * LIVE engine loop with neutral input, so a headless scenario can drive the
   * sim-tick-keyed Simple auto-portal move across a phase boundary without
   * waiting 30 real seconds. Only runs while a shift is actively playing (never
   * during the prologue). Mirrors the RAF loop's fixed-step body exactly, so it
   * exercises the real trace-recorded command stream — not a shortcut.
   */
  debugAdvance(ticks: number): void {
    // Simple-only automation hook (LOCKED-DESIGN 18.11 v2 QA): scoped so an
    // official/ranked shift can never fast-forward its tick clock (spawns, jams,
    // phase boundaries, overflow accrual) from the devtools console.
    if (!this.simpleMode) return;
    for (let i = 0; i < ticks; i++) {
      if (this.runEnded || this.run.phase !== "playing" || this.tutorial?.active) break;
      try {
        this.physicsStep(NEUTRAL_INPUT);
      } catch (error) {
        this.presentFatalFault(error);
        break;
      }
    }
  }

  /** Live run status for the pause-menu context panel (read-only). */
  getLiveStatus(): {
    score: number;
    grade: number;
    phaseIndex: number;
    timeLeft: number;
    overflow: number;
  } {
    return {
      score: this.run.score,
      grade: this.run.grade,
      phaseIndex: this.run.phaseIndex,
      timeLeft: this.run.timeLeft,
      overflow: this.run.overflow,
    };
  }

  /** Active rig view name — automation reads this via window.__tpz. */
  get cameraViewName(): string {
    return this.renderer?.rig.activeViewName ?? "arena-follow";
  }

  /** True while the rig is mid-blend — automation reads this via window.__tpz. */
  get cameraBlending(): boolean {
    return this.renderer?.rig.blending ?? false;
  }

  /**
   * Fraction (0..1) of the player's portal re-arm cooldown still to run
   * (LOCKED-DESIGN 18.10 presentation hook). A later HUD/FX stage reads this to
   * paint a brief "portal cooling" cue over the player right after a crossing;
   * 0 means fully re-armed. Sourced from the authoritative sim so the cue and
   * the deterministic rule can never drift.
   */
  get playerPortalCooling(): number {
    return this.run.portalRearmTicksLeft("player") / PORTAL_REARM_TICKS;
  }

  // ------------------------------------------------------------------

  private frame(now: number): void {
    // First-encounter teaching pause (LOCKED-DESIGN 12.2/18.10): freeze the sim
    // clock — no accumulator advance, no fixed steps — while the dismissable
    // card teaches the system. The frozen scene stays rendered behind it so the
    // player sees the jam/orb being explained. Unranked only; ranked never
    // pauses. Swallow the elapsed wall-time so resume never fast-forwards.
    if (this.firstEncounter?.active) {
      this.lastTime = now;
      this.fpsLast = now;
      this.fpsFrames = 0;
      this.accumulator = 0;
      if (this.postfx) this.postfx.render(0);
      else this.renderer.render();
      return;
    }

    const frameDt = Math.min((now - this.lastTime) / 1000, MAX_FRAME_DT);
    this.performanceElapsedMs += frameDt * 1000;
    this.lastTime = now;
    // Fixed steps NEVER pause (LOCKED-DESIGN 13.1/18.4): the accumulator is
    // always fed. Impact beats dip only the COSMETIC clock below.
    this.accumulator += frameDt;
    const fxDt = this.impactTimer > 0 ? frameDt * 0.35 : frameDt;
    if (this.impactTimer > 0) this.impactTimer = Math.max(0, this.impactTimer - frameDt);

    const playing = this.run.phase === "playing";
    const inputFrame = this.input.sample();
    if (inputFrame.resetPressed) this.onResetPressed();
    this.updateAim(inputFrame);

    // tutorial: device-aware prompts, skip, movement progress. Capture the
    // reference and run the finish-capable calls (skip / the ready-beat timer)
    // LAST — reportSkipPressed and update() can null this.tutorial mid-block.
    if (this.tutorial?.active) {
      const tut = this.tutorial;
      this.lastPad = inputFrame.padActive ? true : inputFrame.kbActive ? false : this.lastPad;
      tut.setPadMode(this.lastPad);
      const st = this.controller.state();
      tut.reportMove(Math.hypot(st.vx, st.vz) * frameDt);
      if (inputFrame.skipPressed) tut.reportSkipPressed();
      else tut.update(frameDt);
    }
    // Simple orientation "chase" beat (LOCKED-DESIGN 18.11 v2): the one scripted
    // ring move (floor -> ramp) with the full telegraph, driven off the tutorial
    // clock (the sim isn't stepping during warm-up). No-op outside the beat.
    this.updateOrientationMove(frameDt);

    // latch edges (survive zero-step frames)
    this.pending.jump ||= inputFrame.jumpPressed;
    this.pending.dash ||= inputFrame.dashPressed;
    this.pending.grabP ||= inputFrame.grabPressed;
    this.pending.grabR ||= inputFrame.grabReleased;
    this.pending.portalA ||= inputFrame.portalAPressed;
    this.pending.portalB ||= inputFrame.portalBPressed;
    this.pending.purge ||= inputFrame.purgePressed;
    if (!playing) {
      this.pending.jump = this.pending.dash = this.pending.grabP = this.pending.grabR = false;
      this.pending.portalA = this.pending.portalB = false;
      this.pending.purge = false;
    }

    // portal gun (instant, not physics-bound); denial is NEVER silent (18.4).
    // Simple v2 (LOCKED-DESIGN 18.11): manual portal placement is replaced by the
    // auto-moving ring, so Q/R are INERT — the latched edges are consumed
    // silently (no deny beat, no toast). The adult path is untouched.
    if (playing && !this.simpleMode) {
      if (this.pending.portalA) this.tryPlacePortal("A");
      if (this.pending.portalB) this.tryPlacePortal("B");
    }
    this.pending.portalA = this.pending.portalB = false;

    const stepInput = playing ? inputFrame : NEUTRAL_INPUT;
    if (playing) {
      stepInput.jumpPressed = this.pending.jump;
      stepInput.dashPressed = this.pending.dash;
      stepInput.grabPressed = this.pending.grabP;
      stepInput.grabReleased = this.pending.grabR;
      stepInput.purgePressed = this.pending.purge;
    }
    while (this.accumulator >= FIXED_DT) {
      this.accumulator -= FIXED_DT;
      // Fault containment: a wasm physics panic (Rapier "unreachable" /
      // "recursive use of an object") is unrecoverable — without this catch
      // the RAF loop kept running, re-throwing every frame over a frozen,
      // unexplained game. Fail the run loudly ONCE and stop the loop.
      try {
        this.physicsStep(stepInput);
      } catch (error) {
        this.presentFatalFault(error);
        return;
      }
      // consume one-shot edges after the first step that sees them
      stepInput.jumpPressed = false;
      stepInput.dashPressed = false;
      stepInput.grabPressed = false;
      stepInput.grabReleased = false;
      stepInput.purgePressed = false;
      this.pending.jump = this.pending.dash = this.pending.grabP = this.pending.grabR = false;
      this.pending.purge = false;
    }

    const alpha = this.accumulator / FIXED_DT;
    const state = this.controller.state();

    // movement juice: jump / land / dash edges
    if (playing) {
      if (this.prevGrounded && !state.grounded && state.vy > 4) {
        this.sound.jump();
      }
      if (!this.prevGrounded && state.grounded) {
        const impact = Math.min(1, Math.abs(this.prevVy) / 18);
        if (impact > 0.25) {
          this.renderer.landSquash(impact);
          this.sound.land();
          if (impact > 0.7) this.renderer.shake(0.12);
          this.tmpV.set(state.x, state.y - 0.85, state.z);
          this.particles.burst(this.tmpV, 0x8b95a7, 6, 2.2, { up: 0.5, life: 0.35, gravity: -4 });
        }
      }
      if (!this.prevDashing && state.dashing) {
        this.sound.dash();
        this.renderer.fovPunch(5);
      }
    }
    this.prevGrounded = state.grounded;
    this.prevVy = state.vy;
    this.prevDashing = state.dashing;
    // while beaming, face the cursor instead of the velocity
    if (this.beam.isHolding()) {
      const dx = this.aimPoint.x - state.x;
      const dz = this.aimPoint.z - state.z;
      if (dx * dx + dz * dz > 0.25) state.facingYaw = Math.atan2(dx, dz);
    }
    this.updateChamberView();
    // hero-rig aim state (18.3): the robot's head/visor looks at the aim point
    // and the beam-emitter dish tracks the held load, glowing with beam state
    this.renderer.updateAim(this.aimPoint, this.beam.isHolding());
    this.renderer.updatePlayer(this.prevPlayerPos, this.currPlayerPos, alpha, state, frameDt);
    this.updateTutorialTarget();
    this.trash.syncMeshes(alpha, this.renderer.scratchPos, this.renderer.scratchQuat);
    this.beam.updateVisual(this.renderer.playerMesh.position, this.aimPoint, this.assistTarget());
    // cosmetic clocks run on fxDt: the render-side impact dip (18.4 hitstop)
    this.fx.update(fxDt);
    this.particles.update(fxDt);
    this.trails.update();
    // ambient embers drifting up inside the maw
    if (Math.random() < frameDt * 7) {
      this.tmpV.set((Math.random() - 0.5) * 8, 0.7 + Math.random() * 2.5, -18.7);
      this.particles.burst(this.tmpV, 0xff7a2a, 1, 0.6, { up: 1.2, gravity: .4, life: 0.9, spread: 0.1 });
    }
    this.reticle.position.copy(this.aimPoint);
    if (this.reticleShake > 0) {
      // deny feedback: shake routes through the screenshake knob like every
      // impulse, and prefers-reduced-motion stills it entirely (the deny beat
      // still lands via sound + the reticle color flash below)
      const jitter = this.reducedMotionQuery?.matches
        ? 0
        : this.reticleShake * 0.32 * this.ctx.presentation.screenshakeIntensity;
      this.reticle.position.x += (Math.random() - 0.5) * jitter;
      this.reticle.position.y += (Math.random() - 0.5) * jitter * 0.5;
      this.reticle.position.z += (Math.random() - 0.5) * jitter;
      this.reticleShake = Math.max(0, this.reticleShake - frameDt * 1.7);
    }
    const reticleMat = this.reticle.material as THREE.MeshBasicMaterial;
    if (this.reticleDenyFlash > 0) {
      this.reticleDenyFlash = Math.max(0, this.reticleDenyFlash - frameDt * 2.4);
      reticleMat.color.setHex(FEEDBACK_COLORS.deny);
    } else if (reticleMat.color.getHex() !== PALETTE.avatar) {
      reticleMat.color.setHex(PALETTE.avatar);
    }
    this.reticle.quaternion.copy(this.renderer.camera.quaternion);
    this.animClock += fxDt;
    this.portals.animate(this.animClock);
    const phaseSpec = this.run.seed.phases[this.run.phaseIndex - 1]!;
    this.arena.updateGameplay(this.run.tick, phaseSpec);
    // Baffle pose is a pure function of tick, so render interpolation is exact:
    // evaluate it at the fractional tick between the last two fixed steps.
    this.arena.setBafflePose(this.run.baffleYawAt(this.run.tick - 1 + alpha));
    this.arena.updateOccupied(this.portals.A.pos, this.portals.B.pos);
    this.arena.updateLabelVisibility(this.renderer.camera);
    // close-range bulkhead haze fade (18.10 4B): thin the glass treatment as
    // the camera closes so the chamber stays legible point-blank
    this.arena.updateGlassProximity(this.renderer.camera.position);
    if (this.run.activeRoute.anchor === "rail") {
      const pose = this.arena.railPose();
      if (pose) this.portals.followEnd("A", pose.pos, pose.normal);
    }
    this.arena.animate(this.animClock, fxDt);
    this.updatePresentation(playing, phaseSpec, fxDt);
    if (this.postfx) this.postfx.render(frameDt);
    else this.renderer.render();

    // audio: host volumes + the three-axis music model (18.4). Each axis
    // drives its own bus — phase → arrangement layers, chain → percussion,
    // overflow → filter tension — replacing the old bundled max() scalar.
    // Alarm/fuse priority ducking is event-driven inside Sound.
    const junk = this.run.overflow;
    this.sound.poll(this.ctx.muted, this.ctx.masterVolume, this.ctx.sfxVolume, this.ctx.musicVolume, {
      phase: this.run.phaseIndex,
      chainTier: this.run.chainTier,
      overflow01: Math.min(1, junk / OVERFLOW_LIMIT),
    });

    this.hud?.update(this.buildHudModel(phaseSpec));
    this.updateFps(now);
  }

  /**
   * The intent gate refused a chamber entry — teach the verb (18.25 / F7).
   *
   * A silent refusal is worse than the bug it fixes: the child leans on the ring,
   * nothing happens, and the game has taught them the ring is broken. So the
   * refusal speaks, at the ring, in the mode's own voice.
   *
   * Deliberately a `popup` and not a `toast`: toast is a single latest-wins
   * element with fourteen call sites, and the one most likely to be on screen
   * here is "RING MOVED! FOLLOW IT!" — the exact cue that sent the player to
   * this ring. Popups also carry no `data-hud-element`, so they stay out of the
   * HUD readability overlap check and cannot re-open F12's world-tag stacking.
   *
   * Rate-limited because the gate is evaluated every fixed step and a child
   * standing in the capture volume would otherwise trigger it 60 times a second.
   */
  private notePortalRefusedForIntent(): void {
    // Counted BEFORE the nudge cooldown, so it measures the gate rather than the
    // copy. Without it "the player did not end up in the chamber" is satisfied
    // just as well by a drive that never reached the ring at all, and the QA
    // assertion would pass for the wrong reason — the exact failure mode that
    // kept anchorHop's telegraph bound green over an out-of-reach hop.
    this.intentRefusals++;
    if (this.run.tick - this.lastIntentNudgeTick < INTENT_NUDGE_COOLDOWN_TICKS) return;
    this.lastIntentNudgeTick = this.run.tick;
    if (!this.hud) return;
    const screen = this.projectToHud(this.portals.A.pos);
    // Name the control the player's own device actually has (the pad's A button
    // is the jump the keyboard spells "Space").
    const jump = this.lastPad ? "A" : "SPACE";
    this.hud.popup(
      screen.x,
      screen.y,
      this.simpleMode ? "JUMP IN!" : `${jump} TO JUMP IN`,
      cssHex(PALETTE.anchorBlue),
    );
  }

  /** World point → container-space pixels (for HUD popups). */
  private projectToHud(world: THREE.Vector3): { x: number; y: number } {
    // The tutorial cue is updated before the WebGL render. Keep projection in
    // lockstep with the camera pose chosen by updatePlayer this frame instead
    // of using the previous render's matrixWorld.
    this.renderer.camera.updateMatrixWorld();
    const v = this.renderer.scratchPos.copy(world).project(this.renderer.camera);
    const w = this.ctx.container.clientWidth || 1;
    const h = this.ctx.container.clientHeight || 1;
    return { x: (v.x * 0.5 + 0.5) * w, y: (-v.y * 0.5 + 0.5) * h };
  }

  /**
   * Assemble the per-frame HUD model (LOCKED-DESIGN 12.1 / 18.5). Pure read of
   * sim getters + beam state — no sim writes, so the fixture hashes cannot move.
   * The dispatch panel, shift telegraph, reticle dock, and baffle gate all come
   * from the sim's read-only view getters.
   */
  private buildHudModel(phaseSpec: PhaseSpec): HudModel {
    const activeTicket = this.run.activeTicket();
    const holding = this.beam.isHolding();
    const composition = holding ? this.beam.heldComposition() : [];
    const route = { anchor: this.run.activeRoute.anchor, receiver: this.run.activeRoute.receiver };
    // Route economics at decision time (LOCKED-DESIGN 8.3 / 18.10 fourth
    // dogfood): the sim's own freshness + truthful projection for the held
    // load, plus the best alternative route ONLY when switching visibly pays.
    const heldKinds = composition.flatMap((c) => Array.from({ length: c.count }, () => c.kind));
    const projected = holding ? this.run.projectedScore(heldKinds, this.run.activeRoute) : 0;
    const alternative = holding ? this.run.projectedAlternative(heldKinds) : null;
    const freshness = this.run.routeFreshness();
    // Switching pays on SCORE or on GRADE (18.10 fourth dogfood): anchors carry
    // no score term, so the fresher same-receiver anchor swap always ties on
    // score — the Section 8.3 Grade swing must still surface the ALT cell.
    const altPays = alternative !== null && (alternative.score > projected
      || (alternative.score === projected && alternative.score > 0 && alternative.freshness.gradeDelta > freshness.gradeDelta));
    const reticle: HudReticle | null = holding
      ? {
          bulk: this.beam.heldBulk(),
          // Seed-derived (LOCKED-DESIGN 18.11 v2): adults still read 6/6; a Simple
          // run reads 3/3 (though the whole reticle dock is display:none in Simple).
          capacity: this.beam.capacity,
          composition,
          redFuse: this.beam.redFuseSeconds(),
          route,
          freshness: { state: freshness.state, delta: freshness.gradeDelta },
          ticketContribution: this.ticketContribution(activeTicket, composition),
          projectedScore: projected,
          projectedAlt: alternative && altPays
            ? {
                anchor: alternative.route.anchor,
                receiver: alternative.route.receiver,
                score: alternative.score,
                freshness: { state: alternative.freshness.state, delta: alternative.freshness.gradeDelta },
              }
            : null,
        }
      : null;
    return {
      score: this.run.score,
      timeLeft: this.run.timeLeft,
      overflow: this.run.overflow,
      overflowBuckets: this.overflowBuckets(),
      grade: this.run.grade,
      chainTier: this.run.chainTier,
      chainMultiplier: this.run.multiplier(),
      chainFrac: this.run.comboFrac(),
      shift: this.run.shiftChange(),
      activeTicket,
      nextTicket: this.run.nextTicket(),
      reticle,
      criticalSeconds: this.run.criticalExpiresAt === null ? null : Math.max(0, (this.run.criticalExpiresAt - this.run.tick) / 60),
      // Explicit hazard-baffle timing feedback (LOCKED-DESIGN 3.2): the
      // lead-compensated gate the simulation applies to a release THIS tick.
      baffleGate: phaseSpec.activeReceivers.includes("hazard")
        ? { blocked: this.run.baffleBlocked, secondsToOpen: this.run.baffleSecondsToNextOpen, secondsToClose: this.run.baffleSecondsToNextClose }
        : null,
      motionReduced: this.flashReduced(),
    };
  }

  /**
   * Where the bulk actually is (Section 7 buckets, LOCKED-DESIGN 18.10 third
   * dogfood): held, reject-tray, and belt bulk come from the live physical
   * pieces (they ARE the locations); portal-transit bulk comes from the sim's
   * read-only pendingLoads view (the authority on released-but-unresolved
   * loads); the floor is the remainder against the authoritative total, so the
   * segments always sum to the meter and 36/36 over a clean floor names WHERE.
   * Pure read — no sim writes, no ledger events.
   */
  private overflowBuckets(): HudOverflowBuckets {
    let beam = 0;
    let chamber = 0;
    let belt = 0;
    for (const ride of this.returnRides) if (ride.piece.live && !ride.piece.held) belt += ride.piece.bulk;
    for (const piece of this.trash.pieces) {
      if (!piece.live) continue;
      if (piece.held) beam += piece.bulk;
      // Chamber bulk (LOCKED-DESIGN 18.12): the jam pile is now keyed off jamLane /
      // the chamber flag, not the old rejectReady lifecycle, so the segmented meter
      // stays truthful under the feed-the-maw model (a belt-riding reject counts as
      // belt above, not chamber).
      else if ((piece.jamLane !== null || (piece.rejected && piece.chamber)) && !this.returnRides.some((ride) => ride.piece === piece)) chamber += piece.bulk;
    }
    const total = this.run.overflow;
    const transit = Math.min(this.run.transitBulk, Math.max(0, total - beam - chamber - belt));
    const floor = Math.max(0, total - beam - chamber - belt - transit);
    return { floor, beam, transit, chamber, belt };
  }

  /**
   * Held-load contribution toward the active dispatch ticket (reticle field).
   * Mirrors the sim's ticketEligible rule: a load counts only if EVERY piece is
   * a recipe piece and the route clause is satisfied; contribution is the count
   * of held pieces that still fit under the remaining per-kind requirement.
   */
  private ticketContribution(
    active: { entries: Array<{ kind: string; required: number; have: number }>; receiver: string | null } | null,
    composition: Array<{ kind: string; count: number }>,
  ): number | null {
    if (!active) return null;
    const remaining = new Map(active.entries.map((entry) => [entry.kind, Math.max(0, entry.required - entry.have)]));
    let total = 0;
    for (const held of composition) {
      if (!remaining.has(held.kind)) return 0; // off-recipe piece → whole load ineligible
      total += Math.min(held.count, remaining.get(held.kind) ?? 0);
    }
    const clauseOk = !active.receiver || active.receiver === this.run.activeRoute.receiver;
    return clauseOk ? total : 0;
  }

  // ---------------- presentation layer (LOCKED-DESIGN 18.4) ----------------
  // Everything below is render-side: it reads the sim (getters + ledger) and
  // never writes back. Determinism note: the fixture hashes cannot move here.

  /** flashReduction knob OR prefers-reduced-motion — either caps every flash/pulse. */
  private flashReduced(): boolean {
    return this.ctx.presentation.flashReduction || (this.reducedMotionQuery?.matches ?? false);
  }

  /**
   * Chamber traversal camera (LOCKED-DESIGN 2.2 / 18.8 predicate #3): entering
   * the chamber-block footprint blends to the fixed 'chamber' view; leaving
   * blends back. The z threshold is the SAME derived-geometry value the sim's
   * chamber predicates use, bounded to the 24-wide block (18.13) so walking
   * the open north-shoulder floor never hijacks the follow camera.
   * prefers-reduced-motion turns the swoop into a hard cut.
   */
  private updateChamberView(): void {
    const inChamber = inChamberBlock(this.currPlayerPos, this.arenaGeometry);
    if (inChamber === this.chamberViewOn) return;
    this.chamberViewOn = inChamber;
    const duration = this.reducedMotionQuery?.matches ? 0 : CAMERA_BLEND.chamberSeconds;
    if (!inChamber) {
      // Returning: the follow view's smoothing target is stale from before
      // the traversal — hard-cut it onto the player so the blend lands
      // centered instead of swinging through old framing (Phase 0 caveat).
      this.renderer.rig.snapFollow(this.currPlayerPos.x, this.currPlayerPos.z);
    }
    this.renderer.rig.blendTo(inChamber ? "chamber" : "arena-follow", duration);
  }

  /** Full-screen flash with the reduction policy applied in ONE place. */
  private flash(color: string, peak: number, durationSeconds: number): void {
    if (!this.hud) return;
    const reduced = this.flashReduced();
    this.hud.flash(color, reduced ? Math.min(peak, 0.22) : peak, reduced ? Math.min(durationSeconds, 0.18) : durationSeconds);
  }

  /** Placement attempt for one end; failure gets the deny beat, never silence. */
  private tryPlacePortal(end: "A" | "B"): void {
    // During the prologue, placement is gated to the matching teaching step
    // (LOCKED-DESIGN 18.6): a blue anchor is placeable only on the BLUE beat and
    // an orange receiver only on the ORANGE beat, so an unrelated step can't be
    // short-circuited by revealing/relocating a portal on a lit panel.
    if (this.tutorial?.active) {
      const step = this.tutorial.currentStep();
      const allowed = end === "A" ? step === "blue" : step === "orange";
      if (!allowed) {
        this.presentDeny();
        return;
      }
    }
    const target = this.aimPlaceable && this.aimObject ? this.arena.portalTarget(this.aimObject, end) : null;
    const phase = this.run.seed.phases[this.run.phaseIndex - 1];
    const active = end === "A"
      ? (phase?.activeAnchors.includes(target?.name as ArenaAnchor) ?? false)
      : (phase?.activeReceivers.includes(target?.name as ReceiverTier) ?? false);
    if (target && active && this.portals.place(end, target.pos, target.normal)) {
      if (this.tutorial?.active) {
        // Orientation reveal (LOCKED-DESIGN 18.6): the chosen portal appears
        // exactly where it was aimed, no arming slide, so placement reads crisp.
        this.portals.snapPending(end);
        this.portals.setLive(end, true);
      }
      this.pendingSimulationActions.push({ type: "place", endpoint: end === "A" ? "blue" : "orange", target: target.name });
      const portalEnd = end === "A" ? this.portals.A : this.portals.B;
      this.fx.spawn(portalEnd.pos, portalEnd.normal, end === "A" ? PALETTE.anchorBlue : PALETTE.receiverOrange, 1.2);
      this.tutorial?.reportPortalPlaced(end === "A" ? "blue" : "orange");
      return;
    }
    // inactive anchor/receiver, non-target surface, or arming interlock
    this.presentDeny();
  }

  // ---- Simple v2 auto-moving portal (LOCKED-DESIGN 18.11) ----

  /** The run's authored blue-anchor cycle, or null outside Simple (adult/official never auto-move). */
  private simplePortalCycle(): readonly ArenaAnchor[] | null {
    if (!this.simpleMode) return null;
    return this.run.seed.rules?.portalCycle ?? null;
  }

  /**
   * The anchor the entrance portal SHOULD sit at for a given sim tick, with the
   * locked 45-tick (0.75s) arming lead baked in: shifting the desired anchor to
   * the next cycle entry 45 ticks before the phase boundary means the place
   * action's sim-side arming (readyAt = tick+45) lands the atomic swap exactly
   * on the boundary. Clamped so the pre-roll and the final phase are stable.
   */
  private simpleDesiredAnchor(tick: number, cycle: readonly ArenaAnchor[]): ArenaAnchor {
    const index = Math.max(0, Math.min(cycle.length - 1, Math.floor((tick + 45) / 1800)));
    return cycle[index] ?? "floor";
  }

  /**
   * The declarative auto-move rule (engine-issued scheduled place action). Runs
   * once per fixed step in the live loop only: if the sim's live route differs
   * from the tick's desired anchor and no blue placement is already armed, push
   * a normal `place` action into THIS step's command stream. The -45 lead lands
   * the swap on the boundary; the one-slot queue check makes the rule its own
   * jam-deferral retry without ever double-queuing (risk register: scheduler
   * double-issue). Keyed purely on sim tick, so practice offsets / countdown
   * pre-roll issue exactly one coalescing move, never a backlog.
   */
  private maybeIssueSimplePortalMove(actions: SimulationAction[]): void {
    const cycle = this.simplePortalCycle();
    if (!cycle) return;
    const desired = this.simpleDesiredAnchor(this.run.tick, cycle);
    if (this.run.activeRoute.anchor === desired) return;
    if (this.run.bluePlacementPending) return; // the queued move IS the retry
    // Guard against a duplicate blue place already staged this step (defensive;
    // the rule runs once per step so the local buffer is normally clean here).
    if (actions.some((action) => action.type === "place" && action.endpoint === "blue")) return;
    actions.push({ type: "place", endpoint: "blue", target: desired });
  }

  /**
   * Per-frame Simple portal telegraph (LOCKED-DESIGN 18.11; widened to 8s for the
   * 56-wide arena per 18.12): the 8s kid banner
   * countdown plus a world-anchored destination beacon at the next anchor's
   * authored frame. Honest per the risk register — the beacon tracks the ACTUAL
   * route vs the desired anchor (so a jam-deferred move keeps the beacon lit
   * past the boundary instead of lying), while the banner counts down the 8s
   * pre-boundary window. Pure read of sim tick + activeRoute; no sim writes.
   */
  /**
   * Pure-tick Simple portal telegraph state (LOCKED-DESIGN 18.11): banner window
   * + the honest beacon anchor. `bannerActive` is the 8s (480-tick) window before
   * a boundary where the ring actually changes anchors; `beaconAnchor` stays lit
   * whenever the live route hasn't reached the desired anchor yet (covers the 8s
   * telegraph, the 45-tick arm, AND any jam deferral past the boundary). Null
   * outside Simple. Exposed for QA (window.__tpz.simplePortalTelegraph) so the
   * e2e can assert the telegraph fires without waiting 30 real seconds.
   */
  get simplePortalTelegraph(): { bannerActive: boolean; count: number; beaconAnchor: ArenaAnchor | null } | null {
    const cycle = this.simplePortalCycle();
    if (!cycle) return null;
    const tick = this.run.tick;
    const desired = this.simpleDesiredAnchor(tick, cycle);
    const boundaryIndex = Math.floor(tick / 1800) + 1; // 1..6 (next boundary's cycle index)
    const ticksToBoundary = boundaryIndex * 1800 - tick;
    // boundaryIndex 0 (the pre-roll countdown, tick < 0) is the shift START, not
    // an anchor-change boundary — never telegraph a move there.
    const boundaryMoves = boundaryIndex >= 1 && boundaryIndex <= 5 && cycle[boundaryIndex] !== cycle[boundaryIndex - 1];
    const bannerActive = boundaryMoves && ticksToBoundary > 0 && ticksToBoundary <= 480;
    const beaconAnchor = this.run.activeRoute.anchor !== desired ? desired : bannerActive ? cycle[boundaryIndex]! : null;
    return { bannerActive, count: bannerActive ? Math.ceil(ticksToBoundary / 60) : 0, beaconAnchor };
  }

  private updateSimplePortalTelegraph(playing: boolean): void {
    // The orientation "chase" beat paints its own banner + ramp beacon via
    // updateOrientationMove earlier this same frame (LOCKED-DESIGN 18.11 v2).
    // Bail so we don't hide the kid's one scripted teaching move the instant it
    // is set — that stomp made the countdown and OVER HERE beacon invisible.
    if (this.orientationMove) return;
    const state = playing && !this.tutorial?.active ? this.simplePortalTelegraph : null;
    if (!state) {
      this.hud?.portalBanner(null, "");
      this.hud?.portalBeacon(null);
      return;
    }
    this.hud?.portalBanner(state.bannerActive ? "THE RING IS MOVING!" : null, state.bannerActive ? String(state.count) : "");
    const authored = state.beaconAnchor ? this.authoredTarget("A", state.beaconAnchor) : undefined;
    if (state.beaconAnchor && authored) {
      const screen = this.projectToHud(this.tmpV.copy(authored.pos).addScaledVector(authored.normal, 0.4));
      this.hud?.portalBeacon({ x: screen.x, y: screen.y, target: state.beaconAnchor });
    } else {
      this.hud?.portalBeacon(null);
    }
  }

  /**
   * React to the sim's atomic blue-route swap (Simple only): relocate the render
   * ring to the new anchor's authored frame, fizz at the old spot, burst at the
   * new, and drop the kid follow toast. Driven off the sim's `portal-swapped`
   * ledger event (never a parallel clock), so a jam-deferred move relocates the
   * ring exactly when the route actually changes. Arms the arrival celebration
   * and the one-shot first-move teaching pause. Adult play ignores this — its
   * render ring already moved on manual placement (tryPlacePortal).
   */
  private onSimplePortalSwapped(target: ArenaAnchor): void {
    const authored = this.authoredTarget("A", target);
    const oldPos = this.tmpV.copy(this.portals.A.pos);
    // fizz at the old spot BEFORE relocating
    this.fx.spawn(oldPos, this.portals.A.normal, PALETTE.anchorBlue, 1.0, 0.35);
    this.particles.burst(oldPos, PALETTE.anchorBlue, 10, 3.2, { spread: 0.8, gravity: -2, life: 0.45 });
    if (authored) {
      this.portals.followEnd("A", authored.pos, authored.normal);
      this.fx.spawn(this.portals.A.pos, this.portals.A.normal, PALETTE.anchorBlue, 1.4, 0.5);
      this.particles.burst(this.portals.A.pos, PALETTE.anchorBlue, 14, 4, { spread: 0.9, gravity: -2, life: 0.55 });
    }
    this.sound.portalZap();
    this.hud?.toast("RING MOVED! FOLLOW IT!", cssHex(PALETTE.anchorBlue), 2200);
    // One-shot first-move teaching pause (LOCKED-DESIGN 18.11 v2), alongside the
    // IT'S STUCK card — fires once per shift the first time the ring moves.
    if (!this.taughtRingMove && !this.tutorial?.active) {
      this.taughtRingMove = true;
      this.teachFirstEncounter("ringmove");
    }
  }

  /**
   * Drive the Simple orientation "chase" beat's one scripted ring move (18.11
   * v2). While the tutorial sits on "chase", telegraph floor -> ramp for the
   * full 8s (kid banner countdown + ramp beacon), then relocate the render ring
   * once. Tutorial-clock driven because the sim doesn't step during warm-up; the
   * real in-shift auto-move rides the sim's scheduled-action machinery instead.
   */
  private updateOrientationMove(dt: number): void {
    const inChase = this.simpleMode && this.tutorial?.active === true && this.tutorial.currentStep() === "chase";
    if (!inChase) {
      if (this.orientationMove) {
        this.orientationMove = null;
        this.hud?.portalBanner(null, "");
        this.hud?.portalBeacon(null);
      }
      return;
    }
    if (!this.orientationMove) this.orientationMove = { elapsed: 0, relocated: false };
    const state = this.orientationMove;
    state.elapsed += dt;
    const remaining = SIMPLE_ORIENTATION_TELEGRAPH_S - state.elapsed;
    this.hud?.portalBanner(remaining > 0 ? "THE RING IS MOVING!" : null, remaining > 0 ? String(Math.max(1, Math.ceil(remaining))) : "");
    if (remaining <= 0 && !state.relocated) {
      state.relocated = true;
      this.onSimplePortalSwapped("ramp");
      // Unlock the chase beat's graduation: only NOW may an incineration cross
      // "chase" -> "ready" (LOCKED-DESIGN 18.11 v2), so the kid always sees the
      // ring actually move before the beat can complete.
      this.tutorial?.reportRingMoved();
    }
    // Beacon tracks the ramp anchor for the whole beat so a kid always sees WHERE.
    const authored = this.authoredTarget("A", "ramp");
    if (authored) {
      const screen = this.projectToHud(this.tmpV.copy(authored.pos).addScaledVector(authored.normal, 0.4));
      this.hud?.portalBeacon({ x: screen.x, y: screen.y, target: "ramp" });
    }
  }

  /** Deny beat: flash at the aimed target + reticle shake + deny audio hook. */
  private presentDeny(): void {
    this.sound.deny();
    this.reticleShake = Math.max(this.reticleShake, 0.4);
    this.reticleDenyFlash = 0.5;
    this.fx.spawn(this.aimPoint, this.aimNormal, FEEDBACK_COLORS.deny, 0.9);
  }

  /**
   * The enforced glass beat's payoff (LOCKED-DESIGN 18.6): a bespoke reinforced-
   * bulkhead hit — glass-stress shimmer, a shard-cool ring/particles, a small
   * flash-aware kick, its own SFX, and the deadpan "SEALED" facility placard.
   */
  private presentGlassHit(x: number, y: number): void {
    this.sound.glassHit();
    this.arena.stressGlass(1);
    this.tmpV.set(x, y, GLASS_FRONT_Z);
    this.fx.spawn(this.tmpV, new THREE.Vector3(0, 0, 1), FEEDBACK_COLORS.bulkhead, 1.3, 0.4);
    this.particles.burst(this.tmpV, FEEDBACK_COLORS.bulkhead, 14, 4, { spread: 0.7, up: 0.4, life: 0.5, gravity: -6 });
    this.flash(cssHex(FEEDBACK_COLORS.bulkhead), 0.22, 0.16);
    this.renderer.shake(0.16);
    this.hud?.toast("SEALED — USE THE TRANSFER SYSTEM", cssHex(FEEDBACK_COLORS.overflowWarning), 2600);
  }

  /**
   * First-encounter teaching beat (LOCKED-DESIGN 12.2/18.10). A brief,
   * dismissable PAUSE (not the missed Phase-3 toast): frame() freezes the sim
   * while the card explains the verb. The first-jam beat teaches recovery (the
   * glowing handle is already lit in-world); the first-red beat teaches the
   * fuse danger + emergency chute. Unranked only, one-shot ever — the caller
   * sets the persisted flag before calling and we save it here.
   */
  private teachFirstEncounter(kind: "red" | "jam" | "stale" | "ringmove"): void {
    if (this.firstEncounter) return;
    const spec = kind === "ringmove"
      ? {
          // Simple v2 first-move pause (LOCKED-DESIGN 18.11): the ring just moved
          // for the first time — a brief, dismissable kid card teaching the chase.
          eyebrow: "LOOK!",
          title: "THE RING MOVES!",
          body: "Follow the blue ring. Throw trash in!",
          accent: cssHex(PALETTE.anchorBlue),
          dismiss: this.lastPad ? "Ⓐ GOT IT ▸" : "GOT IT ▸",
        }
      : kind === "red"
      ? {
          eyebrow: "FIRST CONTACT · RED ORB",
          title: "Armed red orb — 8-second fuse",
          body: "That orb is now counting down. Route it into the furnace fast, or dump a full six-unit load down the emergency chute (C). If it detonates in the pile it scatters trash and resets your Chain.",
          accent: cssHex(FEEDBACK_COLORS.alarm),
        }
      : kind === "jam"
        ? this.simpleMode
          ? {
              // Early-reader jam beat (LOCKED-DESIGN 18.12): Simple now teaches the
              // feed-the-maw loop in kid words — the guided FEED THE FIRE cue chain
              // (armed per jam) carries the live teaching after this card.
              eyebrow: "UH OH!",
              title: "IT'S STUCK!",
              body: "Ride the ring inside. Feed the trash to the fire!",
              accent: cssHex(RECEIVER_TIER_COLORS.safe),
              dismiss: this.lastPad ? "Ⓐ GOT IT ▸" : "GOT IT ▸",
            }
          : {
              eyebrow: "FIRST CONTACT · RECEIVER JAM",
              title: "That receiver is jammed — and it will not clear itself",
              body: `A rejected pile is blocking the lane. Ride a portal into the chamber, beam-grab the rejects, and feed them into the furnace maw. Zero points — this is cleanup, not scoring — but every bulk you feed shrinks the pile, and the lane reopens when it's gone. Two clocks: the chamber cooks you in ${Math.round(this.run.dwellTicksFor(this.run.seed) / 60)}s (watch CHAMBER HEAT, leave to reset it), and a neglected jam overloads the furnace in ${Math.round(this.run.overloadTicksFor(this.run.seed) / 60)}s. The return pad launches you home. Don't touch the maw.`,
              accent: cssHex(RECEIVER_TIER_COLORS.safe),
            }
        : {
            // Section 8.3 economics at the moment they first bite (LOCKED-DESIGN
            // 18.10 fourth dogfood: 58 clean loads ground Grade to 0 at ×1.0).
            eyebrow: "FIRST NOTICE · STALE ROUTE",
            title: "Same route three times — Grade docks you",
            body: "That was your third clean load down the exact same anchor→receiver pair, so it read STALE: −2 Sanitation Grade instead of +6 for a varied route. Grade multiplies EVERY load — ×1.0 at C up to ×1.5 at S — so vary your routes (swap either endpoint) and the A/S bands pay for themselves. The ROUTE readout and the receiver sign show FRESH / REPEAT / STALE before you release.",
            accent: cssHex(FEEDBACK_COLORS.stale),
          };
    this.firstEncounter = new FirstEncounter(this.ctx.container, spec, this.lastPad);
    this.firstEncounter.onDismiss(() => {
      this.firstEncounter = null;
      // Drain the buffered dismiss key so resume doesn't fire it as a game edge.
      this.ctx.getInput();
      this.lastTime = performance.now();
      this.fpsLast = this.lastTime;
      this.fpsFrames = 0;
    });
    void this.ctx.save("first-encounters", { red: this.taughtRedFuse, jam: this.taughtJam, stale: this.taughtStale }).catch(() => {
      /* best-effort; worst case the beat replays next session */
    });
  }

  /** Authored anchor/receiver lookup by endpoint + name (for tutorial cues). */
  private authoredTarget(endpoint: "A" | "B", name: string): AuthoredTarget | undefined {
    for (const target of this.arena.authored.values()) {
      if (target.endpoint === endpoint && target.name === name) return target;
    }
    return undefined;
  }

  /** Receiver lane → authored chamber target (world anchor for popups/tags). */
  private receiverTarget(lane: ReceiverTier): AuthoredTarget | null {
    for (const target of this.arena.authored.values()) {
      if (target.endpoint === "B" && target.name === lane) return target;
    }
    return null;
  }

  /**
   * Nearest live, un-held jam reject body on a lane, or null if none is physically
   * present yet. Drives the Simple "GRAB THE TRASH!" cue so it leads the player to a
   * real piece instead of the fixed receiver anchor (ninth dogfood legibility fix).
   */
  private nearestReject(lane: ReceiverTier): THREE.Vector3 | null {
    let best: THREE.Vector3 | null = null;
    let bestD = Infinity;
    for (const p of this.trash.pieces) {
      if (!p.live || p.held || p.jamLane !== lane) continue;
      const d = p.mesh.position.distanceToSquared(this.currPlayerPos);
      if (d < bestD) { bestD = d; best = p.mesh.position; }
    }
    return best;
  }

  private findLoad(loadId: string): LoadRecord | undefined {
    const loads = this.run.loads;
    for (let index = loads.length - 1; index >= 0; index--) {
      if (loads[index]!.id === loadId) return loads[index];
    }
    return undefined;
  }

  /** Per-frame presentation polling: grade band, overflow pressure, alarm, telegraphs. */
  private updatePresentation(playing: boolean, phaseSpec: PhaseSpec, fxDt: number): void {
    // arena strobes (shutter warn strip, chute telegraphs, gate lamp, glass
    // flicker) follow the same flash policy as every screen flash (12.3/18.5)
    this.arena.setFlashReduced(this.flashReduced());
    this.updateFuseHalos(fxDt);
    this.updateChamberRecovery(playing && !this.tutorial?.active);

    // Grade band change = arena-wide lighting moment (18.4), kept gentle (2.2).
    // The toast names the band's MULTIPLIER (LOCKED-DESIGN 8.3 / 18.10 fourth
    // dogfood: the ×1.0→×1.5 economics must be visible) — value read from the
    // sim's own gradeMultiplier table, never restated here.
    const band = bandOf(this.run.grade);
    if (band !== this.prevGradeBand) {
      const direction = GRADE_BANDS.indexOf(band) > GRADE_BANDS.indexOf(this.prevGradeBand) ? "up" : "down";
      this.renderer.setGradeBand(band);
      // Simple hides the Grade meter + multiplier arithmetic (LOCKED-DESIGN
      // 18.11): the grade band still crosses in the sim (route-repeat/stale
      // penalties keep running), but a kid must never see "×1.15" toasts for a
      // meter that isn't on screen. The arena lighting moment still plays.
      if (playing && !this.tutorial?.active && !this.simpleMode) {
        this.sound.gradeBand(direction, band);
        const mult = (gradeMultiplier(this.run.grade) / 100).toFixed(2).replace(/0$/, "");
        this.hud?.toast(
          direction === "up" ? `GRADE ${band} · ALL LOADS ×${mult} — keep it rolling.` : `GRADE ${band} · ALL LOADS ×${mult}`,
          cssHex(direction === "up" ? PALETTE.tierSafe : FEEDBACK_COLORS.muted),
          1700,
        );
      }
      this.prevGradeBand = band;
    }

    // Overflow pressure BEFORE critical: locked 24/30 thresholds, escalating.
    // Hysteresis: a level escalates the instant its threshold is crossed but
    // only de-escalates after junk falls OVERFLOW_REARM_MARGIN below it, so
    // pressure hovering at 23↔24 or 29↔30 can't re-fire the beeps + duck
    // on every upward crossing (deliveries subtract 1-6 bulk constantly).
    const inRun = playing && !this.tutorial?.active;
    const junk = this.run.overflow;
    const previous = this.overflowLevel;
    const level = !inRun ? null
      : junk >= OVERFLOW_SEVERE ? "severe" as const
      : previous === "severe" && junk >= OVERFLOW_SEVERE - OVERFLOW_REARM_MARGIN ? "severe" as const
      : junk >= OVERFLOW_WARNING ? "warning" as const
      : previous !== null && junk >= OVERFLOW_WARNING - OVERFLOW_REARM_MARGIN ? "warning" as const
      : null;
    if (level !== previous) {
      const rank = (value: typeof level): number => (value === "severe" ? 2 : value === "warning" ? 1 : 0);
      if (level !== null && rank(level) > rank(previous)) this.sound.overflowWarning(level);
      this.overflowLevel = level;
    }
    if (!this.alarmActive) this.hud?.setPressure(level);

    // Return-incoming world cue (Section 6 / 18.10): the hatch beside the
    // island announces the reject bulk headed back — sitting in the tray or
    // already riding the belt — so "that trash comes BACK" reads before it
    // lands. Bulk census only; the sim owns all timing.
    let incoming = 0;
    for (const piece of this.trash.pieces) if (piece.live && piece.rejectReady) incoming += piece.bulk;
    for (const ride of this.returnRides) if (ride.piece.live && !ride.piece.held) incoming += ride.piece.bulk;
    this.arena.setReturnIncoming(inRun ? incoming : 0);

    // Critical overflow: true alarm state — pulsing edge vignette (flash-aware).
    // Simple softens it to a gentle steady glow (LOCKED-DESIGN 18.11 soft
    // failure): no adult strobing red frame, matched to the paired silent klaxon.
    if (this.alarmActive) {
      const pulse = this.simpleMode || this.flashReduced()
        ? this.simpleMode ? 0.2 : 0.55
        : 0.5 + 0.5 * Math.sin(this.animClock * 6);
      this.hud?.setAlarm(true, pulse);
    }

    if (inRun) {
      this.updateHazardTelegraphs(phaseSpec);
      this.updateBatchTelegraphs();
      // Order matters: world tags own the event flares (the persistent jam
      // entrance chevron is issued/retracted here), and the chute chevrons
      // separate themselves against THIS frame's flare anchors (18.19).
      this.updateWorldTags();
      this.updateChuteIndicators();
      // World-space FRESH/REPEAT/STALE + Grade-delta chips are route-economics
      // arithmetic; Simple drops signage to role icons only (LOCKED-DESIGN
      // 18.11), so the chips stay off and the receiver sign shows its icon alone.
      if (this.simpleMode) this.arena.setFreshnessChips([]);
      else this.updateRouteFreshnessSignage(phaseSpec);
    } else {
      this.arena.setFreshnessChips([]);
      // Tutorial / run ended / paused: retract every chute cue. The tutorial
      // owns the frame, and a finished run has no live intake to announce.
      if (this.chuteIndicatorSpecs.length > 0) {
        this.chuteIndicatorSpecs = [];
        this.hud?.setChuteIndicators([]);
      }
    }
    // Simple v2 auto-portal telegraph (18.11): kid banner countdown + world
    // destination beacon. Pure read of sim tick + activeRoute; hides itself
    // outside Simple play (guards inside).
    this.updateSimplePortalTelegraph(inRun);
  }

  /**
   * Route economics on the world signage (LOCKED-DESIGN 8.3 / 18.10 fourth
   * dogfood): the committed route's receiver sign carries its live
   * FRESH/REPEAT/STALE + Grade-delta chip, and AIMING at a different active
   * anchor/receiver shows the would-be freshness of that candidate route at
   * the aimed target — the vary-routes economics are visible at the moment of
   * route choice, before any release. States come from the sim's
   * routeFreshness getter; nothing is recomputed here.
   */
  private updateRouteFreshnessSignage(phaseSpec: PhaseSpec): void {
    const active = this.run.activeRoute;
    const committed = this.run.routeFreshness();
    const entries: Array<{ endpoint: "A" | "B"; name: string; state: "fresh" | "repeat" | "stale"; gradeDelta: number; candidate: boolean }> = [
      { endpoint: "B", name: active.receiver, state: committed.state, gradeDelta: committed.gradeDelta, candidate: false },
    ];
    const aimedA = this.aimPlaceable && this.aimObject ? this.arena.portalTarget(this.aimObject, "A") : null;
    const aimedB = this.aimPlaceable && this.aimObject ? this.arena.portalTarget(this.aimObject, "B") : null;
    if (aimedA && aimedA.name !== active.anchor && phaseSpec.activeAnchors.includes(aimedA.name as ArenaAnchor)) {
      const candidate = this.run.routeFreshness({ anchor: aimedA.name as ArenaAnchor, receiver: active.receiver });
      entries.push({ endpoint: "A", name: aimedA.name, state: candidate.state, gradeDelta: candidate.gradeDelta, candidate: true });
    } else if (aimedB && aimedB.name !== active.receiver && phaseSpec.activeReceivers.includes(aimedB.name as ReceiverTier)) {
      const candidate = this.run.routeFreshness({ anchor: active.anchor, receiver: aimedB.name as ReceiverTier });
      entries.push({ endpoint: "B", name: aimedB.name, state: candidate.state, gradeDelta: candidate.gradeDelta, candidate: true });
    }
    this.arena.setFreshnessChips(entries);
  }

  /** In-world 8s fuse telegraph: escalating pulse on the orb itself. */
  private updateFuseHalos(dt: number): void {
    let index = 0;
    for (const piece of this.trash.pieces) {
      if (!piece.live || piece.fuseTicks === null || index >= this.fuseHalos.length) continue;
      const halo = this.fuseHalos[index]!;
      const seconds = piece.fuseTicks / 60;
      const urgency = Math.min(1, Math.max(0, (8 - seconds) / 8));
      halo.visible = true;
      halo.position.copy(piece.mesh.position);
      const material = halo.material as THREE.MeshBasicMaterial;
      if (this.flashReduced()) {
        // reduction policy (same as the alarm vignette): NO oscillation — the
        // fairness cue survives as a steady swell that deepens toward zero
        material.opacity = 0.2 + 0.3 * urgency;
        const scale = 1 + 0.14 * urgency;
        halo.scale.set(scale, scale, scale);
      } else {
        // pulse rate ramps 1.6 → 3 Hz, capped at the 3 flashes/sec
        // photosensitivity limit; urgency escalates through amplitude and the
        // fuseTick audio subdivisions, never a faster strobe. Phase integrates
        // per halo so the changing rate can't alias into per-frame flicker.
        const rate = 1.6 + 1.4 * urgency;
        this.fuseHaloPhases[index] = (this.fuseHaloPhases[index]! + dt * rate) % 1;
        const pulse = 0.5 + 0.5 * Math.sin(this.fuseHaloPhases[index]! * Math.PI * 2);
        material.opacity = 0.12 + (0.3 + 0.2 * urgency) * pulse;
        const scale = 1 + pulse * (0.08 + 0.1 * urgency);
        halo.scale.set(scale, scale, scale);
      }
      index++;
    }
    for (; index < this.fuseHalos.length; index++) this.fuseHalos[index]!.visible = false;
  }

  /** Locked 5s warnings: lane shutters + conveyor reversals + expiring panels + baffle gate lamp. */
  private updateHazardTelegraphs(phaseSpec: PhaseSpec): void {
    const tick = this.run.tick;
    const nextIndex = Math.floor(Math.max(0, tick) / 1800) + 1;
    const next = nextIndex < 6 ? this.run.seed.phases[nextIndex] ?? null : null;
    const ticksToNext = nextIndex * 1800 - tick;
    const warning = next !== null && ticksToNext <= HAZARD_WARNING_TICKS && ticksToNext > 0;
    let warnLane: "left" | "right" | null = null;
    let conveyorWarn: "forward" | "reverse" | null = null;
    if (next && warning) {
      if (next.closedLane && next.closedLane !== phaseSpec.closedLane) warnLane = next.closedLane;
      if (next.conveyor !== phaseSpec.conveyor) conveyorWarn = next.conveyor;
    }
    if (warnLane && warnLane !== this.shutterWarnedLane) this.sound.shutterWarn();
    this.shutterWarnedLane = warnLane;
    this.arena.setShutterWarning(warnLane);
    // conveyor reversal warns in all three locked channels (18.4): the aprons
    // scroll toward the new direction (motion+light) and the belt sounds its
    // reversing alarm once per warning window
    if (conveyorWarn && conveyorWarn !== this.conveyorWarnedDirection) this.sound.conveyorWarn();
    this.conveyorWarnedDirection = conveyorWarn;
    this.arena.setConveyorWarning(conveyorWarn);

    // Expiring panels (18.4): anchors/receivers active now but rotating out at
    // the next boundary blink amber in-world (light+motion, flash-aware) and
    // sound the powering-down cue once. When the player's COMMITTED route is
    // the one expiring, the facility says so — the reroute is never a surprise.
    const expiring: string[] = [];
    if (next && warning) {
      for (const anchor of phaseSpec.activeAnchors) if (!next.activeAnchors.includes(anchor)) expiring.push(`A:${anchor}`);
      for (const lane of phaseSpec.activeReceivers) if (!next.activeReceivers.includes(lane)) expiring.push(`B:${lane}`);
    }
    this.arena.setExpiringPanels(expiring);
    if (expiring.length > 0 && this.panelWarnedBoundary !== nextIndex) {
      this.panelWarnedBoundary = nextIndex;
      this.sound.panelExpireWarn();
      const route = this.run.activeRoute;
      if (expiring.includes(`A:${route.anchor}`) || expiring.includes(`B:${route.receiver}`)) {
        this.hud?.toast(
          `PANEL ROTATION IN ${Math.ceil(ticksToNext / 60)}s · ACTIVE ROUTE WILL REVERT`,
          cssHex(FEEDBACK_COLORS.overflowWarning),
          2600,
        );
      }
    }

    // hazard-baffle gate cue, consistent with the HUD gate readout (3.2)
    const hazardActive = phaseSpec.activeReceivers.includes("hazard");
    const blocked = hazardActive ? this.run.baffleBlocked : null;
    if (blocked !== null && this.prevBaffleBlocked !== null && blocked !== this.prevBaffleBlocked) {
      this.sound.baffleTurn(blocked);
    }
    this.prevBaffleBlocked = blocked;
    this.arena.setBaffleGate(blocked);
  }

  /** Mini-batch chute telegraph via the read-only upcoming-spawn getter. */
  private updateBatchTelegraphs(): void {
    const upcoming = this.run.upcomingSpawns(BATCH_TELEGRAPH_TICKS);
    for (const chute of ["left", "center", "right"] as const satisfies readonly ChuteId[]) {
      const ticks = upcoming.filter((spawn) => spawn.chute === chute).map((spawn) => spawn.tick).sort((a, b) => a - b);
      let batchStart: number | null = null;
      for (let index = 0; index + 1 < ticks.length; index++) {
        if (ticks[index + 1]! - ticks[index]! <= BATCH_CLUSTER_GAP) {
          batchStart = ticks[index]!;
          break;
        }
      }
      this.arena.setChuteTelegraph(chute, batchStart !== null);
      if (batchStart !== null && !this.telegraphedBatches.has(batchStart)) {
        this.telegraphedBatches.add(batchStart);
        this.sound.batchTelegraph();
      }
    }
  }

  /**
   * Off-screen chute accumulation cue (PLAYTEST F3 part 2, LOCKED-DESIGN
   * 18.19). The S08 sweep found 0 of 3 chutes on screen from spawn, mid-arena
   * and the chamber approach, so the game's primary pressure source had no
   * feedback channel at all during the core loop.
   *
   * COMPOSE, DO NOT COLLIDE: `updateBatchTelegraphs` above is the IN-WORLD
   * channel — a flash on the chute mouth itself, which only reads when the
   * chute is on screen. This is the OFF-SCREEN channel. The pure model reports
   * an on-screen chute as `hidden` precisely so the in-world telegraph owns it
   * alone; the two are never both up for the same chute.
   *
   * Reads only data that already exists — no kernel change, no new piece field,
   * no spawn-budget touch (F3 part 1, the drop RATE, is deliberately untouched).
   */
  private updateChuteIndicators(): void {
    if (!this.hud) return;
    // Same read `updateBatchTelegraphs` does, over a wider horizon. Note this
    // returns [] while critical overflow has shuttered the chutes, so the cue
    // then reports the physical pile alone and never claims incoming trash.
    const upcoming = this.run.upcomingSpawns(BATCH_TELEGRAPH_TICKS * 2);
    const width = this.ctx.container.clientWidth || 0;
    const height = this.ctx.container.clientHeight || 0;
    const chutes = ["left", "center", "right"] as const satisfies readonly ChuteId[];
    const outlets = chutes.map((chute) => this.arenaGeometry.chuteOutlets[chute].position);
    const incoming = [0, 0, 0];
    for (const spawn of upcoming) {
      const index = chutes.indexOf(spawn.chute);
      if (index >= 0) incoming[index] += TRASH[spawn.kind].bulk;
    }
    // Purely spatial pile census: live, un-held pieces resting near an outlet.
    // No TrashPiece field is added for it — the kernel owns that shape.
    //
    // PIECE-OUTER, CHUTE-INNER on purpose: `body.translation()` crosses the
    // Rapier WASM boundary and allocates a fresh vector every call, and this
    // runs every frame of every run now that the cue is persistent chrome. One
    // crossing per piece, compared against three cached outlets, instead of one
    // per piece PER CHUTE. A piece inside two radii still counts for both, as
    // before.
    const pile = [0, 0, 0];
    const radiusSq = CHUTE_PILE_RADIUS_M * CHUTE_PILE_RADIUS_M;
    for (const piece of this.trash.pieces) {
      if (!piece.live || piece.held) continue;
      const p = piece.body.translation();
      for (let i = 0; i < outlets.length; i++) {
        const outlet = outlets[i]!;
        const dx = p.x - outlet[0];
        const dy = p.y - outlet[1];
        const dz = p.z - outlet[2];
        if (dx * dx + dy * dy + dz * dz <= radiusSq) pile[i] += piece.bulk;
      }
    }
    const signals: ChuteSignal[] = chutes.map((chute, i) => {
      const outlet = outlets[i]!;
      this.chuteProbe.set(outlet[0], outlet[1], outlet[2]);
      const screen = this.projectChuteToHud(this.chuteProbe);
      return { id: chute, screenX: screen.x, screenY: screen.y, pileBulk: pile[i]!, incomingBulk: incoming[i]! };
    });
    this.chuteIndicatorSpecs = chuteIndicatorModel(
      signals,
      { width, height },
      { simple: this.simpleMode, motionReduced: this.flashReduced() },
      this.chuteIndicatorSpecs,
      // Cross-channel anti-collision (18.19): the event flares — the persistent
      // adult jam-entrance chevron above all, re-issued every frame of a jam —
      // clamp into the SAME band as these chevrons and are `data-hud-element`s
      // too. They own their point; a chevron that cannot clear one gives way.
      this.hud.eventFlareAnchors(),
    );
    this.hud.setChuteIndicators(this.chuteIndicatorSpecs);
  }

  /**
   * Chute-outlet projection with a behind-camera correction. `projectToHud`
   * divides by a NEGATED w for points behind the camera, which MIRRORS them
   * about the frame centre — so the centre chute at your back would point the
   * arrow forward, straight at the chamber. Mirror it back and push it clear of
   * the frame so a chute behind the player always reads as behind the player.
   */
  private projectChuteToHud(world: THREE.Vector3): { x: number; y: number } {
    const camera = this.renderer.camera;
    camera.updateMatrixWorld();
    const point = this.projectToHud(world);
    const cameraSpaceZ = this.renderer.scratchPos.copy(world).applyMatrix4(camera.matrixWorldInverse).z;
    if (cameraSpaceZ <= 0) return point; // in front of the camera: honest already
    const cx = (this.ctx.container.clientWidth || 1) / 2;
    const cy = (this.ctx.container.clientHeight || 1) / 2;
    let dx = cx - point.x;
    let dy = cy - point.y;
    if (Math.abs(dx) < 1e-3 && Math.abs(dy) < 1e-3) dy = 1;
    const push = Math.max(1, (2 * Math.max(cx, cy)) / Math.max(Math.abs(dx), Math.abs(dy), 1e-3));
    return { x: cx + dx * push, y: cy + dy * push };
  }

  /** World-anchored micro-labels: per-lane jam bulk + overload chip + STALE route. */
  private updateWorldTags(): void {
    const hud = this.hud;
    if (!hud) return;
    // Feed-the-maw jam legibility (LOCKED-DESIGN 18.12 stage 3): each jammed
    // receiver's world tag names the remaining reject BULK (counts down live as
    // the player feeds the maw), the reticle-dock prompt names the new verb chain
    // (ride in → feed the pile → no points), and the maw feed-guard glows. The old
    // clear-time countdown is superseded by remaining-bulk + the overload chip,
    // both always visible while jammed.
    let anyJam = false;
    for (const lane of ["safe", "technical", "hazard"] as const) {
      const id = `jam-${lane}`;
      const bulk = this.run.jamBulk(lane);
      // The STOP-feeding escalation belongs to the lane it was earned on and
      // dies with that lane's pile, not with the last jam on the board.
      if (bulk <= 0) this.jamExtendingLanes.delete(lane);
      const target = bulk > 0 ? this.receiverTarget(lane) : null;
      if (target) {
        anyJam = true;
        const screen = this.projectToHud(this.tmpV.copy(target.pos).addScaledVector(target.normal, 0.5));
        hud.worldTag(
          id, screen.x, screen.y,
          // Simple keeps it in kid words (LOCKED-DESIGN 18.11).
          this.simpleMode ? "STUCK! GO FIX IT!" : `JAM · ${lane.toUpperCase()} · ${bulk} BULK`,
          cssHex(RECEIVER_TIER_COLORS[lane]),
        );
      } else {
        hud.clearWorldTag(id);
      }
    }
    // Glow the maw feed guard + echo the recovery prompt at the reticle dock.
    this.arena.setJamActive(anyJam);
    if (anyJam) {
      // The lane the player is actually pointed at: their own active receiver
      // when that is the jammed one, else the first jammed lane in the fixed
      // safe→technical→hazard order (deterministic, never frame-dependent).
      const jammed = (["safe", "technical", "hazard"] as const).filter((lane) => this.run.laneJammed(lane));
      const blocked = this.run.laneJammed(this.run.activeRoute.receiver)
        ? this.run.activeRoute.receiver
        : jammed[0]!;
      // Copy is a pure rule (JamRecoveryCopy): it composes the ninth-dogfood
      // STOP-feeding escalation with the Section 3.3 interlock consequence, and
      // deliberately does NOT offer "throw the other ring down" — the kernel
      // holds every placement while any lane is jammed, so that would be a lie.
      // Both signals are lane-accurate: the escalation only fires for a lane the
      // player actually over-fed, and the interlock line only promises release
      // when the LAST jam clears (multi-jam is reachable — two releases a tick
      // apart resolve into two lanes).
      const prompt = jamRecoveryPrompt({
        lane: blocked,
        simple: this.simpleMode,
        extending: this.jamExtendingLanes.has(blocked),
        othersJammed: jammed.filter((lane) => lane !== blocked).length,
        openReceiver: this.openReceiverWhileJammed(),
      });
      hud.setRecoveryPrompt(prompt.text, prompt.urgent, prompt.detail);
      this.arena.setInviteReceiver(blocked);
      this.updateJamEntranceNudge();
    } else {
      this.jamExtendingLanes.clear();
      hud.setRecoveryPrompt(null, false);
      // Everything the jam put on screen retracts in the SAME frame it clears.
      this.arena.setInviteReceiver(null);
      hud.clearWorldTag("jam-alt");
      hud.clearEdgeFlare("jam-alt");
    }
    this.updateSimpleJamCues(anyJam);
    // The STALE route tag is Grade-economics arithmetic ("−2"), which Simple
    // hides (LOCKED-DESIGN 18.11): a kid throwing the same floor→safe route all
    // shift would otherwise see it constantly. Kept for adult mode.
    const staleTarget = !this.simpleMode && this.run.routeStale ? this.receiverTarget(this.run.activeRoute.receiver) : null;
    if (staleTarget) {
      const screen = this.projectToHud(this.tmpV.copy(staleTarget.pos).addScaledVector(staleTarget.normal, 0.5));
      hud.worldTag("stale", screen.x, screen.y - 24, "ROUTE STALE · −2", cssHex(FEEDBACK_COLORS.stale));
    } else {
      hud.clearWorldTag("stale");
    }
  }

  /**
   * The lane that still pays while another one is jammed (LOCKED-DESIGN 18.16
   * amendment / PLAYTEST F10), or null when the jam really is a total scoring
   * stop.
   *
   * The Section 3.3 interlock freezes the ROUTE, but `Simulation.release()`
   * rejects a load only when ITS OWN lane is jammed
   * (`laneJammedAtRelease = this.laneJammed(route.receiver)`), so a route frozen
   * on a clear lane keeps scoring at full value. Mirrors release()'s gates
   * exactly and pessimistically — the same `routeValid` (BOTH endpoints active
   * in the live phase, since an invalid route resolves `delivered: 0`) and the
   * same per-lane jam test — because this is the input to a promise the HUD
   * makes to the player. The hazard baffle is the one gate deliberately NOT
   * folded in: it flips on a seeded cycle several times a jam, so gating on it
   * would flicker the prompt; the copy carries "ON THE OPEN GATE" instead and
   * the always-on baffle readout owns the live state.
   */
  private openReceiverWhileJammed(): ReceiverTier | null {
    const route = this.run.activeRoute;
    if (this.run.laneJammed(route.receiver)) return null;
    const phase = this.run.seed.phases[this.run.phaseIndex - 1];
    if (!phase?.activeAnchors.includes(route.anchor) || !phase.activeReceivers.includes(route.receiver)) return null;
    return route.receiver;
  }

  /**
   * Jam ENTRANCE nudge (LOCKED-DESIGN 18.16 / PLAYTEST F10). While a lane is
   * jammed, the only action the kernel actually permits is "ride the live blue
   * ring into the chamber and hand-feed the pile" — reconfiguring the portal is
   * interlocked (Simulation.processPlacements holds every queued placement while
   * any lane is jammed). So the world nudge points at the JAMMED receiver (the
   * invite highlight, set by updateWorldTags) and at the entrance ring, and
   * never at a fictional alternative lane.
   *
   * The tag persists for the whole jam; when the ring projects off-frame the
   * matching edge chevron is re-issued every frame, because `edgeFlare` restarts
   * its own 1.8 s transient timer on each call (and no-ops back to a clear when
   * the point re-enters the frame).
   */
  private updateJamEntranceNudge(): void {
    const hud = this.hud;
    if (!hud) return;
    // Simple's kid cue chain already owns this exact anchor ("GO IN HERE!"), so
    // a second tag on the same point would be a guaranteed HUD overlap; and the
    // tutorial never carries jam chrome (same rule as updateSimpleJamCues).
    const show = !this.simpleMode
      && !this.tutorial?.active
      && !inChamberBlock(this.currPlayerPos, this.arenaGeometry);
    if (!show) {
      hud.clearWorldTag("jam-alt");
      hud.clearEdgeFlare("jam-alt");
      return;
    }
    const ring = this.portals.A;
    const screen = this.projectToHud(this.tmpV.copy(ring.pos).addScaledVector(ring.normal, 0.5));
    hud.worldTag("jam-alt", screen.x, screen.y - 26, JAM_ENTRANCE_TAG, cssHex(PALETTE.anchorBlue));
    hud.edgeFlare("jam-alt", screen.x, screen.y, JAM_ENTRANCE_FLARE, cssHex(PALETTE.anchorBlue));
  }

  /**
   * Simple guided FEED THE FIRE cue chain (LOCKED-DESIGN 18.12 stage 3, kid copy):
   * a single re-promptable world cue that steps with the player's progress through
   * a jam — GO IN HERE (at the blue ring) → GRAB THE TRASH (at the reject pile) →
   * FEED THE FIRE (at the maw) → JUMP HOME (at the return pad). Derived live each
   * frame so it re-arms on every Simple jam (the pause card stays one-shot). Adult
   * play keeps the persistent reticle-dock prompt instead of this cue.
   */
  private updateSimpleJamCues(anyJam: boolean): void {
    const hud = this.hud;
    if (!hud || !this.simpleMode || this.tutorial?.active) {
      this.hud?.clearWorldTag("kidcue"); this.hud?.clearEdgeFlare("kidcue"); return;
    }
    const inChamber = inChamberBlock(this.currPlayerPos, this.arenaGeometry);
    if (!anyJam && !inChamber) { hud.clearWorldTag("kidcue"); hud.clearEdgeFlare("kidcue"); return; }
    const holding = this.beam.heldCount() > 0;
    let pos: THREE.Vector3 | null = null;
    let label = "";
    if (anyJam && !inChamber) {
      pos = this.portals.A.pos; label = "JUMP IN HERE!";
    } else if (inChamber && anyJam && !holding) {
      const lane = (["safe", "technical", "hazard"] as const).find((l) => this.run.jamBulk(l) > 0);
      // Point at the nearest ACTUAL reject body, not the fixed receiver anchor:
      // the pile settles in FRONT of the maw and scatters, so aiming the kid cue
      // at the receiver mouth pointed them at empty space (ninth dogfood: "didn't
      // see items left in the incinerator"). Fall back to the receiver anchor only
      // while no physical reject is present yet (a load still resolving in).
      const pile = lane ? this.nearestReject(lane) : null;
      const target = lane ? this.receiverTarget(lane) : null;
      if (pile) { pos = pile; label = "GRAB THE TRASH!"; }
      else if (target) { pos = target.pos; label = "GRAB THE TRASH!"; }
    } else if (inChamber && anyJam && holding) {
      pos = this.arena.objects.get("furnace-fire")?.position ?? null; label = "FEED THE FIRE!";
    } else if (inChamber && !anyJam) {
      const pad = this.returnPadCenter();
      if (pad) { pos = this.tmpV2.set(pad.x, pad.y + 0.6, pad.z); label = "JUMP HOME!"; }
    }
    if (pos && label) {
      const s = this.projectToHud(this.tmpV.copy(pos));
      hud.worldTag("kidcue", s.x, s.y - 30, label, cssHex(PALETTE.anchorBlue));
      // Off-frame anchor (PLAYTEST F14). `worldTag` CLAMPS into the HUD safe
      // area, so a pile the fixed chamber view does not frame renders as a cue
      // pinned to the edge with nothing underneath it. Measured at 1440x900 with
      // a reject resting at x 8.17 / z -14.89: the cue sat at y 880 while its
      // crate projected to y 1073 — 173 px below the frame. A kid reads that as
      // the game pointing at nothing and searches until the dwell cooks them
      // (tenth dogfood). Issue the same directional chevron the jam-entrance
      // nudge uses; `edgeFlare` no-ops straight back to a clear the moment the
      // anchor re-enters frame, so an on-screen pile costs nothing.
      hud.edgeFlare("kidcue", s.x, s.y, label, cssHex(PALETTE.anchorBlue));
    } else {
      hud.clearWorldTag("kidcue");
      hud.clearEdgeFlare("kidcue");
    }
  }

  /**
   * On-world tutorial cues (LOCKED-DESIGN 18.6). The glass beat points at the
   * sealed bulkhead; the choose beats present a REAL choice — the recommended
   * safe candidate plus one live alternative anchor/receiver — so "choose an
   * anchor" teaches the actual skill instead of circling a pre-placed default.
   */
  private updateTutorialTarget(): void {
    if (!this.tutorial?.active) return;
    const step = this.tutorial.currentStep();
    const phase = this.run.seed.phases[this.run.phaseIndex - 1];
    // Simple orientation (LOCKED-DESIGN 18.11): portals are pre-placed, so the
    // only cue is on the THROW beat — ring the live blue portal so the kid sees
    // exactly where to let go. Keyed data-cue-target="floor" for the QA scenario.
    if (this.simpleMode) {
      // The throw cue rides BOTH Simple throw beats once softened (18.15 / F9):
      // `clean` always shows it, and a softened `chase` gets it back so a kid who
      // has lost the moving ring can see exactly where to let go. Softened means
      // a bigger ring; the pulse is refused outright under flash reduction.
      const softened = this.tutorial.softened;
      if (step === "clean" || (step === "chase" && softened)) {
        const p = this.projectToHud(this.tmpV.copy(this.portals.A.pos));
        this.tutorial.showCues([{
          x: p.x,
          y: p.y,
          label: "THROW HERE",
          color: cssHex(PALETTE.anchorBlue),
          target: "floor",
          primary: true,
          ...(softened ? { scale: 1.6, pulse: !this.flashReduced() } : {}),
        }]);
      } else {
        this.tutorial.clearCues();
      }
      return;
    }
    if (step === "glass") {
      const p = this.projectToHud(this.tmpV.set(0, 1.6, GLASS_FRONT_Z));
      this.tutorial.showCues([{ x: p.x, y: p.y, label: "REINFORCED GLASS", color: cssHex(FEEDBACK_COLORS.bulkhead), target: "glass", primary: true }]);
    } else if (step === "blue") {
      const cues: CueSpec[] = [];
      const floor = this.authoredTarget("A", "floor");
      if (floor) cues.push(this.tutorialCue(floor, "BLUE · FLOOR", PALETTE.anchorBlue, "floor", true));
      const alt = phase?.activeAnchors.find((anchor) => anchor !== "floor");
      const altTarget = alt ? this.authoredTarget("A", alt) : undefined;
      if (alt && altTarget) cues.push(this.tutorialCue(altTarget, `BLUE · ${alt.toUpperCase()}`, PALETTE.anchorBlue, alt, false));
      this.tutorial.showCues(cues);
    } else if (step === "orange") {
      const cues: CueSpec[] = [];
      const safe = this.authoredTarget("B", "safe");
      if (safe) cues.push(this.tutorialCue(safe, "ORANGE · SAFE", PALETTE.receiverOrange, "safe", true));
      const alt = phase?.activeReceivers.find((receiver) => receiver !== "safe");
      const altTarget = alt ? this.authoredTarget("B", alt) : undefined;
      if (alt && altTarget) cues.push(this.tutorialCue(altTarget, `ORANGE · ${alt.toUpperCase()}`, RECEIVER_TIER_COLORS[alt], alt, false));
      this.tutorial.showCues(cues);
    } else {
      this.tutorial.clearCues();
    }
  }

  private tutorialCue(target: AuthoredTarget, label: string, color: number, name: string, primary: boolean): CueSpec {
    const point = this.projectToHud(this.tmpV.copy(target.pos));
    return { x: point.x, y: point.y, label, color: cssHex(color), target: name, primary };
  }

  /** Throw assist magnetizes whichever portal the cursor is nearer to.
   *  Gamepad aim gets a wider capture + near-snap (a stick can't name a
   *  point behind an elevated ring the way a cursor can). */
  private assistTarget(): AimAssistTarget {
    // No magnetization while either portal is concealed (the glass beat and the
    // choose beats) — a radius-0 target lets a throw fly straight at the glass.
    // Route-gated: NO normal either, so the F5 release clamp is inert while the
    // ring is inactive and can never curve a throw into a dead portal.
    if (!this.portals.bothLive) return { center: this.portals.A.pos, radius: 0 };
    const dA = this.aimPoint.distanceToSquared(this.portals.A.pos);
    const dB = this.aimPoint.distanceToSquared(this.portals.B.pos);
    const end = dA <= dB ? this.portals.A : this.portals.B;
    // Orientation relief (LOCKED-DESIGN 18.15 / F9): while a Simple throw beat is
    // SOFTENED the assist radius widens 1.6x so a kid who has missed for 12 s gets
    // a genuinely easier target. Scoped to the softened tutorial beat only — it
    // ends the moment the beat advances, so shift play is completely untouched.
    const relief = this.tutorial?.softened === true && this.simpleMode ? 1.6 : 1;
    // `normal` carries the aimed disc's plane so GrabBeam can solve a
    // point-blank release from IN FRONT of it (PLAYTEST F5).
    if (this.aimMode === "pad") {
      return { center: end.pos, radius: end.radius * 1.8 * relief, maxBlend: 0.95, normal: end.normal };
    }
    return { center: end.pos, radius: end.radius * relief, normal: end.normal };
  }

  private updateAim(input: InputFrame): void {
    const container = this.ctx.container;
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;

    // last input device wins: stick deflection → pad aim; mouse motion → mouse aim
    if (input.mouseX !== this.lastMouseX || input.mouseY !== this.lastMouseY) {
      if (this.lastMouseX >= 0) this.aimMode = "mouse";
      this.lastMouseX = input.mouseX;
      this.lastMouseY = input.mouseY;
    }
    const stickMag = Math.hypot(input.aimStickX, input.aimStickY);
    if (stickMag > 0.01) {
      this.aimMode = "pad";
      // screen-relative stick → world xz (same basis as movement: the rig's
      // input yaw, which is the blend destination's yaw during transitions)
      const sx = input.aimStickX;
      const sz = -input.aimStickY; // stick up = up-screen
      const yaw = this.renderer.rig.inputYaw;
      const cos = Math.cos(yaw);
      const sin = Math.sin(yaw);
      this.padAimDir.set(sx * cos - sz * sin, -sz * cos - sx * sin).normalize();
      this.padAimDist = 3.5 + Math.min(1, stickMag) * 9.5;
    }

    if (this.aimMode === "pad" && input.gamepadConnected) {
      // virtual cursor: project the world-space aim spot into NDC, then
      // reuse the exact mouse ray path (so portals/walls still catch it)
      this.tmpV.set(
        this.currPlayerPos.x + this.padAimDir.x * this.padAimDist,
        0.05,
        this.currPlayerPos.z + this.padAimDir.y * this.padAimDist,
      );
      this.tmpV.project(this.renderer.camera);
      this.ndc.set(
        Math.max(-0.98, Math.min(0.98, this.tmpV.x)),
        Math.max(-0.98, Math.min(0.98, this.tmpV.y)),
      );
    } else {
      this.ndc.set((input.mouseX / w) * 2 - 1, -((input.mouseY / h) * 2 - 1));
    }
    this.raycaster.setFromCamera(this.ndc, this.renderer.camera);
    const hits = this.raycaster.intersectObjects(
      // Concealed portals are excluded so the surface behind stays placeable
      // during the glass/choose beats (LOCKED-DESIGN 18.6).
      [...this.arena.aimTargets, ...this.portals.raycastTargets()],
      false,
    );
    const hit = hits[0];
    if (hit) {
      this.aimObject = hit.object;
      this.aimPoint.copy(hit.point);
      // placement wants the WORLD-space face normal of a static surface
      this.aimPlaceable = !this.portals.aimTargets.includes(hit.object);
      if (hit.face) {
        this.aimNormal.copy(hit.face.normal).applyQuaternion(hit.object.quaternion).normalize();
      } else {
        this.aimNormal.set(0, 1, 0);
      }
    } else {
      this.aimObject = null;
      // PLAYTEST F18: the miss fallback is BACKSTOPPED against the room, not
      // fired 50 m into the void. `aimPlaceable` stays false and `aimObject`
      // stays null exactly as before — the portal-placement contract only ever
      // reads a real `aimTargets` hit, so nothing downstream of placement moves.
      this.aimPoint.copy(this.raycaster.ray.at(this.aimFallbackDistance(), this.aimPoint));
      this.aimPlaceable = false;
    }
  }

  /**
   * Distance along the aim ray at which a MISSED aim lands (PLAYTEST F18,
   * twelfth dogfood: "i noticed another item fly through the glass" — still,
   * after F16 shipped CCD).
   *
   * `arena.aimTargets` is only the 6 anchor discs and 3 receiver apertures, plus
   * the 2 portal discs from `portals.raycastTargets()`: 11 small meshes in a
   * 56 x 40 room. No floor, no wall, no glass. Measured over a real shift, 93.8%
   * of aim frames miss all 11 and fell through to `ray.at(50)` — a point up to
   * 52 m out and, with the cursor low on screen, ~31 m BELOW the floor. The
   * throw solve then takes that literally: `GrabBeam.ballisticVelocity` floors
   * the flight time at ARC_T_MIN while the vertical term carries the whole
   * phantom drop, so the game's own release path reached 71.5 m/s = 1.19 m of
   * travel per fixed step (measured through `beam.step`, cursor row 875, player
   * at (0, 0.95, -6)). `Trash`'s soft-CCD prediction is 1.0 m and Rapier's
   * `maxCcdSubsteps` is 1, so past ~60 m/s the bulkhead seal is running on hard
   * CCD alone — the exact configuration F16 measured leaking at 2/54,430. The
   * unbounded aim point is the UPSTREAM cause of the bug F16 patched downstream.
   *
   * Fix: slab-clip the ray against the room's interior AABB — `floorExtents` IS
   * that box ([-28, 0, -20]..[28, 19, 20]: the floor plan plus
   * ENCLOSURE_INTERIOR_HEIGHT, the same derivation the containment shell and the
   * camera clamp use, so re-authoring the floor moves this for free) — and stop
   * at the face where the ray leaves it. Capped at the old 50 m so this can only
   * ever SHORTEN the fallback, never lengthen it, and falls back to 50 m
   * unchanged if the ray somehow never meets the box.
   *
   * This also kills the launch-speed cliff the fallback created: one cursor row
   * either side of a receiver disc's edge used to jump the solve from 4.90 m/s
   * to 16.03 m/s, because the aim point teleported from the disc at
   * (0, 0.39, -5.90) to (0, -11.66, -22.98) — 11.7 m underground.
   */
  private aimFallbackDistance(): number {
    const { min, max } = this.arenaGeometry.floorExtents;
    const o = this.raycaster.ray.origin;
    const d = this.raycaster.ray.direction;
    let tExit = AIM_FALLBACK_DISTANCE;
    let tEnter = -AIM_FALLBACK_DISTANCE;
    for (let axis = 0; axis < 3; axis++) {
      const oa = axis === 0 ? o.x : axis === 1 ? o.y : o.z;
      const da = axis === 0 ? d.x : axis === 1 ? d.y : d.z;
      if (Math.abs(da) < 1e-6) {
        // Ray runs parallel to this slab: it clips nothing, but if the origin is
        // already outside the slab it can never be inside the box at all.
        if (oa < min[axis]! || oa > max[axis]!) return AIM_FALLBACK_DISTANCE;
        continue;
      }
      const t0 = (min[axis]! - oa) / da;
      const t1 = (max[axis]! - oa) / da;
      tEnter = Math.max(tEnter, Math.min(t0, t1));
      tExit = Math.min(tExit, Math.max(t0, t1));
    }
    // No overlap (camera outside the box looking away) → old behaviour verbatim.
    if (tExit <= 0 || tEnter > tExit) return AIM_FALLBACK_DISTANCE;
    // A degenerate zero-length aim would leave GrabBeam's hold direction and the
    // player's facing yaw undefined, so keep a floor well inside HOLD_DIST.
    return Math.min(AIM_FALLBACK_DISTANCE, Math.max(AIM_FALLBACK_MIN_DISTANCE, tExit));
  }

  private physicsStep(input: InputFrame): void {
    const actions: SimulationAction[] = this.pendingSimulationActions.splice(0);
    // The F-key / anywhere-F manual-recovery action is retired in m1.3.0
    // (LOCKED-DESIGN 18.12): jams now clear only by hand-feeding rejects to the
    // maw inside the chamber. The Engine sim-facing feed bridge (intake → feed
    // actions, return belt, leak guard, side-stamped player traversal) and the
    // lever/handle presentation removal land in the presentation stage.
    const purge = this.arenaGeometry.purgeZone;
    const atPurge = this.currPlayerPos.x > purge.min[0] && this.currPlayerPos.x < purge.max[0] && this.currPlayerPos.z > purge.min[2] && this.currPlayerPos.z < purge.max[2];
    if (input.purgePressed && atPurge && this.run.purgeLockedUntil <= this.run.tick && this.beam.heldBulk() === 6) {
      const purged = this.beam.purgeHeld();
      if (purged.length > 0) {
        actions.push({ type: "emergency-purge", pieces: purged.map((piece) => piece.kind) });
        for (const piece of purged) this.trash.consume(piece);
      }
    } else if (input.purgePressed && atPurge && this.run.phase === "playing" && !this.tutorial?.active) {
      // refused at the chute — deadpan facility signage, never silent. Simple
      // has no red orbs and never teaches the C chute (LOCKED-DESIGN 18.11), so
      // a kid mashing C here gets the soft deny beat without the adult jargon.
      this.presentDeny();
      const locked = this.run.purgeLockedUntil > this.run.tick;
      if (!this.simpleMode) {
        this.hud?.toast(
          locked
            ? `EMERGENCY CHUTE LOCKED · READY IN ${Math.ceil((this.run.purgeLockedUntil - this.run.tick) / 60)}s`
            : "EMERGENCY CHUTE ACCEPTS COMPLETE SIX-UNIT LOADS ONLY",
          cssHex(FEEDBACK_COLORS.deny),
          2000,
        );
      }
    }
    // store interpolation history BEFORE advancing
    this.prevPlayerPos.copy(this.currPlayerPos);
    this.trash.storePrev();

    this.controller.step(FIXED_DT, input);
    const playerState = this.controller.state();
    this.currPlayerPos.set(playerState.x, playerState.y, playerState.z);
    if (this.beamJammedTicks > 0) this.beamJammedTicks--;
    const beamInput = this.beamJammedTicks > 0
      ? { ...input, grabHeld: false, grabPressed: false, grabReleased: false }
      : input;
    // The beam is occluded by the bulkhead (PLAYTEST F21): it may only acquire
    // pieces on the player's own side of the glass. The player's side is the
    // FOOTPRINT test (they walk the floor plan, so `inChamberBlock` — the same
    // predicate `chamberRelease` below uses); the piece's side is the VOLUME
    // fact `p.chamber`, which F19 recomputes positionally every step.
    const beamResult = this.beam.step(
      FIXED_DT,
      beamInput,
      this.currPlayerPos,
      this.aimPoint,
      this.trash.pieces,
      this.assistTarget(),
      inChamberBlock(this.currPlayerPos, this.arenaGeometry),
    );
    // IN-CHAMBER RELEASES ARE NOT LOADS (LOCKED-DESIGN 18.12 §3.4): a beam release
    // from inside the chamber pushes NO `release` sim action and skips the load
    // prediction mapping — the thrown pieces are just bodies the maw intake / belt
    // then report as zero-point feeds. This closes the latent hole where a chamber
    // release could mint a scored load. Only the physical position decides —
    // bounded to the 24-wide block footprint (18.13) so a throw from the open
    // north-shoulder floor stays a normal, scoreable release. Identical FX.
    const chamberRelease = inChamberBlock(this.currPlayerPos, this.arenaGeometry);
    if (beamResult.threw.length > 0) {
      // Enforced glass beat (LOCKED-DESIGN 18.6): a throw during the glass step
      // only counts once a piece actually strikes the bulkhead — so watch the
      // thrown pieces rather than advancing on release in any direction.
      if (this.tutorial?.currentStep() === "glass") {
        for (const piece of beamResult.threw) this.glassWatch.add(piece.id);
      }
      if (!chamberRelease) actions.push({ type: "release", pieces: beamResult.threw.map((piece): SimTrashKind => piece.kind) });
      this.sound.throwSfx(beamResult.threw.length);
      for (const piece of beamResult.threw) {
        this.trails.follow(piece, TRASH_THEME[piece.kind].accent);
      }
    }
    if (beamResult.captured.length > 0) {
      for (const piece of beamResult.captured) {
        if (piece.kind === "red" && piece.fuseTicks === null) {
          piece.fuseTicks = 8 * 60;
          // First-encounter teaching (LOCKED-DESIGN 12.2/18.10): the first armed
          // red orb in real play gets a brief, non-blocking coach beat.
          if (!this.taughtRedFuse && !this.tutorial?.active && this.run.phase === "playing") {
            this.taughtRedFuse = true;
            this.teachFirstEncounter("red");
          }
        }
      }
      // pitch climbs as the cluster grows
      this.sound.grab(this.beam.heldCount());
      this.tutorial?.reportHeldCount(this.beam.heldCount());
    }
    // Beam-full one-shot (LOCKED-DESIGN 18.11 v2, retained under 18.15): with the
    // standard 6-bulk beam restored (F1) this fires far LESS often than it did at
    // 3 bulk, but it is still correct — the first time the beam does fill, a kid
    // gets one plain "throw it" nudge. Same for the capacity-blocked nudge below.
    const toppedOut = this.beam.consumeToppedOut();
    // Capacity-blocked (LOCKED-DESIGN 4.1 / 18.11 v2): the kid is holding a load
    // and a too-big piece can't join — same "throw it first" lesson as topping
    // out, so a can they can't scoop never reads as a broken beam. Consume the
    // edge unconditionally (it is only ever set in Simple) so it never lingers.
    const capacityBlocked = this.beam.consumeCapacityBlocked();
    if ((toppedOut || capacityBlocked) && this.simpleMode && !this.taughtBeamFull && this.run.phase === "playing" && !this.tutorial?.active) {
      this.taughtBeamFull = true;
      this.hud?.toast("FULL UP! THROW IT!", cssHex(RECEIVER_TIER_COLORS.safe), 2200);
    }
    // The kinematic baffle collider tracks the simulation's deterministic yaw
    // so blocked-window loads are physically deflected at the throat.
    this.baffleQuat.setFromEuler(this.baffleEuler.set(0, this.run.baffleYawAt(this.run.tick), 0));
    this.arenaPhysics.baffle.setNextKinematicRotation(this.baffleQuat);
    this.world.step();
    const conveyorDirection = this.run.seed.phases[this.run.phaseIndex - 1]?.conveyor === "reverse" ? 1 : -1;
    const closedLane = this.run.seed.phases[this.run.phaseIndex - 1]?.closedLane;
    for (const piece of this.trash.pieces) {
      const t = piece.body.translation();
      if (!piece.held && t.z > 13.2 && t.y < 1.4) piece.body.applyImpulse({ x: 0, y: 0, z: conveyorDirection * .055 }, true);
      if (!piece.held && closedLane === "left" && t.x < -7.4) piece.body.applyImpulse({ x: .18, y: .02, z: 0 }, true);
      if (!piece.held && closedLane === "right" && t.x > 7.4) piece.body.applyImpulse({ x: -.18, y: .02, z: 0 }, true);
    }
    if ((closedLane === "left" && playerState.x < -7.6) || (closedLane === "right" && playerState.x > 7.6)) {
      const push = closedLane === "left" ? 7 : -7;
      this.controller.teleport(this.currPlayerPos, new THREE.Vector3(push, playerState.vy, playerState.vz));
    }
    for (const piece of [...this.trash.pieces]) {
      if (piece.fuseTicks === null) continue;
      piece.fuseTicks--;
      // fuse metronome (priority cue per 2.2): one tick per remaining second
      if (piece.fuseTicks > 0 && piece.fuseTicks % 60 === 0) this.sound.fuseTick(piece.fuseTicks / 60);
      if (piece.fuseTicks > 0) continue;
      const origin = piece.body.translation();
      const location = inChamberBlock(origin, this.arenaGeometry) ? "chamber" as const : "arena" as const;
      this.beam.forceDrop();
      this.beamJammedTicks = 45;
      this.trash.detonate(piece);
      actions.push({ type: "red-detonate", location });
      for (const other of this.trash.pieces) {
        if (other === piece || !other.live) continue;
        const t = other.body.translation(); const dx = t.x - origin.x; const dy = t.y - origin.y; const dz = t.z - origin.z;
        const distance = Math.hypot(dx, dy, dz);
        if (distance <= 0 || distance >= 5 || blastOccluded(origin, t, this.arenaGeometry)) continue;
        const impulse = (1 - distance / 5) * 22;
        other.body.applyImpulse({ x: dx / distance * impulse, y: Math.max(.25, dy / distance) * impulse, z: dz / distance * impulse }, true);
      }
      this.presentBlast(origin.x, origin.y, origin.z, location, playerState.x, playerState.z);
      const pdx = playerState.x - origin.x; const pdy = playerState.y - origin.y; const pdz = playerState.z - origin.z;
      const playerDistance = Math.hypot(pdx, pdy, pdz);
      if (playerDistance > 0 && playerDistance < 5 && !blastOccluded(origin, playerState, this.arenaGeometry)) {
        const impulse = (1 - playerDistance / 5) * 18;
        this.controller.teleport(this.currPlayerPos, new THREE.Vector3(pdx / playerDistance * impulse, Math.max(.3, pdy / playerDistance) * impulse, pdz / playerDistance * impulse));
      }
    }
    this.currPlayerPos.set(
      this.controller.state().x,
      this.controller.state().y,
      this.controller.state().z,
    );

    // portal traversals (after step, uses prev/curr positions)
    this.portals.update(FIXED_DT);
    const traversed = this.portals.step(this.trash.pieces);
    for (const p of traversed) {
      actions.push({ type: "portal-traversal", subject: "trash", id: p.id });
      const translation = p.body.translation();
      // PLAYTEST F19: the AUTHORITATIVE chamber predicate, not z alone. Since the
      // 18.13 depth re-author the open arena floor wraps both shoulders north of
      // the threshold, so `z < chamberThresholdZ` on its own also calls |x| > 12
      // walkable floor "the chamber" (see CHAMBER_HALF_WIDTH_X above), and the
      // block is a sealed box in a 19 m room, so the airspace and roof above it
      // are not the chamber either (see `inChamberVolume`). Every writer of this
      // flag now uses the same predicate. No portal target sits above the lid
      // (the highest chamber-side aperture is receiver-hazard at y 2.75), so the
      // volume bound cannot cost a real traversal its arrival credit below.
      p.chamber = inChamberVolume(translation, this.arenaGeometry);
      // Physical arrival (LOCKED-DESIGN 18.14 / F8-C): arena -> chamber only. A
      // chamber -> arena knock-back has `p.chamber === false` and never queues.
      if (p.chamber) this.arrivalQueue.push({ pieceId: p.id, queuedTick: this.run.tick });
      // Late-arriving reject (LOCKED-DESIGN 18.12): loads resolve a fixed 3s after
      // release but PHYSICAL traversal is unbounded — a lobbed or blast-shoved
      // reject can cross into the chamber after its receiver-jammed event already
      // stamped jamLane onto the pieces that were physically present. Stamp it on
      // arrival so this piece becomes a hand-feedable jam body (the overflow census
      // also names CHAMBER, not floor). The returned-feed reconciliation below
      // settles the debt if it ever leaves the chamber again.
      if (p.rejected && p.jamLane === null && p.loadId !== null && p.chamber) {
        const load = this.findLoad(p.loadId);
        if (load && this.run.laneJammed(load.route.receiver)) {
          p.jamLane = load.route.receiver;
          p.rejectReady = true;
        }
      }
      p.prevPos.copy(p.mesh.position); // extra guard vs. render smear
    }
    if (traversed.length > 0) {
      // ONE aggregated ring pair per tick, not one per piece (LOCKED-DESIGN
      // 18.10 4B capture: per-piece additive rings stacked across a volley's
      // crossing ticks read as a lingering white flash). Shorter + softer, and
      // flash reduction caps the peak further (12.3/18.5 policy).
      const reduced = this.flashReduced();
      const ringLife = reduced ? 0.16 : 0.26;
      const ringPeak = reduced ? 0.4 : 0.7;
      this.fx.spawn(this.portals.A.pos, this.portals.A.normal, PALETTE.anchorBlue, 1.4, ringLife, ringPeak);
      this.fx.spawn(this.portals.B.pos, this.portals.B.normal, PALETTE.receiverOrange, 1.4, ringLife, ringPeak);
      this.sound.portalZap();
      this.particles.burst(this.portals.A.pos, PALETTE.anchorBlue, 9, 3.5, { spread: 0.8, gravity: -2, life: 0.45 });
      this.particles.burst(this.portals.B.pos, PALETTE.receiverOrange, 9, 3.5, { spread: 0.8, gravity: -2, life: 0.45 });
    }

    // CHAMBER OCCUPANCY IS A FACT, NOT A MEMORY (PLAYTEST F19). Before this pass
    // `p.chamber` was written in exactly three places — the traversal loop above
    // and the two return-ride exits — so it recorded portal HISTORY, never
    // position. A body that reached the chamber by any other route therefore kept
    // `chamber === false` while physically sitting behind the sealed bulkhead.
    // Measured live on the twelfth-dogfood build: a reject parked at z −15.5 held
    // `chamber === false` for 98 consecutive ticks. Every consumer that asks "is
    // it in the chamber?" reads this flag rather than the body — the
    // receiver-jammed stamping (`piece.rejected && piece.chamber`), the Section 7
    // chamber bulk census, `flushArrivals`' knock-back guard — so such a pile was
    // invisible to all three: it could never be stamped, never hand-fed, never
    // counted, and the F13 backing guarantee then wrote its lane's debt off at
    // +90 ticks with the trash still lying in the furnace. The reachable route
    // today is tunnelling the 0.32 m glass (PLAYTEST F16/F18, still reported by
    // the owner); the flag being a fact makes the class unreachable regardless of
    // which route a future build opens.
    //
    // This CANNOT create a scoring path: `arrivalQueue` is pushed only by the
    // traversal loop above, so a piece that arrives without crossing still earns
    // no `arrive` credit — it merely becomes stampable, feedable jam.
    //
    // Placed after the traversal loop and BEFORE `flushArrivals` / the maw intake
    // / the reconciliation / the F13 backing pass, so every consumer in this tick
    // reads the post-physics truth. Held pieces and belt riders are skipped: their
    // positions are scripted by the beam and `updateReturnRides`, which own the
    // flag across those journeys (the ride's hatch eject clears it explicitly).
    //
    // The predicate is `inChamberVolume`, NOT the footprint: making the flag
    // authoritative for every body is exactly what makes the 14 m of open air
    // and the roof slab above the sealed box matter, and neither is a place the
    // maw or the player can reach (see `inChamberVolume`).
    for (const p of this.trash.pieces) {
      if (!p.live || p.held) continue;
      if (this.returnRides.some((ride) => ride.piece === p)) continue;
      p.chamber = inChamberVolume(p.body.translation(), this.arenaGeometry);
    }

    // Physical-arrival credit (LOCKED-DESIGN 18.14 / F8-C) — drained HERE, on the
    // heels of the traversal loop that queued it and BEFORE the maw intake below.
    // PLAYTEST F17: it used to run at the end of action assembly (after the maw),
    // which silently inverted the whole mechanic. The safe receiver's trash exit
    // point is `to.pos + normal * 0.7` = (0, 0.95, -14.45) (PortalPair.ts:510),
    // and that point is INSIDE `arenaGeometry.intake` ([-5.8,5.8] x [0.05,6] x
    // [-19.5,-13.45]) — every normally-delivered piece is in the furnace maw the
    // instant it crosses, so the intake loop consumed it on its own crossing tick
    // and `flushArrivals` then dropped the entry on `!piece.live`. The ONLY
    // survivors were `rejected` pieces (the intake loop skips them), and
    // `Simulation.arriveLoad` refuses exactly those (jammed/baffled loads). The
    // two guards were complementary, so the upgrade path was unreachable BY
    // CONSTRUCTION: two family bundles read loadsUpgraded 0 / upgradesApplied 0
    // with 107 in-window crossings and only 10 `arrive` actions, all rejects.
    //
    // The 18.14 constraint is unchanged and still satisfied verbatim — "after the
    // traversal loop that queued the crossings, inside the same tick's action
    // assembly" never said "after the maw". `actions` is not read until `run.step`
    // below, and `arrive` touches only pendingLoads while `feed` touches only
    // lane/overflow state, so moving it ahead of the maw/return/backing feeds
    // cannot reorder any effect. Gate matches the old call site: playing phase,
    // tutorial excluded (the warm-up drip scores nothing).
    if (this.run.phase === "playing" && !this.tutorial?.active) this.flushArrivals(actions);

    // reject-return journey (Section 6/18.10): walk any riding rejects along
    // the visible belt path AFTER the world step so the scripting wins the tick
    this.updateReturnRides();

    // player traversal — momentum carries YOU too. A deterministic re-arm
    // cooldown (LOCKED-DESIGN 3.4/18.10) gates re-entry ON TOP of the spatial
    // clearance test, so stepping straight back out of a portal can no longer
    // instantly re-loop. PORTAL_REARM_TICKS is the shared authoritative
    // duration the sim also enforces on the recorded trace.
    if (this.playerRearmTicks > 0) this.playerRearmTicks--;
    if (!this.portals.bothLive) {
      // Portals concealed (glass/choose beats): no player traversal. No-op in
      // real runs, where both ends are live from the placement countdown on.
    } else if (!this.playerPortalArmed) {
      // re-arm only once the cooldown elapses AND the player is fully clear of
      // both portal volumes (kills the instant re-loop and any ping-pong)
      if (this.playerRearmTicks <= 0 && this.portals.isClearOfPortals(this.currPlayerPos, 1.0)) this.playerPortalArmed = true;
    } else {
      const st = this.controller.state();
      this.playerTrv.prevPos.copy(this.prevPlayerPos);
      this.playerTrv.currPos.copy(this.currPlayerPos);
      this.playerTrv.vel.set(st.vx, st.vy, st.vz);
      // radius 1.0 (> capsule half-extent 0.9) so floor portals engage
      const r = this.portals.computeTraversal(this.playerTrv, PLAYER_EXIT_CLEARANCE, 1.0);
      // THE INTENT GATE (PLAYTEST F7 / open question 8, owner-approved with the
      // CHASE IT! slice — LOCKED-DESIGN 18.25). Geometry alone did not close F7:
      // 18.17 moved the entrance out of the first walk line, which killed the
      // accidental DRIFT, but in Simple the blue ring is also the throw target,
      // so a child stands beside it for the whole shift and any stance within
      // `bodyRadius + 0.55` = 1.55 m of the disc is already inside their own
      // capture volume. One sidestep mid-throw and they are in the chamber on a
      // dwell clock they cannot read. Requiring the player to be AIRBORNE or
      // DASHING makes entry an act — you jump into the ring — using controls
      // that already exist, with no new key and nothing new to read.
      //
      // ONE-WAY, and this is load-bearing. This branch is direction-agnostic: it
      // is the arena->chamber path AND the chamber->arena path. Gating both would
      // strand a player who rode in during the prologue, because the only other
      // exit — `updateReturnPad` — hard-returns while the tutorial is active. So
      // the gate is evaluated on the DESTINATION and applies only when entering
      // the chamber; walking back out through the ring is untouched.
      //
      // Refusing costs nothing downstream: `portal-traversal` is simply never
      // pushed into the frame, so it is never in the recorded trace either. The
      // Engine is not instantiated during fixture replay at all, so no frozen
      // hash can move.
      const entersChamber = r !== null && r.outPos.z < this.arenaGeometry.chamberThresholdZ;
      const intentional = !st.grounded || st.dashing;
      if (r && entersChamber && !intentional) {
        // Held at the threshold. No re-arm burn and no side-stamp — the player
        // never left the arena, so the next honest jump-in still works.
        this.notePortalRefusedForIntent();
      } else if (r) {
        this.controller.teleport(r.outPos, r.outVel);
        this.prevPlayerPos.copy(r.outPos);
        this.currPlayerPos.copy(r.outPos);
        this.renderer.snapFollow(r.outPos.x, r.outPos.z);
        // player crossing: the exit ring sits right at the camera, so it gets
        // the shortened/softened treatment too (18.10 4B white-flash capture)
        {
          const reduced = this.flashReduced();
          const ringLife = reduced ? 0.16 : 0.24;
          const ringPeak = reduced ? 0.4 : 0.7;
          this.fx.spawn(r.from.pos, r.from.normal, PALETTE.anchorBlue, 1.6, ringLife, ringPeak);
          this.fx.spawn(r.to.pos, r.to.normal, PALETTE.receiverOrange, 1.6, ringLife, ringPeak);
        }
        this.playerPortalArmed = false;
        this.playerRearmTicks = PORTAL_REARM_TICKS;
        // Side-stamp from the post-teleport z against the SAME chamberThresholdZ
        // predicate the camera uses (LOCKED-DESIGN 18.12): the sim reads this to
        // drive chamber occupancy + the dwell clock. Absolute, not a toggle — a
        // missed crossing self-heals on the next one. INVARIANT: the Engine's
        // physical gate (playerPortalArmed + rearm + clearance) is strictly
        // tighter than the sim's re-arm rule, so this crossing is always honoured.
        actions.push({ type: "portal-traversal", subject: "player", side: r.outPos.z < this.arenaGeometry.chamberThresholdZ ? "chamber" : "arena" });
        this.tutorial?.reportTraversed();
        this.sound.portalZap();
        this.renderer.fovPunch(9);
        this.renderer.shake(0.15);
        this.particles.burst(r.to.pos, 0x9fd8ff, 14, 4.5, { spread: 0.9, gravity: -3, life: 0.5 });
      }
    }

    // RETURN PAD (LOCKED-DESIGN 18.12 §3.4, finally functional): standing on the
    // chamber pad for a 0.5s spin-up launches the player home THROUGH the active
    // receiver portal. Scripted direct traversal (teleport to the blue anchor's
    // exit pose, then a pushed portal-traversal side:"arena"), NOT ballistic — so a
    // wall/ceiling receiver frame can never strand you and the 30-tick spin-up
    // outlasts the 24-tick portal re-arm, so re-arm never blocks the launch.
    this.updateReturnPad(actions);

    // incineration: intake maw consumes trash inside the maw volume.
    // Reward ladder (18.4): the per-piece tick stays SMALL — the celebration
    // belongs to load-resolved. Per-piece hitstop is gone; one aggregated
    // impact fires per resolved load instead.
    const maw = this.arenaGeometry.intake;
    // The maw MOUTH is the deep slab of the intake volume against the furnace
    // wall (back ~40% of its depth). A jam-reject pile settles in FRONT of it
    // near the floor receiver; incinerating it requires feeding it that last
    // stretch INTO the mouth (LOCKED-DESIGN 18.12 recovery-loop-feel) — merely
    // resting in the chamber must never auto-drain a lane the player has to
    // hand-clear. Delivered/loose trash is still eaten anywhere in the volume
    // (the chamber IS the furnace), so the "throw trash through the portal to
    // burn it" fiction is unchanged; only rejects get the mouth gate.
    const mawMouthZ = maw.min[2] + (maw.max[2] - maw.min[2]) * 0.4;
    for (const p of this.trash.pieces) {
      if (!p.live || p.held) continue;
      const t = p.body.translation();
      if (
        !(t.x > maw.min[0] && t.x < maw.max[0] &&
          t.y > maw.min[1] && t.y < maw.max[1] &&
          t.z > maw.min[2] && t.z < maw.max[2])
      ) continue;
      const jamLane = p.jamLane;
      // (1) A jam-pile reject incinerates ONLY once it reaches the maw mouth —
      //     a reject piled/settled deeper in the chamber stays a physical body so
      //     the lane remains hand-clearable rather than silently auto-draining.
      if (jamLane !== null && t.z > mawMouthZ) continue;
      // (2) An UNSTAMPED reject (rejected, no jamLane yet) is a load still
      //     resolving — its receiver-jammed event has not fired — or an arena-side
      //     reject that wandered in. It is NOT a maw feed. Eating it here (the old
      //     over-broad "stray" path) removed the physical reject the jam pile is
      //     measured against, stranding laneJamBulk above zero bodies: an
      //     unclearable phantom jam that guaranteed a jam-overload loss
      //     (LOCKED-DESIGN 18.12 recovery-loop-feel). Leave it physical; it becomes
      //     feedable once receiver-jammed stamps jamLane.
      //
      //     PLAYTEST F19 narrows that protection to the window it was written for.
      //     Unqualified, it also protected a reject whose jam is already SETTLED —
      //     the load resolved and its lane is no longer jammed, e.g. because the
      //     F13 backing guarantee returned the debt while the piece was invisible
      //     behind a stale `chamber === false`. Such a body is immortal: the maw
      //     skips it forever, the traversal-loop late stamp needs a jammed lane it
      //     will never have again, and the Section 7 census (jamLane or chamber,
      //     both false) cannot see it — while `Trash` caps the arena at 5 live
      //     pieces, so each one permanently eats 20% of the spawn budget. Reached
      //     without any tunnelling too: any F13-returned arena-side reject that is
      //     later bumped or blast-shoved through the portal lands in that state.
      //
      //     `findLoad` reads `this.loads`, which is only pushed at RESOLVE, so
      //     `undefined` is exactly "this load is still resolving" and keeps the
      //     original protection verbatim.
      if (jamLane === null && p.rejected) {
        const load = p.loadId === null ? undefined : this.findLoad(p.loadId);
        if (load === undefined || this.run.laneJammed(load.route.receiver)) continue;
        // Debt already settled: retire the reject identity so the disposition
        // below reads `stray` (lane-less feed — overflow-honest, zero points) and
        // NOT `late` (no action at all). `late` is correct only for a DELIVERED
        // piece, whose bulk `resolveLoad` already subtracted from overflow;
        // rejected bulk was never subtracted, so eating it as `late` would leak
        // this bulk in the overflow meter forever — the same trap the
        // `reject-returned` handler documents.
        p.rejected = false;
        p.rejectReady = false;
        p.loadId = null;
        p.loadIndex = -1;
      }
      // m1.3.0 maw intake (LOCKED-DESIGN 18.12) — the surviving dispositions:
      //  1. jamLane piece (at the mouth) → recovery beat: consume, push a
      //     zero-point lane `feed` that drains laneJamBulk, "RECOVERED · n BULK".
      //  2. stray (a loose loadId-less piece thrown into the maw from the chamber)
      //     → lane-less feed: overflow-honest, zero points.
      //  3. normal delivered load piece → the sim already accounted it at resolve,
      //     so NO action; just the small incinerate beat (LATE popup if its load
      //     already closed). This is the pre-m1.3.0 path, unchanged.
      const stray = jamLane === null && p.loadId === null;
      const late = jamLane === null && !stray && p.loadId !== null && this.findLoad(p.loadId) !== undefined;
      const bulk = p.bulk;
      this.trash.consume(p);
      this.tutorial?.reportIncinerated();
      this.sound.incinerate(jamLane || stray ? 1 : this.run.combo);
      this.renderer.shake(0.08);
      this.tmpV.set(t.x, t.y, t.z);
      this.particles.burst(this.tmpV, jamLane || stray ? PALETTE.conveyance : FEEDBACK_COLORS.blastCore, 10, 4, { up: 0.9, gravity: -3, life: 0.55, spread: 0.4 });
      this.fx.spawn(new THREE.Vector3(t.x, t.y + 0.5, t.z), new THREE.Vector3(0, -1, 0), jamLane || stray ? PALETTE.conveyance : FEEDBACK_COLORS.blastCore, 0.7);
      if (jamLane) {
        actions.push({ type: "feed", lane: jamLane, bulk });
        if (this.hud) {
          const screen = this.projectToHud(this.tmpV);
          // Zero-point framing is VISUAL, not just textual (18.12 stage 3): teal,
          // never score-gold. Simple says it in kid words.
          this.hud.popup(screen.x, screen.y, this.simpleMode ? "FED THE FIRE!" : `RECOVERED · ${bulk} BULK`, cssHex(PALETTE.conveyance));
        }
      } else if (stray) {
        actions.push({ type: "feed", bulk });
        if (this.hud && !this.simpleMode) {
          const screen = this.projectToHud(this.tmpV);
          this.hud.popup(screen.x, screen.y, "INCINERATED · NO CREDIT", cssHex(PALETTE.conveyance));
        }
      } else if (late && this.hud) {
        const screen = this.projectToHud(this.tmpV);
        this.hud.popup(screen.x, screen.y, "LATE", cssHex(FEEDBACK_COLORS.muted));
      }
    }

    // Returned-feed reconciliation (LOCKED-DESIGN 18.12 risk register): a jamLane
    // reject that has physically left the chamber — carried out through a portal,
    // blast-shoved to the arena, or ridden the return belt to the hatch — pays its
    // ONE terminal `feed` (disposition "returned", overflow untouched: the piece is
    // back in circulation). This is the single settle-point that guarantees
    // sum(physical jamLane bulk) === laneJamBulk, so the lane can never strand
    // jammed above the pile. The maw intake above owns the incinerated disposition.
    for (const p of this.trash.pieces) {
      if (p.jamLane === null || !p.live) continue;
      const t = p.body.translation();
      // PLAYTEST F19: "left the chamber" is the authoritative volume predicate,
      // not z alone. Since the 18.13 depth re-author the walkable arena floor
      // wraps both shoulders north of the threshold, so a stamped reject
      // blast-shoved to |x| > 12 at z < −13 was in neither state: physically
      // outside the sealed box (unreachable by the maw, invisible to the chamber
      // census) yet never paying its returned feed, so it backed the lane's debt
      // forever. That is exactly the unfeedable-phantom class 18.12 was written to
      // eliminate, reintroduced by the geometry change. Landing ON the lid is the
      // same story one axis over, so the height bound settles that body too. The
      // belt ride is unaffected — it runs |x| <= RETURN_DUCT_X = 6.7 at y < 1.5,
      // well inside the box, so its return still pays at the hatch as before.
      if (!inChamberVolume(t, this.arenaGeometry)) {
        actions.push({ type: "feed", lane: p.jamLane, bulk: p.bulk, disposition: "returned" });
        p.jamLane = null;
        p.rejected = false;
        p.rejectReady = false;
        continue;
      }
      // Physical belt capture (LOCKED-DESIGN 18.12): a jam reject knocked/settled
      // onto the east collection belt strip rides the visible return journey to the
      // hatch. The ride is COMPLETION-triggered, not start-triggered, so a mid-ride
      // beam grab keeps the debt on the piece; the reconciliation above then pays
      // the returned-feed once it erupts from the hatch arena-side.
      if (!p.held && !this.returnRides.some((ride) => ride.piece === p)) {
        if (Math.abs(t.z - RETURN_BELT_Z) < 0.7 && t.y < 1.5 && t.x > 4.4 && t.x < RETURN_DUCT_X + 0.5) {
          this.startReturnRide(p, 0, { bulk: 0, announced: false });
        }
      }
    }

    // Backing guarantee (PLAYTEST F13). The per-piece pass above settles debt
    // carried by a body that LEFT the chamber; by construction it cannot settle
    // debt that no body ever carried. `Simulation.resolveLoad` jams whenever an
    // under-delivered load had `chamberEntered`, which is route VALIDITY, not
    // physical arrival — the geometry-free kernel cannot know whether a reject
    // actually got there. So a volley thrown AT the receiver that never crosses
    // the portal still jams the lane while every reject lies on the arena floor:
    // no body carries `jamLane`, the pile can never be hand-fed, and the run is
    // dead the moment it stamps (tenth dogfood — "I teleport into the incinerator
    // but I don't actually SEE anything that I can grab"; that run then cooked on
    // the chamber dwell hunting a crate that was never in the room).
    //
    // Returning the unbacked remainder makes the documented invariant
    // sum(physical jamLane bulk) === laneJamBulk ENFORCED rather than merely
    // intended. "returned" is the correct disposition and already means exactly
    // this: bulk that is back in circulation, so overflow is untouched, the
    // recovered tally does not move, and the anti-neglect grace is not refreshed.
    for (const lane of ["safe", "technical", "hazard"] as const) {
      const debt = this.run.jamBulk(lane);
      if (debt <= 0) { this.jamUnbackedSince[lane] = null; continue; }
      let backed = 0;
      for (const p of this.trash.pieces) if (p.live && p.jamLane === lane) backed += p.bulk;
      if (backed >= debt) { this.jamUnbackedSince[lane] = null; continue; }
      const since = this.jamUnbackedSince[lane] ?? this.run.tick;
      this.jamUnbackedSince[lane] = since;
      if (this.run.tick - since < Engine.JAM_BACKING_GRACE_TICKS) continue;
      const written = debt - backed;
      actions.push({ type: "feed", lane, bulk: written, disposition: "returned" });
      // PLAYTEST F20: remember that THIS lane's returned feed was a write-off, so
      // the presentation can say why. The ledger cannot tell the two "returned"
      // sources apart — `jam-fed` carries only { receiver, bulk, remaining,
      // disposition } and the per-piece reconciliation above emits the identical
      // shape for a body that physically left the chamber. Only the Engine knows,
      // and only right here. Consumed and cleared by `consumeLedgerEvents` at the
      // end of this same fixed step (this loop runs during action assembly, before
      // `run.step` emits the event it describes).
      this.jamWrittenOff[lane] += written;
      this.jamUnbackedSince[lane] = null;
    }

    // Enforced glass beat (LOCKED-DESIGN 18.6): a watched thrown piece whose
    // center has reached the reinforced bulkhead has demonstrably struck it.
    // Nothing else clears the step — a release in any other direction just
    // leaves the load on the floor to try again.
    if (this.tutorial?.currentStep() === "glass" && this.glassWatch.size > 0) {
      for (const p of this.trash.pieces) {
        if (!this.glassWatch.has(p.id) || !p.live || p.held) continue;
        const t = p.body.translation();
        // |x| <= 12: the reinforced-glass bulkhead is the 24-wide chamber
        // block's face after the LOCKED-DESIGN 18.13 depth re-author.
        if (t.z <= GLASS_CONTACT_Z && Math.abs(t.x) <= 12) {
          this.presentGlassHit(t.x, Math.max(0.5, t.y));
          this.tutorial.reportBulkheadHit();
          this.glassWatch.clear();
          break;
        }
      }
    }

    if (this.run.phase === "playing") {
      if (this.tutorial?.active) {
        // warm-up: slow drip, few pieces, no timer, no overflow
        this.trash.updateWarmup(FIXED_DT);
      } else {
        // Furnace-maw contact stays local to the central furnace (footprint
        // ±5.5) — without the x-bound the penalty would fire across the whole
        // chamber floor. z bound follows the furnace line to z −19.25 after
        // the LOCKED-DESIGN 18.13 depth re-author (x/y bounds unchanged).
        if (playerState.z < -18.4 && playerState.y < 4.5 && Math.abs(playerState.x) < 5.5) {
          actions.push({ type: "furnace-contact" });
          this.controller.reset();
        }
        this.trash.updateCooldowns(FIXED_DT);
        // (The 18.14 physical-arrival flush used to sit here. PLAYTEST F17 moved it
        // above the maw intake — the receiver's exit point is inside the intake
        // volume, so from here it only ever saw already-eaten pieces. Do not move
        // it back: the ordering is the fix.)
        // Simple v2 auto-moving portal (LOCKED-DESIGN 18.11): a declarative,
        // sim-tick-keyed rule that issues the blue-anchor relocation as a normal
        // scheduled place action into THIS step's command stream (trace-recorded,
        // replay-clean — the 4D engine-issued place path is the precedent).
        this.maybeIssueSimplePortalMove(actions);
        this.run.step({
          moveX: input.moveX,
          moveZ: input.moveZ,
          aimX: this.aimPoint.x / 14,
          aimZ: this.aimPoint.z / 14,
          buttons: input.grabHeld ? 1 : 0,
          ...(actions.length ? { actions } : {}),
        });
        if (beamResult.threw.length > 0 && !chamberRelease && this.run.lastReleasePrediction) {
          const prediction = this.run.lastReleasePrediction;
          beamResult.threw.forEach((piece, index) => {
            piece.loadId = prediction.loadId;
            // INDEX-aligned (LOCKED-DESIGN 18.14): the sim jams
            // bulkOf(pieces.slice(delivered)), so the physical reject set must
            // stay index-for-index identical to that slice. Stamping the index
            // here is what lets the upgrade path clear rejects by index later.
            piece.loadIndex = index;
            piece.rejected = index >= prediction.delivered;
          });
        }
        this.consumeLedgerEvents();
        if (this.run.ended && !this.runEnded) this.finishRun();
      }
    }

    // fell off somehow → respawn player only
    if (playerState.y < -10) this.controller.reset();
  }

  /**
   * Drain the deferred physical-arrival queue into this tick's action stream
   * (LOCKED-DESIGN 18.14 / F8 direction C). Called during action assembly —
   * after the traversal loop, BEFORE the furnace-maw intake, before `run.step` —
   * so a piece whose `loadId` was stamped on an earlier tick emits on the SAME
   * tick it crossed, and a same-tick release+capture emits one tick later once
   * the stamp lands. The pre-maw position is load-bearing, not cosmetic: the
   * receiver's exit point sits inside the intake volume, so a delivered piece is
   * eaten on its crossing tick and `!piece.live` below would discard it
   * (PLAYTEST F17 — see the call site for the full ordering argument).
   *
   * Entries are dropped when the piece is gone, dead, or has been knocked back
   * out of the chamber, and when the grace window expires without a loadId
   * (chamber-side releases push no `release` action, so their pieces never get
   * one — dropping them is correct: an in-chamber throw is not a scored load).
   * KNOWN RESIDUAL (F17, unchanged by the move): a piece that crosses on its own
   * release tick has `loadId === null` when it crosses — the stamp lands after
   * `run.step` — and is eaten that same tick, so it never gets to use the grace
   * window. Measured zero occurrences across both family bundles (every arrival
   * was >= 7 ticks after its release); a fix would have to stamp `loadId` before
   * `run.step`, which reorders release assembly.
   */
  private flushArrivals(actions: SimulationAction[]): void {
    for (let index = this.arrivalQueue.length - 1; index >= 0; index--) {
      const entry = this.arrivalQueue[index]!;
      const piece = this.trash.pieces.find((p) => p.id === entry.pieceId);
      if (!piece || !piece.live || !piece.chamber) {
        this.arrivalQueue.splice(index, 1);
        continue;
      }
      if (piece.loadId !== null) {
        actions.push({ type: "arrive", loadId: piece.loadId, id: piece.id });
        this.arrivalQueue.splice(index, 1);
        continue;
      }
      if (this.run.tick - entry.queuedTick > Engine.ARRIVAL_STAMP_GRACE_TICKS) this.arrivalQueue.splice(index, 1);
    }
  }

  /**
   * The visual loop consumes EVERY presentation-relevant ledger event the sim
   * emits (LOCKED-DESIGN 18.4). Read-only over the ledger: nothing here may
   * push actions or mutate sim state.
   */
  private consumeLedgerEvents(): void {
    /**
     * Lane whose pile was just WRITTEN OFF rather than fed (PLAYTEST F20).
     * `Simulation.feed` emits `receiver-cleared` on the line immediately after
     * the `jam-fed` that drained the pile to zero, so within one pass over this
     * slice the write-off is always seen first; null therefore means "the
     * clearing feed was a real feed", which is the celebratory case.
     */
    let clearedByWriteOff: { lane: ReceiverTier; bulk: number } | null = null;
    for (const event of this.run.ledger.slice(this.visualLedgerIndex)) {
      switch (event.type) {
        case "trash-spawned": {
          const kind = event.data.kind;
          const chute = event.data.chute;
          if ((kind === "crate" || kind === "can" || kind === "green" || kind === "red") && (chute === "left" || chute === "center" || chute === "right")) this.trash.spawnScheduled(kind, chute);
          break;
        }
        case "receiver-jammed": {
          // Stamp jamLane (LOCKED-DESIGN 18.12) onto every reject of this load that
          // is physically in the chamber: they become the hand-feedable jam pile.
          // Late/blast-shoved arrivals get stamped by the traversal loop instead.
          if (event.loadId && (event.data.receiver === "safe" || event.data.receiver === "technical" || event.data.receiver === "hazard")) {
            for (const piece of this.trash.pieces) {
              if (piece.loadId === event.loadId && piece.rejected && piece.chamber) {
                piece.rejectReady = true;
                piece.jamLane = event.data.receiver;
              }
            }
          }
          // The player fed an already-jammed lane again — escalate the recovery
          // prompt for THAT lane (18.10). Dropped as its pile clears
          // (updateWorldTags), so the escalation never migrates to a lane the
          // player never fed.
          if (event.data.cause === "jam-extended"
            && (event.data.receiver === "safe" || event.data.receiver === "technical" || event.data.receiver === "hazard")) {
            this.jamExtendingLanes.add(event.data.receiver);
          }
          // Off-screen comprehension (LOCKED-DESIGN 18.10): a jam forming behind
          // the camera gets a directional edge cue toward its receiver lane, so
          // a run never ends from a jam the player never saw.
          const lane = event.data.receiver;
          if (this.hud && (lane === "safe" || lane === "technical" || lane === "hazard")) {
            const target = this.receiverTarget(lane);
            if (target) {
              const screen = this.projectToHud(this.tmpV.copy(target.pos).addScaledVector(target.normal, 0.5));
              this.hud.edgeFlare(`jam-${lane}`, screen.x, screen.y, `JAM · ${lane.toUpperCase()}`, cssHex(RECEIVER_TIER_COLORS[lane]));
            }
          }
          // First-encounter teaching (LOCKED-DESIGN 12.2/18.10): the first jam
          // in real play teaches recovery — the dogfood safe-routed and never
          // met one.
          if (!this.taughtJam && !this.tutorial?.active) {
            this.taughtJam = true;
            this.teachFirstEncounter("jam");
          }
          break;
        }
        case "load-upgraded": {
          // Physical arrival raised `delivered` on a still-pending load
          // (LOCKED-DESIGN 18.14 / F8-C). Un-stamp the rejects that are no
          // longer rejects — STRICTLY BY INDEX, because the sim will jam
          // `bulkOf(record.pieces.slice(delivered))`, an index-based slice.
          // Clearing chamber-preferred or by ascending id would break that on a
          // mixed-bulk load (pieces [crate 1, green 3], delivered 0 -> the green
          // arrives -> delivered 1 -> the sim jams slice(1) = 3 while only the
          // crate, bulk 1, is still marked), stranding laneJamBulk above the
          // feedable physical bulk: the phantom-jam class 18.12 eliminated.
          //
          // `jamLane` needs no unwinding here: findLoad reads `this.loads`,
          // which is only pushed at RESOLVE, so the late-arriving-reject stamp
          // in the traversal loop cannot fire on a still-pending load — the
          // whole upgrade window is provably free of jam debt.
          if (!event.loadId) break;
          const delivered = Number(event.data.delivered ?? 0);
          for (const p of this.trash.pieces) {
            if (p.loadId !== event.loadId || p.loadIndex < 0) continue;
            if (p.loadIndex >= delivered) continue; // still a reject by index — leave stamped
            if (!p.rejected) continue;
            p.rejected = false;
            p.rejectReady = false;
          }
          break;
        }
        case "reject-returned": {
          // F4 jamsDisabled (LOCKED-DESIGN 18.14): no jam pile forms, so the
          // load's rejects must stop being rejects entirely. Clearing
          // loadId/loadIndex is LOAD-BEARING — a piece that keeps its loadId
          // reads as `late` at the maw intake, which pushes NO feed and leaks
          // overflow upward forever. Cleared, it is a `stray`: overflow-honest,
          // zero points.
          if (!event.loadId) break;
          const delivered = this.findLoad(event.loadId)?.delivered ?? 0;
          for (const p of this.trash.pieces) {
            if (p.loadId !== event.loadId || p.loadIndex < delivered) continue;
            p.rejected = false;
            p.rejectReady = false;
            p.loadId = null;
            p.loadIndex = -1;
          }
          break;
        }
        case "jam-fed": {
          // THE JAM THAT CLEANED ITSELF (PLAYTEST F20, twelfth dogfood: "I also
          // saw a jam happen and then clean on it's own for some reason").
          //
          // `jam-fed` had NO case here at all, so the F13 write-off produced
          // literally zero presentation. What the family actually got, three
          // times in one shift: the full jam vocabulary raised at tick T — the
          // first-encounter teaching card, the `JAM · SAFE` edge flare, the
          // "STUCK! GO FIX IT!" world tag, the recovery prompt, the receiver
          // invite highlight, the maw guard glow, the entrance nudge, and the
          // Section 3.3 interlock freezing every portal placement — and then at
          // T+91, with no input and nothing on screen to explain it, the whole
          // thing vanished under a "ALL CLEAR! GO SCORE!" toast and a relight
          // sting. The kernel is not confused (`Simulation.feed` deliberately
          // leaves overflow, jamBulkFed and the anti-neglect grace untouched for
          // a "returned" feed: it is not a player achievement). Only the
          // presentation was, and it congratulated them for it.
          //
          // The cause is honest and worth saying: the load's ROUTE was legal so
          // the geometry-free kernel jammed the lane (`chamberEntered` is
          // `routeValid`, Simulation.ts), but not one reject physically crossed —
          // verified against the bundle's portal-traversal stream for every one
          // of those jams. "Nothing arrived" is the player-facing truth.
          //
          // Only the write-off is presented. The other "returned" source is a real
          // body leaving the chamber, which already narrates itself (the belt ride
          // pops "RETURNED · n BULK" at the hatch; a portal carry is in your
          // hands), and `incinerated` is already covered by the maw popup.
          const lane = event.data.receiver;
          const bulk = Number(event.data.bulk ?? 0);
          if (event.data.disposition !== "returned") break;
          if (lane !== "safe" && lane !== "technical" && lane !== "hazard") break;
          if (bulk <= 0 || this.jamWrittenOff[lane] < bulk) break;
          this.jamWrittenOff[lane] -= bulk;
          // Hand off to `receiver-cleared` ONLY when this feed drained the pile to
          // zero — `Simulation.feed` emits the pair adjacently and gates the second
          // on `laneJamBulk[lane] === 0`. Latching on a PARTIAL write-off (the
          // owner's jams #3/#4 were 3→1 and 5→1) would leave the hand-off armed for
          // the rest of the slice, and a genuine hand-fed clear of the same lane in
          // the same fixed step would inherit a "never arrived" framing it did not
          // earn. The popup below still fires: the write-off did happen.
          if (Number(event.data.remaining ?? 0) === 0) clearedByWriteOff = { lane, bulk };
          const target = this.receiverTarget(lane);
          if (this.hud && target) {
            const screen = this.projectToHud(this.tmpV.copy(target.pos).addScaledVector(target.normal, 0.5));
            // Teal, never score-gold: nothing was recovered and nothing was
            // scored (18.12 stage 3 zero-point framing).
            this.hud.popup(
              screen.x, screen.y,
              this.simpleMode ? "IT NEVER GOT THERE!" : `${bulk} BULK NEVER ARRIVED`,
              cssHex(PALETTE.conveyance),
            );
          }
          break;
        }
        case "receiver-cleared": {
          // LANE BACK IN SERVICE (LOCKED-DESIGN 18.12 stage 3): in m1.3.0 this means
          // the reject pile was fully hand-fed to the maw — the lane relights. No
          // return-belt eject here anymore; the pieces already incinerated at the
          // intake. Chain/Grade are visibly untouched (chamber is recovery-only).
          const lane = event.data.receiver;
          if (lane === "safe" || lane === "technical" || lane === "hazard") {
            const target = this.receiverTarget(lane);
            if (target) {
              this.fx.spawn(target.pos, target.normal, PALETTE.conveyance, 1.3, 0.45);
              this.particles.burst(this.tmpV.copy(target.pos).addScaledVector(target.normal, 0.3), PALETTE.conveyance, 12, 3.8, { spread: 0.7, gravity: -3, life: 0.55 });
            }
            // PLAYTEST F20: a write-off is not a victory. The relight is real —
            // the lane IS back in service — but the player did nothing to earn it,
            // so it gets the neutral "that came back" sting the return hatch uses
            // and copy that names the cause instead of the "ALL CLEAR! GO SCORE!"
            // praise, which was the exact line that made the retraction read as
            // arbitrary. Cleared only on a lane THIS pass wrote off, so a genuine
            // hand-fed clear is untouched.
            const wroteOff = clearedByWriteOff?.lane === lane ? clearedByWriteOff : null;
            if (wroteOff) this.sound.returnEject(wroteOff.bulk);
            else this.sound.ticketDone(); // relight PA sting (reuses the "system OK" chime)
            this.hud?.toast(
              wroteOff
                ? (this.simpleMode ? "NOTHING WAS STUCK! THROW AGAIN!" : `${lane.toUpperCase()} LANE CLEAR — THE JAM NEVER ARRIVED`)
                : (this.simpleMode ? "ALL CLEAR! GO SCORE!" : `${lane.toUpperCase()} LANE CLEAR — BACK IN SERVICE`),
              cssHex(PALETTE.conveyance),
              2000,
            );
            if (wroteOff) clearedByWriteOff = null;
          }
          break;
        }
        case "portal-swapped": {
          // Simple v2 (LOCKED-DESIGN 18.11): the engine-issued auto-move landed —
          // relocate the render ring, celebrate, teach. Adult play ignores this;
          // its render ring already moved on manual placement (tryPlacePortal),
          // so reacting here would double-move it.
          if (this.simpleMode && event.data.endpoint === "blue") {
            this.onSimplePortalSwapped(event.data.target as ArenaAnchor);
          }
          break;
        }
        case "portal-fallback": {
          // Expiring-panel resolution (18.4): the boundary reroute is telegraphed
          // five seconds ahead (updateHazardTelegraphs) and ANNOUNCED here —
          // never a silent snap the player has to discover mid-throw.
          const blue = event.data.blue === true;
          const orange = event.data.orange === true;
          if (blue) this.portals.resetEndToDefault("A");
          if (orange) this.portals.resetEndToDefault("B");
          if (blue || orange) {
            if (blue) this.fx.spawn(this.portals.A.pos, this.portals.A.normal, PALETTE.anchorBlue, 1.2);
            if (orange) this.fx.spawn(this.portals.B.pos, this.portals.B.normal, PALETTE.receiverOrange, 1.2);
            // Simple never surfaces the facility "panel expired" vocabulary
            // (18.11); a jam-deferred move past a boundary just reverts to the
            // safe floor silently, and the still-pending auto-move re-swaps.
            if (!this.simpleMode) this.hud?.toast("PANEL EXPIRED · ROUTE REVERTED TO DEFAULT", cssHex(FEEDBACK_COLORS.muted), 2000);
          }
          break;
        }
        case "load-resolved":
          this.presentLoadResolved(event);
          break;
        case "chain-expired":
          if (this.prevChainCombo > 1) {
            this.sound.chainBreak("expiry");
            this.hud?.chainBreakFlash();
          }
          this.prevChainCombo = 1;
          break;
        case "ticket-completed": {
          this.sound.ticketDone();
          // The dispatch bonus moves the Grade meter too (Section 9 +8) — name
          // it (18.10 fourth dogfood), clamp-true via the sim's own fold.
          const move = this.run.gradeMoveAt(event.seq);
          this.hud?.toast(`Dispatch complete — +${event.data.bonus}${move ? `, Grade ${signedGrade(move.applied)}` : ""}. Nice hustle.`, cssHex(FEEDBACK_COLORS.dispatch));
          // stage for the same-tick load-resolved so ONE chip carries both causes
          if (event.loadId) this.pendingDispatch = { loadId: event.loadId, seq: event.seq };
          break;
        }
        case "ticket-expired":
          this.hud?.toast("Dispatch expired — no penalty. Next one's coming.", cssHex(FEEDBACK_COLORS.muted), 1800);
          break;
        case "critical-overflow":
          this.alarmActive = true;
          // Soft failure in Simple (LOCKED-DESIGN 18.11): the run still ends on
          // critical overflow, but a 7-9yo gets no adult panic klaxon — the
          // vignette below also drops to a gentle steady glow (never a strobe).
          if (!this.simpleMode) this.sound.alarmStart();
          this.arena.setChuteShuttersClosed(true);
          break;
        case "critical-cleared":
          this.alarmActive = false;
          this.sound.alarmEnd();
          this.arena.setChuteShuttersClosed(false);
          this.hud?.setAlarm(false);
          break;
        case "emergency-purge":
          this.presentPurge(event);
          break;
        case "purge-failed":
        case "placement-failed":
          this.presentDeny();
          break;
        case "red-detonated": {
          // blast VFX/audio fire at the detonation site in physicsStep; the
          // ledger event owns the chain consequence.
          if (this.prevChainCombo > 1) this.hud?.chainBreakFlash();
          this.prevChainCombo = 1;
          // Grade attribution (18.10 fourth dogfood): the detonation's Section 5
          // Grade cost names itself on the meter, clamp-true.
          const move = this.run.gradeMoveAt(event.seq);
          if (move && move.applied !== 0) this.hud?.gradeDelta(`${signedGrade(move.applied)} DETONATION`, cssHex(FEEDBACK_COLORS.alarm));
          break;
        }
        case "furnace-contact":
          this.presentFurnaceDeath(event);
          break;
        default:
          break;
      }
    }
    this.visualLedgerIndex = this.run.ledger.length;
    // PLAYTEST F20: the write-off record is per-fixed-step. Cleared unconditionally
    // so a feed the sim clamped to a smaller `drained` (or dropped entirely) can
    // never leak the "never arrived" framing onto a later, genuine return.
    this.jamWrittenOff.safe = 0;
    this.jamWrittenOff.technical = 0;
    this.jamWrittenOff.hazard = 0;
  }

  /**
   * Hand a rejected piece to the return-belt journey (Section 6 / 18.10 third
   * dogfood). The sim's receiver-cleared IS the timing authority — this only
   * walks the piece along the visible route (reject pile → collection belt →
   * east duct → the bright return hatch beside the island) instead of the old
   * instant teleport. Render-side body scripting; the sim never sees piece
   * positions and the exit point/velocity are unchanged.
   */
  private startReturnRide(piece: TrashPiece, index: number, batch: { bulk: number; announced: boolean }): void {
    piece.rejectReady = false; // consumed by the ride; `rejected` stays true in transit
    batch.bulk += piece.bulk;
    const t = piece.body.translation();
    const beltEntry = new THREE.Vector3(Math.min(Math.max(t.x, -4.6), RETURN_DUCT_X - 0.4), RETURN_BELT_Y, RETURN_BELT_Z);
    this.returnRides.push({
      piece,
      ticks: 0,
      delay: index * RETURN_STAGGER_TICKS,
      from: new THREE.Vector3(t.x, t.y, t.z),
      beltEntry,
      beltTicks: Math.max(8, Math.round(((RETURN_DUCT_X - beltEntry.x) / RETURN_BELT_SPEED) * 60)),
      batch,
    });
  }

  /** Walk active reject-return rides one fixed tick (render-side scripting). */
  private updateReturnRides(): void {
    for (let index = this.returnRides.length - 1; index >= 0; index--) {
      const ride = this.returnRides[index]!;
      const piece = ride.piece;
      if (!piece.live) {
        this.returnRides.splice(index, 1);
        continue;
      }
      if (piece.held) {
        // fished out of the return system by the beam — it's the player's now
        piece.rejected = false;
        piece.rejectReady = false;
        piece.loadId = null;
        piece.loadIndex = -1;
        // PLAYTEST F19: same authoritative predicate every other writer uses.
        piece.chamber = inChamberVolume(piece.body.translation(), this.arenaGeometry);
        this.returnRides.splice(index, 1);
        continue;
      }
      if (ride.delay > 0) {
        ride.delay--;
        continue;
      }
      ride.ticks++;
      const drop = RETURN_DROP_TICKS;
      if (ride.ticks >= drop + ride.beltTicks) {
        // re-entry: erupt from the return hatch beside the island (Section 6)
        piece.body.setTranslation(RETURN_EXIT, true);
        piece.body.setLinvel(RETURN_EXIT_VEL, true);
        piece.prevPos.set(RETURN_EXIT.x, RETURN_EXIT.y, RETURN_EXIT.z); // no render smear
        piece.mesh.position.copy(piece.prevPos);
        piece.chamber = false;
        piece.rejected = false;
        piece.loadId = null;
        piece.loadIndex = -1;
        this.trails.follow(piece, PALETTE.conveyance); // "that trash came BACK"
        this.sound.returnEject(piece.bulk);
        this.tmpV.set(RETURN_EXIT.x, RETURN_EXIT.y, RETURN_EXIT.z);
        this.fx.spawn(this.tmpV, new THREE.Vector3(0, 1, 0), PALETTE.conveyance, 1.1);
        if (!ride.batch.announced && this.hud) {
          ride.batch.announced = true;
          const screen = this.projectToHud(this.tmpV);
          this.hud.popup(screen.x, screen.y, `RETURNED · ${ride.batch.bulk} BULK`, cssHex(PALETTE.conveyance));
        }
        this.returnRides.splice(index, 1);
        continue;
      }
      // path scripting: reject pile → belt entry, then east along the belt.
      // Translation AND matching velocity are written every fixed tick, so the
      // dynamic body stays on the path and interpolation reads a smooth ride.
      if (ride.ticks <= drop) {
        const f = ride.ticks / drop;
        this.tmpV.lerpVectors(ride.from, ride.beltEntry, f);
        const inv = 60 / drop;
        piece.body.setLinvel({
          x: (ride.beltEntry.x - ride.from.x) * inv,
          y: (ride.beltEntry.y - ride.from.y) * inv,
          z: (ride.beltEntry.z - ride.from.z) * inv,
        }, true);
      } else {
        const f = (ride.ticks - drop) / ride.beltTicks;
        this.tmpV.set(ride.beltEntry.x + (RETURN_DUCT_X - ride.beltEntry.x) * f, RETURN_BELT_Y, RETURN_BELT_Z);
        piece.body.setLinvel({ x: RETURN_BELT_SPEED, y: 0, z: 0 }, true);
      }
      piece.body.setTranslation({ x: this.tmpV.x, y: this.tmpV.y, z: this.tmpV.z }, true);
    }
  }

  /**
   * Return-pad launch (LOCKED-DESIGN 18.12 §3.4). Detects the player standing on
   * the chamber pad, runs a 0.5s (30-tick) spin-up, then teleports them to the
   * blue anchor's exit pose (the SAME twin-center exit computeTraversal produces)
   * and pushes a portal-traversal side:"arena" so the sim registers the chamber
   * exit + dwell reset. Scripted, not ballistic. `returnPadHold` is exposed for
   * the presentation ring-fill / rising-tone and the QA scenario.
   */
  private updateReturnPad(actions: SimulationAction[]): void {
    if (this.run.phase !== "playing" || this.tutorial?.active || this.runEnded) {
      if (this.returnPadHold !== 0) { this.returnPadHold = 0; this.arena?.setReturnPadCharge(0); }
      return;
    }
    const pad = this.returnPadCenter();
    const p = this.currPlayerPos;
    const onPad = pad !== null && Math.abs(p.x - pad.x) < 1.0 && Math.abs(p.z - pad.z) < 1.0 && p.y < pad.y + 1.6 && p.z < this.arenaGeometry.chamberThresholdZ;
    if (!onPad) {
      if (this.returnPadHold !== 0) { this.returnPadHold = 0; this.arena?.setReturnPadCharge(0); }
      return;
    }
    this.returnPadHold++;
    this.arena?.setReturnPadCharge(Math.min(1, this.returnPadHold / RETURN_PAD_SPINUP_TICKS));
    if (this.returnPadHold === 1) this.sound.padSpinUp();
    if (this.returnPadHold < RETURN_PAD_SPINUP_TICKS || !this.portals.bothLive) return;
    // Spin-up complete → scripted launch through the receiver portal (exit at the
    // blue anchor A, arena-side). Exit pose mirrors computeTraversal's twin-center
    // rule so a wall/ceiling receiver can never strand the player.
    const exit = this.tmpV.copy(this.portals.A.pos).addScaledVector(this.portals.A.normal, 0.7 + PLAYER_EXIT_CLEARANCE);
    const exitVel = this.tmpV2.copy(this.portals.A.normal).multiplyScalar(6).setY(3.2);
    this.controller.teleport(exit, exitVel);
    this.prevPlayerPos.copy(exit);
    this.currPlayerPos.copy(exit);
    this.renderer.snapFollow(exit.x, exit.z);
    this.playerPortalArmed = false;
    this.playerRearmTicks = PORTAL_REARM_TICKS;
    this.returnPadHold = 0;
    this.arena?.setReturnPadCharge(0);
    actions.push({ type: "portal-traversal", subject: "player", side: exit.z < this.arenaGeometry.chamberThresholdZ ? "chamber" : "arena" });
    this.sound.portalZap();
    this.renderer.fovPunch(9);
    this.renderer.shake(0.15);
    const reduced = this.flashReduced();
    this.fx.spawn(this.portals.A.pos, this.portals.A.normal, PALETTE.anchorBlue, 1.6, reduced ? 0.16 : 0.24, reduced ? 0.4 : 0.7);
    this.particles.burst(exit, 0x9fd8ff, 14, 4.5, { spread: 0.9, gravity: -3, life: 0.5 });
  }

  /** Authored chamber return-pad center (arena.v1.json), or null before the arena binds. */
  private returnPadCenter(): { x: number; y: number; z: number } | null {
    const obj = this.arena?.objects.get("return-pad");
    return obj ? { x: obj.position.x, y: obj.position.y, z: obj.position.z } : null;
  }

  /**
   * Chamber-recovery presentation (LOCKED-DESIGN 18.12 stage 3), polled per frame
   * from the sim's dwell/overload getters — all new flash/pulse behind the flash
   * knobs, camera never shakes while escaping (readability). Drives: the CHAMBER
   * HEAT dwell meter (HUD + furnace/rim world escalation + heat-whine), the
   * FURNACE OVERLOAD chip + glass-distress ramp + final-5s klaxon, and the maw
   * feed-guard glow. Read-only over the sim; issues no actions.
   */
  private updateChamberRecovery(active: boolean): void {
    const inChamber = active && this.run.playerInChamber;
    const dwellLeft = this.run.dwellTicksLeft;
    const dwellTotal = this.run.dwellTicksFor(this.run.seed);
    // DWELL / CHAMBER HEAT — fill = elapsed fraction (1 = about to cook).
    if (inChamber) {
      const frac = dwellTotal > 0 ? Math.min(1, Math.max(0, (dwellTotal - dwellLeft) / dwellTotal)) : 0;
      const warning = this.run.dwellWarning;
      this.hud?.setChamberHeat({
        frac,
        seconds: dwellLeft / 60,
        warning,
        simple: this.simpleMode,
        reduced: this.flashReduced(),
      });
      this.arena?.setChamberHeat(frac);
      if (warning && !this.dwellWarned) {
        this.dwellWarned = true;
        this.sound.chamberHeatWarn();
      } else if (!warning) {
        this.dwellWarned = false;
      }
    } else {
      if (this.chamberHeatWasOn) this.sound.chamberCool();
      this.hud?.setChamberHeat(null);
      this.arena?.setChamberHeat(0);
      this.dwellWarned = false;
    }
    this.chamberHeatWasOn = inChamber;

    // OVERLOAD — the furnace clock (any-jam). Chip appears the instant a jam forms.
    const overloadLeft = this.run.overloadTicksLeft;
    const held = this.run.overloadHeld;
    if (active && overloadLeft !== null) {
      const seconds = Math.ceil(overloadLeft / 60);
      // Never fires over the critical-overflow alarm (overflow owns the priority
      // bus, 18.5). Simple gets no klaxon (soft-alarm policy, 18.11).
      const finalWarn = !held && overloadLeft <= 300 && overloadLeft > 0;
      this.hud?.setOverload({ seconds, held, simple: this.simpleMode });
      this.arena?.setFurnaceDistress(held ? 1 : Math.min(1, (this.run.overloadTicksFor(this.run.seed) - overloadLeft) / this.run.overloadTicksFor(this.run.seed)));
      if (finalWarn && !this.simpleMode && !this.alarmActive && !this.overloadKlaxonOn) {
        this.overloadKlaxonOn = true;
        this.sound.overloadKlaxon(true);
      } else if ((!finalWarn || this.alarmActive) && this.overloadKlaxonOn) {
        this.overloadKlaxonOn = false;
        this.sound.overloadKlaxon(false);
      }
    } else {
      this.hud?.setOverload(null);
      this.arena?.setFurnaceDistress(0);
      if (this.overloadKlaxonOn) { this.overloadKlaxonOn = false; this.sound.overloadKlaxon(false); }
    }
  }

  /**
   * Reward ladder core (LOCKED-DESIGN 18.4): load-resolved drives the
   * celebration hierarchy. Clean = the payoff (real multiplied total at the
   * receiver), partial = muted subtotal, miss = distinct negative beat.
   */
  private presentLoadResolved(event: LedgerEvent): void {
    const outcome = event.data.outcome;
    const score = Number(event.data.score ?? 0);
    const chain = Number(event.data.chain ?? 1);
    const delivered = Number(event.data.delivered ?? 0);
    const record = event.loadId ? this.findLoad(event.loadId) : undefined;
    const lane = record?.route.receiver ?? this.run.activeRoute.receiver;
    const target = this.receiverTarget(lane);
    const chainTint = CHAIN_TIER_COLORS[Math.max(0, Math.min(CHAIN_TIER_COLORS.length - 1, record?.chainAtRelease ?? chain - 1))]!;
    const loadBulk = record ? bulkOf(record.pieces) : delivered;

    if (outcome === "clean") {
      if (target) {
        const screen = this.projectToHud(this.tmpV.copy(target.pos).addScaledVector(target.normal, 0.4));
        this.hud?.popup(screen.x, screen.y, `+${score}`, cssHex(chainTint));
        this.fx.spawn(target.pos, target.normal, chainTint, 1.6, 0.45);
        // CLEAN LOAD celebration (LOCKED-DESIGN 18.4). Simple scales the burst up
        // to confetti-size for the payoff (18.11); prefers-reduced-motion / the
        // flash knob cap it to a small static burst. Same +points popup + chime.
        const burst = this.simpleMode
          ? this.flashReduced()
            ? { count: 16, speed: 3, opts: { spread: 0.8, gravity: -2, life: 0.4, up: 0.5 } }
            : { count: 34 + delivered * 6, speed: 6, opts: { spread: 0.95, gravity: -3, life: 0.85, up: 0.75 } }
          : { count: 14 + delivered * 3, speed: 4.5, opts: { spread: 0.7, gravity: -3, life: 0.6, up: 0.6 } };
        this.particles.burst(this.tmpV.copy(target.pos).addScaledVector(target.normal, 0.3), chainTint, burst.count, burst.speed, burst.opts);
      }
      this.arena.pulseReceiver(lane, 1);
      this.sound.cleanLoad(loadBulk, chain);
      // The "YOU FOUND IT!" arrival star-burst (LOCKED-DESIGN 18.11 v2) is GONE
      // (eleventh dogfood: "they aren't helpful"). It fired on the first clean
      // load after every auto ring move, so on the Simple portal cycle it landed
      // roughly once a phase — frequent enough to read as noise rather than
      // praise, and it competed with the toast that had just said RING MOVED.
      // The move itself is still taught by that toast and the one-shot pause card.
      // volley policy: ONE aggregated impact per resolved load, scaled by size
      this.impactTimer = Math.min(0.05 + loadBulk * 0.012, 0.13);
      this.renderer.impactPunch(1.1 + loadBulk * 0.3);
      this.renderer.shake(0.12 + loadBulk * 0.03);
    } else if (outcome === "partial") {
      if (target) {
        const screen = this.projectToHud(this.tmpV.copy(target.pos).addScaledVector(target.normal, 0.4));
        this.hud?.popup(screen.x, screen.y, `+${score} PARTIAL`, cssHex(FEEDBACK_COLORS.partial));
        this.fx.spawn(target.pos, target.normal, FEEDBACK_COLORS.partial, 1.0, 0.35);
      }
      this.arena.pulseReceiver(lane, 0.4);
      this.sound.partialLoad();
      this.impactTimer = Math.max(this.impactTimer, 0.04);
      this.renderer.impactPunch(0.7);
    }

    // Grade attribution (LOCKED-DESIGN 8.3 / 18.10 fourth dogfood): every
    // Grade delta names its cause with the movement the meter ACTUALLY made
    // (the sim's clamp-true fold — never recomputed here, never pre-clamp).
    const move = this.run.gradeMoveAt(event.seq);
    const dispatch = this.pendingDispatch && this.pendingDispatch.loadId === event.loadId
      ? this.run.gradeMoveAt(this.pendingDispatch.seq)
      : null;
    if (this.pendingDispatch?.loadId === event.loadId) this.pendingDispatch = null;

    if (outcome !== "clean" && outcome !== "partial") {
      // The miss popup names its APPLIED Grade cost directly (8.3 attribution;
      // at Grade 4 the meter can only pay −4, so that is what it says). Simple
      // hides the Grade meter + arithmetic (LOCKED-DESIGN 18.11), so the kid
      // gets the soft miss SOUND but no signed Grade-delta popup.
      if (target && !this.simpleMode) {
        const screen = this.projectToHud(this.tmpV.copy(target.pos).addScaledVector(target.normal, 0.4));
        this.hud?.popup(screen.x, screen.y, `MISS ${signedGrade(move?.applied ?? -10)}`, cssHex(FEEDBACK_COLORS.muted));
      }
      this.sound.missLoad();
    }

    // Simple never surfaces the Grade meter or route economics, so it also skips
    // the STALE teaching pause (LOCKED-DESIGN 18.11: the systems still run, they
    // are simply not taught or shown to the kid).
    if (!this.simpleMode && move && (move.cause !== "partial" || dispatch)) {
      this.presentGradeCause(move, dispatch, target);
      // one-time STALE first-encounter pause (12.2): teach the vary-routes
      // rule the first time a load actually resolves stale in unranked play
      if (move.cause === "stale" && !this.taughtStale && !this.tutorial?.active) {
        this.taughtStale = true;
        this.teachFirstEncounter("stale");
      }
    }

    // chain tier movement — data.chain is the post-update visible tier
    if (chain > this.prevChainCombo) {
      this.sound.chainUp(chain);
      this.hud?.pulseCombo();
      // Simple replaces the hidden Chain ladder with a big star-burst word
      // (LOCKED-DESIGN 18.11), riding the same tier stinger audio above.
      this.hud?.chainTierWord(chain);
    } else if (chain < this.prevChainCombo) {
      this.sound.chainBreak("miss");
      this.hud?.chainBreakFlash();
    }
    this.prevChainCombo = chain;
  }

  /**
   * Grade-delta cause moment (LOCKED-DESIGN 8.3 / 18.10 fourth dogfood): a
   * facility-voice popup at the receiver naming the Section 8.3 cause, plus a
   * transient delta chip on the Grade meter itself. Deltas are the sim's
   * APPLIED movements (clamp-true), and a same-resolve dispatch completion
   * rides the same chip, so the chip's sum always equals what the meter fill
   * just did. The popup is rate-limited (one per ~0.8s of cosmetic clock) so a
   * busy volley never spams; the meter chip is a single latest-wins element,
   * so it always reflects the newest delta at zero spam cost.
   */
  private presentGradeCause(move: GradeMove, dispatch: GradeMove | null, target: AuthoredTarget | null): void {
    const signed = signedGrade(move.applied);
    const style = move.cause === "varied"
      ? { text: `VARIED ROUTE ${signed}`, color: cssHex(PALETTE.tierSafe) }
      : move.cause === "repeat"
        ? { text: `ROUTE REPEAT ${signed}`, color: cssHex(FEEDBACK_COLORS.partial) }
        : move.cause === "stale"
          ? { text: `STALE ROUTE ${signed}`, color: cssHex(FEEDBACK_COLORS.stale) }
          : move.cause === "partial"
            ? { text: "", color: cssHex(FEEDBACK_COLORS.dispatch) }
            : { text: `MISS ${signed}`, color: cssHex(FEEDBACK_COLORS.muted) };
    const chipParts: string[] = [];
    if (move.cause !== "partial") chipParts.push(`${signed} ${move.cause.toUpperCase()}`);
    if (dispatch) chipParts.push(`${signedGrade(dispatch.applied)} DISPATCH`);
    this.hud?.gradeDelta(chipParts.join(" · "), style.color);
    // the miss beat already carries its cost at the receiver — chip only
    if (move.cause === "miss") return;
    if (target && this.animClock - this.gradeCauseShownAt >= 0.8) {
      this.gradeCauseShownAt = this.animClock;
      const popupText = [style.text, dispatch ? `DISPATCH ${signedGrade(dispatch.applied)}` : ""].filter(Boolean).join(" · ");
      const screen = this.projectToHud(this.tmpV.copy(target.pos).addScaledVector(target.normal, 0.4));
      this.hud?.popup(screen.x, screen.y + 22, popupText, style.color);
    }
  }

  /**
   * Red-orb detonation theater (Section 5 + 18.4). Arena: shockwave ring
   * scaled to the 5m blast radius, debris, flash, directional shake. Chamber:
   * contained — glass-stress flicker on the bulkhead, no arena shockwave.
   */
  private presentBlast(x: number, y: number, z: number, location: "arena" | "chamber", playerX: number, playerZ: number): void {
    this.sound.blast(location);
    if (location === "arena") {
      this.tmpV.set(x, Math.max(0.15, y * 0.4), z);
      // FxPool rings end at 1.9 × size world units → 5m radius at size ≈ 2.63
      this.fx.spawn(this.tmpV, new THREE.Vector3(0, 1, 0), FEEDBACK_COLORS.blastCore, 2.63, 0.5);
      this.tmpV.set(x, y, z);
      this.particles.burst(this.tmpV, FEEDBACK_COLORS.blastCore, 32, 9, { spread: 0.9, up: 0.8, life: 0.8, gravity: -7 });
      this.particles.burst(this.tmpV, FEEDBACK_COLORS.debris, 20, 6, { spread: 1.1, up: 0.9, life: 1, gravity: -9 });
      this.flash(cssHex(FEEDBACK_COLORS.blastCore), 0.4, 0.3);
      this.renderer.kick(playerX - x, playerZ - z, 0.55);
      this.renderer.shake(0.7);
    } else {
      // 'strong glass-shaking visual feedback but no physical shockwave'
      this.arena.stressGlass(1);
      this.tmpV.set(x, y, z);
      this.particles.burst(this.tmpV, FEEDBACK_COLORS.blastCore, 16, 5, { spread: 0.7, up: 0.6, life: 0.6, gravity: -6 });
      this.renderer.shake(0.3);
    }
    // Off-screen comprehension (LOCKED-DESIGN 18.10): if the detonation is
    // off-camera — the dogfood "red orb detonated in the pile unseen" — drop an
    // edge cue pointing at it. On-screen blasts are already visible, so
    // edgeFlare no-ops for an in-frame point.
    if (this.hud) {
      const screen = this.projectToHud(this.tmpV.set(x, y, z));
      this.hud.edgeFlare("blast", screen.x, screen.y, "RED BLAST", cssHex(FEEDBACK_COLORS.alarm));
    }
  }

  /** Purge accepted: chute slam + the locked score-cost toast, facility voice. */
  private presentPurge(event: LedgerEvent): void {
    if (this.prevChainCombo > 1) this.hud?.chainBreakFlash();
    this.prevChainCombo = 1;
    this.sound.purge();
    this.hud?.toast("NO POINTS · CHAIN RESET", cssHex(FEEDBACK_COLORS.overflowWarning));
    // the purge's Grade band drop names itself on the meter (18.10 fourth
    // dogfood: every Grade delta attributed) — applied value, sim-derived
    const move = this.run.gradeMoveAt(event.seq);
    if (move && move.applied !== 0) this.hud?.gradeDelta(`${signedGrade(move.applied)} PURGE`, cssHex(FEEDBACK_COLORS.overflowWarning));
    const purge = this.arenaGeometry.purgeZone;
    this.tmpV.set((purge.min[0] + purge.max[0]) / 2, 0.4, (purge.min[2] + purge.max[2]) / 2);
    this.fx.spawn(this.tmpV, new THREE.Vector3(0, 1, 0), FEEDBACK_COLORS.overflowWarning, 1.6, 0.45);
    this.particles.burst(this.tmpV, FEEDBACK_COLORS.debris, 16, 4, { up: 0.8, life: 0.6, spread: 0.6, gravity: -7 });
    this.renderer.shake(0.25);
  }

  /** Furnace death: camera hard cut + white-out (flash-aware) + respawn ring. */
  private presentFurnaceDeath(event: LedgerEvent): void {
    if (this.prevChainCombo > 1) this.hud?.chainBreakFlash();
    this.prevChainCombo = 1;
    // the furnace's Grade band drop names itself on the meter (18.10 fourth
    // dogfood: every Grade delta attributed) — applied value, sim-derived
    const move = this.run.gradeMoveAt(event.seq);
    if (move && move.applied !== 0) this.hud?.gradeDelta(`${signedGrade(move.applied)} FURNACE`, cssHex(FEEDBACK_COLORS.alarm));
    this.sound.death();
    this.flash(cssHex(FEEDBACK_COLORS.death), 0.85, 0.4);
    const spawn = this.arenaGeometry.spawn;
    this.renderer.rig.snapFollow(spawn.x, spawn.z); // hard cut, no follow smear
    this.tmpV.set(spawn.x, Math.max(0.1, spawn.y - 0.8), spawn.z);
    this.fx.spawn(this.tmpV, new THREE.Vector3(0, 1, 0), PALETTE.conveyance, 1.4, 0.5);
    this.particles.burst(this.tmpV, PALETTE.conveyance, 14, 3.5, { up: 0.9, life: 0.6, spread: 0.5, gravity: -5 });
  }

  /**
   * Orientation done (graduated or skipped). Graduation exits through a
   * ready-beat: a fresh Simulation is created — which carries the universal
   * five-second placement countdown (Section 10) — with the portals RECONCILED
   * to its default safe route, so the presented route always equals the scored
   * route (fixes the graduation desync, LOCKED-DESIGN 18.6). Warm-up trash is
   * preserved on graduation so nothing pops out of existence; a skip drops
   * straight into a clean shift.
   */
  private finishTutorial(skipped: boolean): void {
    this.tutorial = null;
    this.hud?.setTutorialMode(false);
    // resetWorld rebuilds the sim + reconciles portals to defaults (both live).
    // Graduation keeps the warm-up pieces; a skip clears them.
    this.resetWorld(!skipped);
    if (!skipped) {
      this.sound.runEnd(true); // little fanfare for graduates
      this.hud?.toast(
        this.simpleMode
          ? "READY? HERE COMES THE TRASH!"
          : "Orientation complete — place your portals. Your shift starts on the countdown.",
        cssHex(PALETTE.tierSafe),
        3200,
      );
    }
    // Simple onboarding lives in its OWN slot (LOCKED-DESIGN 18.11) so an adult
    // "onboarding" done never skips the kid orientation and vice versa.
    const slot = this.simpleMode ? "onboarding-simple" : "onboarding";
    void this.ctx.save(slot, { done: true, skipped }).catch(() => {
      /* saving is best-effort; worst case the warm-up runs again */
    });
  }

  /**
   * A T-reset / Select. During orientation it RESTARTS orientation — it never
   * silently marks onboarding done+skipped (LOCKED-DESIGN 18.6). Outside it,
   * it restarts the current shift.
   */
  private onResetPressed(): void {
    if (this.tutorial?.active) {
      this.tutorial.destroy();
      this.tutorial = null;
      this.resetWorld();
      this.spawnTutorial(); // re-arm orientation; no onboarding save
      return;
    }
    // Simple deliberately hides "T reset" from the hint bar (LOCKED-DESIGN
    // 18.11) but the key stays armed engine-wide. For a button-mashing 7-9yo,
    // one stray T (next to R/E) would silently wipe minutes of play with no
    // confirm and no feedback — read as punishment. Ignore it in Simple.
    if (this.simpleMode) return;
    this.resetWorld();
  }

  /** Render-side feedback state back to neutral for a fresh run. */
  private resetPresentation(): void {
    this.impactTimer = 0;
    this.reticleShake = 0;
    this.reticleDenyFlash = 0;
    this.prevChainCombo = 1;
    this.overflowLevel = null;
    if (this.alarmActive) this.sound.alarmEnd();
    this.alarmActive = false;
    this.hud?.setAlarm(false);
    this.hud?.setPressure(null);
    for (const lane of ["safe", "technical", "hazard"] as const) this.hud?.clearWorldTag(`jam-${lane}`);
    this.hud?.clearWorldTag("stale");
    this.hud?.setRecoveryPrompt(null, false);
    // 18.16 jam entrance nudge + invite highlight (arena hook cleared below).
    this.hud?.clearWorldTag("jam-alt");
    this.hud?.clearEdgeFlare("jam-alt");
    this.jamExtendingLanes.clear();
    this.returnRides.length = 0;
    this.arrivalQueue.length = 0;
    this.jamUnbackedSince.safe = null;
    this.jamUnbackedSince.technical = null;
    this.jamUnbackedSince.hazard = null;
    this.jamWrittenOff.safe = 0;
    this.jamWrittenOff.technical = 0;
    this.jamWrittenOff.hazard = 0;
    this.returnPadHold = 0;
    this.dwellWarned = false;
    this.chamberHeatWasOn = false;
    this.hud?.clearWorldTag("kidcue");
    this.hud?.clearEdgeFlare("kidcue");
    if (this.overloadKlaxonOn) { this.overloadKlaxonOn = false; this.sound.overloadKlaxon(false); }
    this.hud?.setChamberHeat(null);
    this.hud?.setOverload(null);
    this.arena?.setChamberHeat(0);
    this.arena?.setFurnaceDistress(0);
    this.arena?.setReturnPadCharge(0);
    this.arena?.setReturnIncoming(0);
    this.arena?.setJamActive(false);
    this.arena?.setInviteReceiver(null);
    this.arena?.setFreshnessChips([]);
    this.gradeCauseShownAt = -Infinity;
    this.pendingDispatch = null;
    this.arena?.setChuteShuttersClosed(false);
    this.arena?.setShutterWarning(null);
    this.arena?.setConveyorWarning(null);
    this.arena?.setExpiringPanels([]);
    for (const chute of ["left", "center", "right"] as const) this.arena?.setChuteTelegraph(chute, false);
    this.arena?.setBaffleGate(null);
    this.prevBaffleBlocked = null;
    this.shutterWarnedLane = null;
    this.conveyorWarnedDirection = null;
    this.panelWarnedBoundary = 0;
    this.telegraphedBatches.clear();
    // Chute cues are re-derived from the live projection every frame; drop both
    // the DOM chevrons and the hysteresis memory so a restart starts silent.
    this.chuteIndicatorSpecs = [];
    this.hud?.setChuteIndicators([]);
    for (const halo of this.fuseHalos) halo.visible = false;
    this.fuseHaloPhases.fill(0);
    this.prevGradeBand = bandOf(this.run.grade);
    this.renderer?.setGradeBand(this.prevGradeBand);
    // Simple v2 one-shots reset per run (18.11): the beam-full + ring-move
    // teaching beats fire once per shift, not once per session.
    this.taughtBeamFull = false;
    this.taughtRingMove = false;
    this.hud?.portalBanner(null, "");
    this.hud?.portalBeacon(null);
  }

  /**
   * Fresh world for a run. Tutorial lifecycle and the onboarding save are the
   * caller's concern (never silently consumed here, LOCKED-DESIGN 18.6).
   * `preserveTrash` keeps the warm-up pieces through a graduation so nothing
   * pops out of existence while the placement countdown ready-beat plays.
   */
  private resetWorld(preserveTrash = false): void {
    // Drop any teaching pause without firing its resume callback — a fresh run
    // resumes clean (the "seen" flag persists, so the beat never replays).
    this.firstEncounter?.destroy();
    this.firstEncounter = null;
    this.beam.forceDrop();
    // Beam capacity is seed-ruleset-driven (LOCKED-DESIGN 18.11 v2 / 18.15): read
    // from the CURRENT run seed so a title-screen Simple<->adult toggle between
    // runs can never leak a narrowed beam into official play. Since 18.15 (F1)
    // Simple carries an explicit 6 as well, so every run resolves to the locked 6
    // — the read stays seed-driven so a future ruleset retune is a data change.
    this.beam.setCapacity(getSeed(this.ctx.seed).rules?.beamCapacity ?? 6, this.simpleMode);
    this.controller.reset();
    if (!preserveTrash) this.trash.reset();
    this.run = this.createSimulation();
    this.runEnded = false;
    this.performance.length = 0;
    this.performanceElapsedMs = 0;
    this.visualLedgerIndex = 0;
    this.portals.resetDefaults();
    this.portals.setBothLive(true);
    this.glassWatch.clear();
    this.playerPortalArmed = true;
    this.playerRearmTicks = 0;
    // Both intent-gate fields are RUN-scoped and must reset with the run clock.
    // `lastIntentNudgeTick` is compared against `run.tick`, which restarts
    // NEGATIVE here (−300 at phase 1), so carrying a previous run's tick forward
    // silences the nudge until the new run climbs past it — and since the value
    // only ever ratchets upward, one late refusal in run 1 mutes the cue for the
    // rest of the session. That is the same defect as the original `-COOLDOWN`
    // seed, one scope wider, and it is why the sentinel is re-seeded rather than
    // zeroed. `intentRefusals` resets alongside it so it means what its comment
    // and the dogfood instructions say: refusals THIS run.
    this.lastIntentNudgeTick = Number.NEGATIVE_INFINITY;
    this.intentRefusals = 0;
    this.beamJammedTicks = 0;
    this.resetPresentation();
    const spawn = this.arenaGeometry.spawn;
    this.prevPlayerPos.set(spawn.x, spawn.y, spawn.z);
    this.currPlayerPos.set(spawn.x, spawn.y, spawn.z);
    this.renderer?.snapFollow(spawn.x, spawn.z);
    // fresh run starts at spawn, arena side — hard-cut any chamber view/blend
    this.chamberViewOn = false;
    this.renderer?.rig.setActiveView("arena-follow");
    // Reflect the current presentation mode (18.11) so a title toggle between
    // runs repaints the HUD density + world signage for this fresh world.
    this.applyMode();
  }

  private updateFps(now: number): void {
    this.fpsFrames++;
    if (now - this.fpsLast >= 500) {
      const fps = Math.round((this.fpsFrames * 1000) / (now - this.fpsLast));
      this.fpsFrames = 0;
      this.fpsLast = now;
      this.fps = fps;
      this.hud?.setFps(fps);
      const expectedMs = this.run.trace.length * (1000 / 60);
      this.performance.push({
        tick: this.run.tick,
        expectedMs,
        actualMs: this.performanceElapsedMs,
        driftMs: this.performanceElapsedMs - expectedMs,
        fps,
      });
    }
  }

  /**
   * Build the run's Simulation. The Simple jam toggle (LOCKED-DESIGN 18.15 / F4 shell)
   * is read HERE and ONLY here: the deterministic stream owns the option for the
   * whole run, so a mid-run settings flip cannot perturb it — the change lands on
   * the next shift, which is exactly what the settings note promises. It is also
   * gated on `this.simpleMode` so a Standard run can never carry the flag; the
   * kernel refuses it on ranked seeds independently, so the two guards agree
   * without a shared predicate that could drift.
   */
  private createSimulation(): Simulation {
    return new Simulation(getSeed(this.ctx.seed), {
      practicePhase: this.ctx.practicePhase,
      ...(this.ctx.simpleJamsDisabled && this.simpleMode ? { jamsDisabled: true } : {}),
    });
  }

  private finishRun(): void {
    this.runEnded = true;
    this.beam.forceDrop();
    if (this.alarmActive) {
      this.sound.alarmEnd();
      this.alarmActive = false;
      this.hud?.setAlarm(false);
    }
    const reason: EndReason = isFailureReason(this.run.endReason) ? "death" : "cash-out";
    this.sound.runEnd(reason === "cash-out");
    if (reason === "death") this.renderer?.shake(0.6);
    // Chamber-cook death placard (LOCKED-DESIGN 18.12 stage 3): a white-hot sear
    // frame + placard before the hard cut to results. Knob-capped via flash(); the
    // furnace-overload end gets its own placard. Simple stays soft (no adult
    // vocabulary — the warm results hero carries it).
    if (!this.simpleMode) {
      if (this.run.endReason === "cooked") {
        this.flash("#fff6e6", 0.7, 0.5);
        this.hud?.toast("UNIT COOKED · SHIFT TERMINATED", cssHex(FEEDBACK_COLORS.alarm), 2400);
      } else if (this.run.endReason === "jam-overload") {
        this.flash(cssHex(FEEDBACK_COLORS.alarm), 0.6, 0.5);
        this.hud?.toast("FURNACE OVERLOAD · SHIFT TERMINATED", cssHex(FEEDBACK_COLORS.alarm), 2400);
      }
    }
    if (this.overloadKlaxonOn) { this.overloadKlaxonOn = false; this.sound.overloadKlaxon(false); }
    // A finished shift has no live intake to announce (18.19).
    this.chuteIndicatorSpecs = [];
    this.hud?.setChuteIndicators([]);
    this.runEndCallback?.(this.runResult());
  }
}

function blastOccluded(origin: { x: number; z: number }, target: { x: number; z: number }, geometry: ReturnType<typeof deriveArenaGeometry>): boolean {
  // Chamber-block occupancy mismatch means the chamber glass (bulkhead or the
  // 18.13 side panes) sits between the endpoints. Blasts crossing z threshold
  // on the OPEN shoulder floor (|x| > 12) are not glass-separated.
  if (inChamberBlock(origin, geometry) !== inChamberBlock(target, geometry)) return true;
  const island = geometry.islandOccluder;
  const steps = 16;
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const x = origin.x + (target.x - origin.x) * t;
    const z = origin.z + (target.z - origin.z) * t;
    if (x > island.min[0] && x < island.max[0] && z > island.min[2] && z < island.max[2]) return true;
  }
  return false;
}
